import { PortalApiErrorCode, PortalApiErrorParams } from './portal-api-errors.js'
import { DefaultLanguage, Language, SecondaryLanguage, TranslatedText } from './types/i18n.js'

export type Translation = (language: Language) => string

export const DEFAULT_LANGUAGE: DefaultLanguage = 'et'
export const SECONDARY_LANGUAGES: SecondaryLanguage[] = ['en']
export const ALL_LANGUAGES: Language[] = [DEFAULT_LANGUAGE, ...SECONDARY_LANGUAGES]

const translate = (lang: Language, langStrings: TranslatedText): string => {
    return langStrings[lang] ?? langStrings[DEFAULT_LANGUAGE]
}

const translation = (langStrings: TranslatedText): Translation => {
    return (lang) => translate(lang, langStrings)
}

export const t = {
    notFound: {
        title: translation({ et: 'Lehte ei leitud', en: 'Page Not Found' }),
        message: translation({
            et: 'Otsitud lehte ei eksisteeri.',
            en: 'The page you are looking for does not exist.',
        }),
        goHome: translation({ et: 'Mine avalehele', en: 'Go to home page' }),
    },
    playtime: {
        profile: translation({ et: 'Märkide kogumine', en: 'Badge collecting' }),
        worktime: translation({ et: 'Worktime', en: 'Worktime' }),
        leaderboard: translation({ et: 'Edetabel', en: 'Leaderboard' }),
        survey: translation({ et: 'Q12 Küsimustik', en: 'Q12 Survey' }),
        projectCreate: translation({ et: 'Uus projekt', en: 'New Project' }),
        badgeRequest: translation({ et: 'Märgi taotlus', en: 'Badge Request' }),
        worktimeView: {
            totalProjects: translation({ et: 'Projekte kokku', en: 'Total Projects' }),
            newProjects: translation({ et: 'Uued projektid', en: 'New Projects' }),
            viewInNotion: translation({ et: 'Vaata Notionis', en: 'View in Notion' }),
            addProject: translation({ et: 'Lisa projekt', en: 'Add Project' }),
            noProjects: translation({ et: 'Projekte veel pole.', en: 'No projects yet.' }),
            createFirst: translation({
                et: 'Loo oma esimene projekt',
                en: 'Create your first project',
            }),
            team: translation({ et: 'Meeskond', en: 'Team' }),
            categories: translation({ et: 'Kategooriad', en: 'Categories' }),
            importance: translation({ et: 'Tähtsus', en: 'Importance' }),
            lessonsLearned: translation({ et: 'Õppetund', en: 'Lessons Learned' }),
            guests: translation({ et: 'Külalised', en: 'Guests' }),
            editProject: translation({ et: 'Muuda projekti', en: 'Edit project' }),
            addImage: translation({ et: 'Lisa pilt', en: 'Add image' }),
            showMore: translation({ et: 'Näita rohkem', en: 'Show more' }),
            showLess: translation({ et: 'Näita vähem', en: 'Show less' }),
            myProjects: translation({ et: 'Minu projektid', en: 'My Projects' }),
            allProjects: translation({ et: 'Kõik projektid', en: 'All Projects' }),
            searchProjects: translation({
                et: 'Otsi projekti...',
                en: 'Search projects...',
            }),
            requestJoin: translation({ et: 'Taotle lisamist', en: 'Request to Join' }),
            pendingRequest: translation({ et: 'Ootab kinnitust', en: 'Pending' }),
            memberBadge: translation({ et: 'Liige', en: 'Member' }),
            joinRequests: translation({
                et: 'Lisamistaotlused',
                en: 'Join Requests',
            }),
            approve: translation({ et: 'Kinnita', en: 'Approve' }),
            reject: translation({ et: 'Keeldu', en: 'Reject' }),
            requestSent: translation({ et: 'Taotlus saadetud', en: 'Request sent' }),
            selectRole: translation({ et: 'Vali roll', en: 'Select role' }),
            memberCount: (count: number) =>
                translation({ et: `${count} liiget`, en: `${count} members` }),
            pendingRequestsBadge: (count: number) =>
                translation({
                    et: `${count} taotlust`,
                    en: `${count} pending`,
                }),
            worktimeSubtitle: (count: number) =>
                translation({
                    et: `Sul on ${count} projekti`,
                    en: `You have ${count} projects`,
                }),
            // Project detail modal + model load error
            dateTba: translation({ et: 'Täpsustamisel', en: 'TBA' }),
            closeProjectDetails: translation({
                et: 'Sulge projekti detailid',
                en: 'Close project details',
            }),
            owner: translation({ et: 'Omanik', en: 'Owner' }),
            loadError: translation({
                et: 'Projektide laadimine ebaõnnestus',
                en: 'Failed to load projects',
            }),
        },
        hero: {
            leaderboardTitle: translation({ et: 'Edetabel', en: 'Leaderboard' }),
            leaderboardSubtitle: (rank: number, total: number) =>
                translation({
                    et: `Sinu koht edetabelis on nr ${rank}!`,
                    en: `Your rank is #${rank} of ${total}`,
                }),
            leaderboardSubtitleNoAnniversary: (rank: number, total: number) =>
                translation({
                    et: `Sinu koht edetabelis on nr ${rank}!`,
                    en: `Your rank is #${rank} of ${total} (without anniversary)`,
                }),
            balancetimeTitle: translation({ et: 'Sinu tööaeg', en: 'BalanceTime' }),
            balancetimeSubtitle: translation({ et: 'Ära hullu pane!', en: "Don't overdo it!" }),
            profileTitle: translation({ et: 'Sinu märgid', en: 'Your Badges' }),
            worktimeTitle: translation({ et: 'Sinu Worktime tööd', en: 'Your Worktime' }),
            greetingNoBadges: translation({
                et: 'Sul pole veel ühtegi märki, aga ruumi on kõikidele märkidele! Milline saab meie esimeseks?',
                en: "You don't have any badges yet, but there's room for all of them! Which one will be first?",
            }),
            greetingSmall: (claimed: number, score: number) =>
                translation({
                    et: `Sul on ${claimed} märk. Oled kogunud ${score} punkti. Tohoh! Anna tuld, ilmselgelt oled suuteline enamaks!`,
                    en: `You have ${claimed} badge. You've earned ${score} pts. Give it your all, you can clearly do more!`,
                }),
            greetingMedium: (claimed: number, score: number) =>
                translation({
                    et: `Sul on ${claimed} märki. Oled kogunud ${score} punkti. Jeah, hakkab tulema neid märke! Keep it up!`,
                    en: `You have ${claimed} badges. You've earned ${score} pts. Keep it up!`,
                }),
            greetingLarge: (claimed: number, score: number) =>
                translation({
                    et: `Sul on ${claimed} märki. Oled kogunud ${score} punkti. Vau, oled tõeline märkide koguja!`,
                    en: `You have ${claimed} badges. You've earned ${score} pts. Wow, you're a true badge collector!`,
                }),
            publicSubtitleSingle: (name: string, claimed: number, score: number) =>
                translation({
                    et: `${name} on kogunud ${claimed} märgi. Tal on ${score} punkti!`,
                    en: `${name} has collected ${claimed} badge. They have ${score} points!`,
                }),
            publicSubtitleMultiple: (name: string, claimed: number, score: number) =>
                translation({
                    et: `${name} on kogunud ${claimed} märki. Tal on ${score} punkti!`,
                    en: `${name} has collected ${claimed} badges. They have ${score} points!`,
                }),
        },
        balancetime: {
            menuLabel: translation({ et: 'BalanceTime', en: 'BalanceTime' }),
            // Filter range types
            week: translation({ et: 'Nädal', en: 'Week' }),
            month: translation({ et: 'Kuu', en: 'Month' }),
            quarter: translation({ et: 'Kvartal', en: 'Quarter' }),
            year: translation({ et: 'Aasta', en: 'Year' }),
            // Filter buttons
            previous: translation({ et: 'Eelmine', en: 'Previous' }),
            next: translation({ et: 'Järgmine', en: 'Next' }),
            // "Jump back to the current period" button, per unit
            thisWeek: translation({ et: 'See nädal', en: 'This week' }),
            thisMonth: translation({ et: 'See kuu', en: 'This month' }),
            thisQuarter: translation({ et: 'See kvartal', en: 'This quarter' }),
            thisYear: translation({ et: 'See aasta', en: 'This year' }),
            myHours: translation({ et: 'Minu tunnid', en: 'My Hours' }),
            // Summary cards
            deltaSummary: translation({ et: 'Kokkuvõte', en: 'Summary' }),
            deltaSummaryDesc: translation({
                et: 'Võrreldes valitud perioodi eesmärgiga',
                en: "Compared to the selected period's target",
            }),
            deltaMore: translation({ et: 'rohkem', en: 'more' }),
            deltaLess: translation({ et: 'vähem', en: 'less' }),
            publicHolidays: translation({ et: 'Riigipühad', en: 'Public Holidays' }),
            publicHolidaysDesc: translation({
                et: 'Riiklikud pühad perioodil',
                en: 'Public holidays in the period',
            }),
            timeOff: translation({ et: 'Vabad päevad', en: 'Time Off' }),
            timeOffDesc: translation({
                et: 'Puhkus, haigusleht jms',
                en: 'Vacation, sick leave etc.',
            }),
            eventDays: translation({ et: 'Ürituse päevad', en: 'Event Days' }),
            eventDaysDesc: translation({
                et: 'Registreeritud üritused perioodil',
                en: 'Registered events in the period',
            }),
            day: translation({ et: 'päev', en: 'day' }),
            days: translation({ et: 'päeva', en: 'days' }),
            // Summary table — the expected-hours header carries the subject's
            // workload so the number explains itself (e.g. "(0,8)").
            expectedTotalFull: (fte: string) =>
                translation({
                    et: `Kogutundide maht täiskoormusega (${fte})`,
                    en: `Total hours at full workload (${fte})`,
                }),
            expectedTotalPartial: (fte: string) =>
                translation({
                    et: `Kogutundide maht osakoormusega (${fte})`,
                    en: `Total hours at partial workload (${fte})`,
                }),
            tracked: translation({ et: 'Harvestis märgitud aeg', en: 'Time tracked in Harvest' }),
            differenceFromTotal: translation({
                et: 'Erinevus kogutundide mahuga',
                en: 'Difference from total hours',
            }),
            // Formula tooltip behind the expected-hours value
            formulaNorm: (workdays: number, dayHours: string) =>
                translation({
                    et: `Normtööaeg (${workdays} tööpäeva × ${dayHours})`,
                    en: `Standard hours (${workdays} workdays × ${dayHours})`,
                }),
            formulaShortenedDays: translation({
                et: 'Lühendatud tööpäevad',
                en: 'Shortened workdays',
            }),
            formulaWorkload: translation({ et: 'Koormus', en: 'Workload' }),
            formulaNotTracked: translation({
                et: 'Harvestis on oodatav nädalakoormus 0h — tunde ei oodata',
                en: 'Harvest weekly capacity is 0h — no hours are expected',
            }),
            formulaTotal: translation({ et: 'Kokku', en: 'Total' }),
            formulaSource: translation({
                et: 'Ametlik allikas: Tööelu — tööaeg',
                en: 'Official source: Tööelu — working time',
            }),
            // Marker under the progress bar, at the expected-by-today line
            expectedTodayMarker: translation({
                et: 'Eeldatav märgitud aeg tänaseks',
                en: 'Expected tracked time by today',
            }),
            // Progress bar
            trackedPercent: translation({ et: 'träkitud', en: 'tracked' }),
            expectedPercent: translation({ et: 'oodatud', en: 'expected' }),
            // Auth / connect page
            authRequired: translation({ et: 'Autentimine vajalik', en: 'Authentication Required' }),
            authDescription: translation({
                et: "Et kasutada BalanceTime'i, pead ühendama oma Harvesti konto. See võimaldab meil tuua sinu ajajälgimise andmed ja näidata sinu töö tasakaalu.",
                en: 'To use BalanceTime, you need to connect your Harvest and Humaans accounts. This allows us to fetch your time tracking data and show your work balance.',
            }),
            harvestIntegration: translation({
                et: 'Seadista Harvest integratsioon',
                en: 'Set up Harvest integration',
            }),
            harvestDesc: translation({
                et: 'Ühenda oma Harvesti konto ajaarvestuse jaoks.',
                en: 'Connect your Harvest account for time tracking.',
            }),
            connectHarvest: translation({ et: 'Ühenda Harvest', en: 'Connect Harvest' }),
            connected: translation({ et: 'Ühendatud', en: 'Connected' }),
            authRedirectNote: translation({
                et: "Kui mõlemad integratsioonid on seadistatud, suunatakse teid automaatselt teie BalanceTime'i juhtpaneelile.",
                en: 'Once both integrations are set up, you will be automatically redirected to your BalanceTime dashboard.',
            }),
            // Error state
            loadError: translation({
                et: 'BalanceTime andmete laadimine ebaõnnestus.',
                en: 'Unable to load BalanceTime data.',
            }),
        },
        badges: {
            score: (score: number) => translation({ et: `${score} punkti`, en: `${score} pts` }),
            claimed: (date: string) =>
                translation({ et: `LUNASTATUD ${date}`, en: `Claimed ${date}` }),
            pending: translation({ et: 'OOTEL', en: 'Pending' }),
            special: translation({ et: 'Eriline', en: 'Special' }),
            comment: translation({ et: 'Kommentaar või selgitus', en: 'Comment' }),
            evidenceLabel: translation({ et: 'Tõestusmaterjal', en: 'Evidence' }),
            download: translation({ et: 'Lae alla', en: 'Download' }),
            evidence: translation({ et: 'Tõestusmaterjal', en: 'Evidence (optional)' }),
            submitting: translation({ et: 'Saadan...', en: 'Submitting...' }),
            size: translation({ et: 'Suurus', en: 'Size' }),
            sort: translation({ et: 'Sorteeri', en: 'Sort' }),
            sortName: translation({ et: 'Nimi', en: 'Name' }),
            sortPointsDesc: translation({ et: 'Rohkem punkte', en: 'More points' }),
            sortPointsAsc: translation({ et: 'Vähem punkte', en: 'Fewer points' }),
            count: (n: number) =>
                translation({
                    et: n === 1 ? '1 märk' : `${n} märki`,
                    en: n === 1 ? '1 badge' : `${n} badges`,
                }),
            filters: translation({ et: 'Filtrid', en: 'Filters' }),
            removeFilter: (label: string) =>
                translation({ et: `Eemalda filter: ${label}`, en: `Remove filter: ${label}` }),
            clearFilters: translation({ et: 'Tühjenda', en: 'Clear' }),
            status: translation({ et: 'Staatus', en: 'Status' }),
            category: translation({ et: 'Kategooria', en: 'Category' }),
            showCount: (n: number) =>
                translation({
                    et: n === 1 ? 'Näita 1 märki' : `Näita ${n} märki`,
                    en: n === 1 ? 'Show 1 badge' : `Show ${n} badges`,
                }),
            tier: {
                bronze: translation({ et: 'Pronks', en: 'Bronze' }),
                silver: translation({ et: 'Hõbe', en: 'Silver' }),
                gold: translation({ et: 'Kuld', en: 'Gold' }),
                crystal: translation({ et: 'Kristall', en: 'Crystal' }),
            },
            state: {
                claimed: (date: string) =>
                    translation({ et: `Lunastatud ${date}`, en: `Claimed ${date}` }),
                active: translation({ et: 'Aktiveeritud', en: 'Activated' }),
                pending: translation({ et: 'Ootel kinnitamist', en: 'Pending approval' }),
                inactive: translation({ et: 'Aktiveerimata', en: 'Not activated' }),
            },
            newFlag: translation({ et: 'Uus', en: 'New' }),
            preview: translation({ et: 'Märgi eelvaade', en: 'Preview badge' }),
            claimTitle: translation({ et: 'Aktiveeri märk', en: 'Activate badge' }),
            claimCommentPlaceholder: translation({
                et: 'Mida tegid ja millal?',
                en: 'What did you do and when?',
            }),
            dropzone: translation({ et: 'Vali fail', en: 'Choose a file' }),
            dropzoneOr: translation({ et: 'või lohista siia', en: 'or drop it here' }),
            dropzoneHint: translation({ et: 'Pilt või PDF', en: 'Image or PDF' }),
            removeFile: translation({ et: 'Eemalda fail', en: 'Remove file' }),
            submitClaim: translation({ et: 'Saada kinnitamiseks', en: 'Send for approval' }),
            uploadFailed: translation({
                et: 'Faili üleslaadimine ebaõnnestus. Proovi uuesti.',
                en: 'Uploading the file failed. Please try again.',
            }),
            autoGranted: translation({
                et: 'Lisandub automaatselt tööaastapäeval.',
                en: 'Granted automatically on your work anniversary.',
            }),
            givenOnly: translation({
                et: 'Seda märki ise aktiveerida ei saa. See antakse tunnustusena.',
                en: 'This badge cannot be activated by you. It is awarded as recognition.',
            }),
            newNotice: (name: string) =>
                translation({
                    et: `${name} on kinnitatud. Ava märk, et see kätte saada.`,
                    en: `${name} has been approved. Open it to collect it.`,
                }),
            newNoticeMany: (n: number) =>
                translation({
                    et: `${n} märki on kinnitatud. Ava need, et kätte saada.`,
                    en: `${n} badges have been approved. Open them to collect them.`,
                }),
            openBadge: translation({ et: 'Ava märk', en: 'Open badge' }),
            pointsPop: (n: number) => translation({ et: `+${n} punkti`, en: `+${n} pts` }),
            prevBadge: translation({ et: 'Eelmine märk', en: 'Previous badge' }),
            nextBadge: translation({ et: 'Järgmine märk', en: 'Next badge' }),
        },
        leaderboardView: {
            toggleLabel: translation({
                et: 'Näita punktisummasid koos tööaastapäeva punktidega',
                en: 'Show scores with anniversary points',
            }),
            withAnniversary: translation({ et: 'Koos aastapäevaga', en: 'With Anniversary' }),
            withoutAnniversary: translation({ et: 'Ilma aastapäevata', en: 'Without Anniversary' }),
            scoreLabel: translation({ et: 'punkti', en: 'pts' }),
            topByProjects: translation({ et: 'Top projektide järgi', en: 'Top by Projects' }),
        },
        filter: {
            all: translation({ et: 'Kõik', en: 'All' }),
            active: translation({ et: 'Aktiveeritud', en: 'Claimed' }),
            inactive: translation({ et: 'Aktiveerimata', en: 'Unclaimed' }),
            pending: translation({ et: 'Ootel', en: 'Pending' }),
            new: translation({ et: 'Uued', en: 'New' }),
        },
        anniversaryDivider: translation({ et: 'Tööaastapäev', en: 'Anniversary Badges' }),
        publicProfile: {
            back: translation({ et: 'Edetabel', en: 'Leaderboard' }),
            notFound: translation({ et: 'Kasutajat ei leitud', en: 'User not found' }),
            rank: (rank: number) =>
                translation({ et: `Edetabelis ${rank}. kohal`, en: `#${rank} on the leaderboard` }),
        },
        hoverCard: {
            badges: translation({ et: 'märki', en: 'badges' }),
            newest: translation({ et: 'Uusim', en: 'Latest' }),
        },
        notifications: {
            requestCreated: translation({
                et: 'Hea töö! Märk on nüüd kinnituse ootel.',
                en: 'Great work! Badge is now pending approval.',
            }),
            close: translation({ et: 'Sulge', en: 'Close' }),
        },
        q12: {
            likert: {
                stronglyDisagree: translation({ et: 'Üldse ei nõustu', en: 'Strongly disagree' }),
                disagree: translation({ et: 'Ei nõustu', en: 'Disagree' }),
                neutral: translation({ et: 'Neutraalne', en: 'Neutral' }),
                agree: translation({ et: 'Nõustun', en: 'Agree' }),
                stronglyAgree: translation({ et: 'Täiesti nõustun', en: 'Strongly agree' }),
            },
            noResponse: translation({ et: 'Vastus puudub', en: 'No response' }),
            startSurvey: translation({ et: 'Alusta küsimustikku', en: 'Start Survey' }),
            submit: translation({ et: 'Saada', en: 'Submit' }),
            submitting: translation({ et: 'Saadan...', en: 'Submitting...' }),
            previous: translation({ et: 'Eelmine', en: 'Previous' }),
            next: translation({ et: 'Järgmine', en: 'Next' }),
            backToHome: translation({ et: 'Tagasi avalehele', en: 'Back to Home' }),
            thankYou: translation({ et: 'Aitäh!', en: 'Thank you!' }),
            deadline: translation({ et: 'Tähtaeg', en: 'Deadline' }),
            answered: (count: number, total: number) =>
                translation({
                    et: `${count} / ${total} vastatud`,
                    en: `${count} / ${total} answered`,
                }),
            questionOf: (current: number, total: number) =>
                translation({
                    et: `Küsimus ${current} / ${total}`,
                    en: `Question ${current} of ${total}`,
                }),
            alreadyAnsweredTitle: translation({ et: 'Juba vastatud', en: 'Already Answered' }),
            alreadyAnsweredDescription: translation({
                et: 'Oled selle küsimustiku vastused juba esitanud.',
                en: 'You have already submitted your answers for this questionnaire.',
            }),
            closedTitle: translation({ et: 'Kampaania lõppenud', en: 'Campaign Closed' }),
            closedDescription: (date: string) =>
                translation({
                    et: `See kampaania lõppes ${date} ja ei võta enam vastuseid vastu.`,
                    en: `This campaign ended on ${date} and is no longer accepting responses.`,
                }),
            notOpenTitle: translation({
                et: 'Kampaania pole veel alanud',
                en: 'Campaign not open yet',
            }),
            notOpenDescription: (date: string) =>
                translation({
                    et: `See kampaania avaneb ${date}. Proovi siis uuesti.`,
                    en: `This campaign opens on ${date}. Please come back then.`,
                }),
            notInGroup: translation({
                et: 'Sa ei ole veel ühtegi gruppi määratud. Palun võta ühendust administraatoriga.',
                en: 'You are not assigned to any group yet. Please contact your administrator.',
            }),
            answerLanguage: translation({ et: 'Vastamise keel', en: 'Answer language' }),
            yourAnswer: translation({ et: 'Sinu vastus...', en: 'Your answer...' }),
            /** Admin preview of a form/campaign — nothing is saved. */
            previewLabel: translation({ et: 'Eelvaade', en: 'Preview' }),
            previewNote: translation({
                et: 'Eelvaade — vastuseid ei salvestata.',
                en: 'Preview — answers are not saved.',
            }),
            previewDisplayMode: translation({ et: 'Kuvamine', en: 'Display' }),
            previewOneAtATime: translation({ et: 'Ükshaaval', en: 'One at a time' }),
            previewAllAtOnce: translation({ et: 'Kõik korraga', en: 'All at once' }),
            previewNotFound: translation({
                et: 'Eelvaadet ei saa kuvada — vormi või kampaaniat ei leitud.',
                en: 'Preview unavailable — the form or campaign was not found.',
            }),
            questionNumber: (n: number) => translation({ et: `Küsimus ${n}`, en: `Question ${n}` }),
        },
        heroLabels: {
            years: translation({ et: 'Aastat', en: 'Years' }),
            lvl: translation({ et: 'LVL', en: 'LVL' }),
        },
        levelProgress: {
            level: translation({ et: 'Tase', en: 'Level' }),
            score: translation({ et: 'Skoor', en: 'Score' }),
            badges: translation({ et: 'Märgid', en: 'Badges' }),
            // Progress subtitle sentences — picked by progress-percent band in
            // playtime-utils.ts (getLevelProgressSubtitle).
            subtitleStart: (rank: number) =>
                translation({
                    et: `Oled uue taseme alguses! Järgmise tasemeni jõudmiseks on vaja veel veidi pingutada. Edetabelis oled tublil ${rank}. kohal.`,
                    en: `You're at the beginning of a new level! Just a little effort is needed to reach the next milestone. You are in a solid #${rank} position on the leaderboard.`,
                }),
            subtitleHalfway: (rank: number) =>
                translation({
                    et: `Suurepärane töö, peaaegu pool teed on läbitud! Jätka samas vaimus uute saavutuste poole. Edetabelis hoiad kindlalt ${rank}. kohta.`,
                    en: `Great job, you're almost halfway there! Keep going towards new achievements. You firmly hold the #${rank} spot on the leaderboard.`,
                }),
            subtitleNear: (rank: number) =>
                translation({
                    et: `Väga tubli progress, järgmine tase on juba lähedal! Tee järgmise tasemeni on peaaegu lõpusirgel. Hoiad edetabelis vägevat ${rank}. kohta.`,
                    en: `Wonderful progress, the next level is near! The road to the next level is almost on the home stretch. You hold an awesome #${rank} position on the leaderboard.`,
                }),
            subtitleVerge: (rank: number) =>
                translation({
                    et: `Suurepärane hoog, uue taseme lävel! Veel viimane samm uue taseme saavutamiseks! Edetabelis hoiad suurepärast ${rank}. kohta.`,
                    en: `Amazing momentum, on the verge of a new level! Just one last step to level up! You hold a stellar #${rank} place on the leaderboard.`,
                }),
        },
        leaderboardTable: {
            user: translation({ et: 'Kasutaja', en: 'User' }),
            score: translation({ et: 'Skoor', en: 'Score' }),
            projects: translation({ et: 'Projektid', en: 'Projects' }),
            projectSingular: translation({ et: 'projekt', en: 'project' }),
            projectPlural: translation({ et: 'projekti', en: 'projects' }),
        },
        projectForm: {
            newProject: translation({ et: 'Uus projekt', en: 'New Project' }),
            editProject: (name: string) =>
                translation({ et: `Muuda: ${name}`, en: `Edit: ${name}` }),
            projectName: translation({ et: 'Projekti nimi *', en: 'Project Name *' }),
            projectNamePlaceholder: translation({
                et: 'Sisesta projekti nimi',
                en: 'Enter project name',
            }),
            description: translation({ et: 'Kirjeldus', en: 'Description' }),
            descriptionPlaceholder: translation({
                et: 'Kirjelda oma projekti',
                en: 'Describe your project',
            }),
            startDate: translation({ et: 'Alguskuupäev *', en: 'Start Date *' }),
            endDate: translation({ et: 'Lõppkuupäev', en: 'End Date' }),
            ongoing: translation({
                et: 'Käimasolev projekt (lõppkuupäev puudub)',
                en: 'Ongoing project (no end date)',
            }),
            teamMembers: translation({ et: 'Meeskonnaliikmed *', en: 'Team Members *' }),
            teamMembersHint: translation({
                et: 'Vali meeskonnaliikmed nimekirjast või lisa uus liige rolliga. Sa pead ennast lisama.',
                en: 'Select team members from the list or add a new member with a role. You must include yourself.',
            }),
            searchPeople: translation({ et: 'Otsi inimesi...', en: 'Search people...' }),
            assignRole: translation({ et: 'Määra roll', en: 'Assign Role' }),
            addNewTeamMember: translation({
                et: '+ Lisa uus meeskonnaliige',
                en: 'Add New Team Member',
            }),
            categories: translation({ et: 'Kategooriad', en: 'Categories' }),
            guestsLabel: translation({
                et: 'Külalised / Välised koostööpartnerid',
                en: 'Guests / External Collaborators',
            }),
            guestsPlaceholder: translation({
                et: 'Lisa koostööpartnerid rollidega, komadega eraldatult. Näide: John Doe - Full stack, Jane Smith - Designer',
                en: 'Add collaborators with roles, separated by commas. Example: John Doe - Full stack, Jane Smith - Designer',
            }),
            importance: translation({ et: 'Tähtsus', en: 'Importance' }),
            importancePlaceholder: translation({
                et: 'Miks see projekt sulle oluline oli?',
                en: 'Why was this project important to you?',
            }),
            lessonsLearned: translation({ et: 'Õppetund', en: 'Lessons Learned' }),
            lessonsLearnedPlaceholder: translation({
                et: 'Mida sa sellest projektist õppisid?',
                en: 'What did you learn from this project?',
            }),
            testimonial: translation({ et: 'Soovituskiri', en: 'Testimonial' }),
            quote: translation({ et: 'Tsitaat', en: 'Quote' }),
            quotePlaceholder: translation({
                et: 'Soovituskiri selle projekti kohta...',
                en: 'A testimonial about this project...',
            }),
            author: translation({ et: 'Autor', en: 'Author' }),
            authorPlaceholder: translation({ et: 'Autori nimi', en: 'Author name' }),
            position: translation({ et: 'Ametikoht', en: 'Position' }),
            positionPlaceholder: translation({ et: 'Autori ametikoht', en: 'Author position' }),
            projectBackground: translation({ et: 'Projekti taust', en: 'Project Background' }),
            bgHint: translation({
                et: 'Vali üks: lae üles kaanepilt või kasuta logo koos brändi värviga.',
                en: 'Choose one: upload a cover image, or use a logo with a brand color.',
            }),
            coverImage: translation({ et: 'Kaanepilt', en: 'Cover Image' }),
            currentCoverImage: translation({ et: 'Praegune kaanepilt', en: 'Current cover image' }),
            logoAndColor: translation({ et: 'Logo + Brändi värv', en: 'Logo + Brand Color' }),
            currentLogo: translation({ et: 'Praegune logo', en: 'Current logo' }),
            brandColor: translation({ et: 'Brändi värv', en: 'Brand Color' }),
            fileHint: translation({
                et: 'Lubatud formaadid: JPG, PNG, WebP, GIF, SVG. Max suurus: 10 MB.',
                en: 'Accepted formats: JPG, PNG, WebP, GIF, SVG. Max size: 10 MB.',
            }),
            unsupportedFormat: (name: string) =>
                translation({
                    et: `"${name}" ei ole toetatud formaat. Kasuta JPG, PNG, WebP, GIF või SVG.`,
                    en: `"${name}" is not a supported format. Use JPG, PNG, WebP, GIF, or SVG.`,
                }),
            fileTooLarge: (name: string, sizeMb: string) =>
                translation({
                    et: `"${name}" on liiga suur (${sizeMb} MB). Maksimaalne suurus on 10 MB.`,
                    en: `"${name}" is too large (${sizeMb} MB). Maximum is 10 MB.`,
                }),
            saveProject: translation({ et: 'Salvesta projekt', en: 'Save Project' }),
            deleting: translation({ et: 'Kustutan...', en: 'Deleting...' }),
            deleteProject: translation({ et: 'Kustuta projekt', en: 'Delete Project' }),
            deleteConfirm: (name: string) =>
                translation({
                    et: `Kas oled kindel, et soovid kustutada "${name}"? Seda ei saa tagasi võtta.`,
                    en: `Are you sure you want to delete "${name}"? This cannot be undone.`,
                }),
            addTeamMemberTitle: translation({
                et: 'Lisa uus meeskonnaliige',
                en: 'Add New Team Member',
            }),
            teamMember: translation({ et: 'Meeskonnaliige', en: 'Team Member' }),
            selectPerson: translation({ et: 'Vali isik...', en: 'Select a person...' }),
            role: translation({ et: 'Roll', en: 'Role' }),
            rolePlaceholder: translation({
                et: 'nt Full Stack Developer, Designer...',
                en: 'e.g. Full Stack Developer, Designer...',
            }),
            addTeamMember: translation({ et: 'Lisa meeskonnaliige', en: 'Add Team Member' }),
            adding: translation({ et: 'Lisan...', en: 'Adding...' }),
            selectPersonAndRole: translation({
                et: 'Palun vali isik ja sisesta rolli nimi.',
                en: 'Please select a person and enter a role name.',
            }),
            mustAddTeamMember: translation({
                et: 'Pead lisama vähemalt ühe meeskonnaliikme (sh iseenda).',
                en: 'You must add at least one team member (including yourself).',
            }),
            mustIncludeYourself: translation({
                et: 'Pead ennast meeskonnaliikmena lisama.',
                en: 'You must include yourself as a team member.',
            }),
            unexpectedError: translation({
                et: 'Ilmnes ootamatu viga',
                en: 'An unexpected error occurred',
            }),
            deleteError: translation({
                et: 'Projekti kustutamine ebaõnnestus',
                en: 'Failed to delete project',
            }),
            // WYSIWYG toolbar titles
            bold: translation({ et: 'Rasvane', en: 'Bold' }),
            italic: translation({ et: 'Kaldkiri', en: 'Italic' }),
            bulletList: translation({ et: 'Täppidega loend', en: 'Bullet List' }),
            numberedList: translation({ et: 'Nummerdatud loend', en: 'Numbered List' }),
        },
    },
    eat: {
        menuLabel: translation({ et: 'gotoAndEat', en: 'gotoAndEat' }),
        orders: translation({ et: 'Tellimused', en: 'Orders' }),
        meals: translation({ et: 'Menüü', en: 'Menu' }),
        comments: translation({ et: 'Kommentaarid', en: 'Comments' }),
        payments: translation({ et: 'Maksed', en: 'Payments' }),
        mealTypes: {
            daily_meal: translation({ et: 'Päevapraad', en: 'Daily meal' }),
            half_daily_meal: translation({ et: '½ päevapraad', en: '½ daily meal' }),
            daily_soup: translation({ et: 'Päevasupp', en: 'Daily soup' }),
            daily_dessert: translation({ et: 'Päevadessert', en: 'Daily dessert' }),
            weekly_meal: translation({ et: 'Nädalatoit', en: 'Weekly meal' }),
            regular: translation({ et: 'Tavaline', en: 'Regular menu' }),
        },
        orderStatuses: {
            pending: translation({ et: 'Ootel', en: 'Pending' }),
            ordered: translation({ et: 'Tellitud', en: 'Ordered' }),
            paid: translation({ et: 'Makstud', en: 'Paid' }),
            canceled: translation({ et: 'Tühistatud', en: 'Canceled' }),
        },
        summary: {
            title: translation({ et: 'Minu gotoAndEat', en: 'My gotoAndEat' }),
            myOrders: translation({ et: 'Minu tellimused', en: 'My orders' }),
            date: translation({ et: 'Kuupäev', en: 'Date' }),
            meal: translation({ et: 'Söök', en: 'Meal' }),
            comment: translation({ et: 'Kommentaar', en: 'Comment' }),
            price: translation({ et: 'Hind', en: 'Price' }),
            status: translation({ et: 'Staatus', en: 'Status' }),
            unpaid: translation({ et: 'Maksmata kokku', en: 'Unpaid total' }),
            unpaidCount: translation({ et: 'Maksmata tellimusi', en: 'Unpaid orders' }),
            unpaidTotal: translation({ et: 'Maksmata summa', en: 'Unpaid total' }),
            oldestUnpaid: translation({ et: 'Vanim maksmata', en: 'Oldest unpaid' }),
            noUnpaid: translation({ et: 'Kõik makstud!', en: 'All paid!' }),
            paidOrders: translation({ et: 'Makstud tellimusi', en: 'Paid orders' }),
            noOrders: translation({ et: 'Tellimusi pole veel', en: 'No orders yet' }),
        },
        ordering: {
            todayTitle: translation({ et: 'Tänane menüü', en: "Today's menu" }),
            noMeals: translation({ et: 'Täna pole menüüd', en: 'No meals available today' }),
            orderButton: translation({ et: 'Telli', en: 'Order' }),
            ordering: translation({ et: 'Tellin...', en: 'Ordering...' }),
            orderBlocked: translation({
                et: 'Tellimine suletud (9:30–12:00)',
                en: 'Ordering closed (9:30–12:00)',
            }),
            comment: translation({ et: 'Lisand (valikuline)', en: 'Add-on (optional)' }),
            noComment: translation({ et: 'Ilma lisandita', en: 'No add-on' }),
            deleteOrder: translation({ et: 'Kustuta', en: 'Delete' }),
            markAsPaid: translation({ et: 'Märgi maksetuks', en: 'Mark as paid' }),
            paying: translation({ et: 'Märgin...', en: 'Marking...' }),
            paymentInfo: translation({ et: 'Makse info', en: 'Payment info' }),
            paymentIban: translation({ et: 'IBAN', en: 'IBAN' }),
            paymentPayee: translation({ et: 'Saaja', en: 'Payee' }),
            dailyMenu: translation({ et: 'Tänane menüü', en: "Today's menu" }),
            weeklyDeals: translation({ et: 'Nädala pakkumised', en: 'Weekly deals' }),
            regularMenu: translation({ et: 'Püsimenüü', en: 'Regular menu' }),
            addToOrder: translation({ et: 'Lisa tellimusele', en: 'Add to Order' }),
            placeOrder: translation({ et: 'Esita tellimus', en: 'Place order' }),
            cartTitle: translation({ et: 'Ostukorv', en: 'Cart' }),
            removeFromCart: translation({ et: 'Eemalda', en: 'Remove' }),
        },
        errors: {
            orderForbidden: translation({
                et: 'Tellimine pole praegu lubatud (9:30–12:00)',
                en: 'Ordering is not allowed at this time (9:30–12:00)',
            }),
            onlyPendingDelete: translation({
                et: 'Ainult ootel tellimusi saab kustutada',
                en: 'Only pending orders can be deleted',
            }),
        },
        // Tooltip of the per-row delete button in the orders table
        deleteOrderTooltip: translation({ et: 'Kustuta tellimus', en: 'Delete order' }),
    },
    theme: {
        label: translation({ et: 'Teema', en: 'Theme' }),
        system: translation({ et: 'Süsteem', en: 'System' }),
        light: translation({ et: 'Hele', en: 'Light' }),
        dark: translation({ et: 'Tume', en: 'Dark' }),
    },
    portalHero: {
        subtitle: translation({
            et: 'Palju tööriist teeb pea haiget. Tööriistad üle koopa. Ei enam jahti tööriistale. Paneme kõik tööriist ühte portaali.',
            en: 'Many tool make head hurt. Tools all over cave. No more hunt for tool. We put all tool in one portal.',
        }),
        // Greeting fallback when the user has no resolvable first name.
        greetingFallback: translation({ et: 'seal', en: 'there' }),
        sections: {
            operational: translation({ et: 'Operatiivne', en: 'Operational' }),
            myJourney: translation({ et: 'Minu teekond', en: 'My Journey' }),
            tools: translation({ et: 'Tööriistad', en: 'Tools' }),
        },
        tiles: {
            projects: translation({ et: 'Projektid', en: 'Projects' }),
            unpaidOrders: translation({ et: 'MAKSMATA TELLIMUSED', en: 'UNPAID ORDERS' }),
            unpaidOrdersCount: (n: number) =>
                translation({ et: `${n} tellimust`, en: `${n} order${n === 1 ? '' : 's'}` }),
            todaysMenu: translation({ et: 'TÄNANE MENÜÜ', en: "TODAY'S MENU" }),
            tomorrowsMenu: translation({ et: 'HOMNE MENÜÜ', en: "TOMORROW'S MENU" }),
            companyRankings: translation({ et: 'ETTEVÕTTE EDETABEL', en: 'COMPANY RANKINGS' }),
            yourPoints: translation({ et: 'SINU PUNKTID', en: 'YOUR POINTS' }),
            nextLevel: (pts: number) =>
                translation({
                    et: `${pts} punkti järgmise tasemeni`,
                    en: `${pts} pts to next level`,
                }),
            lastBadge: translation({ et: 'VIIMANE MEDAL', en: 'LAST BADGE EARNED' }),
            timeOverview: translation({ et: 'AJA ÜLEVAADE', en: 'TIME OVERVIEW' }),
            balanceTimeEmpty: translation({
                et: 'Ühenda Harvest, et näha oma töötundide ülevaadet.',
                en: 'Connect Harvest to see an overview of your work hours.',
            }),
            tracked: translation({ et: 'TEGELIK', en: 'ACTUAL' }),
            expected: translation({ et: 'Oodatav see kuu', en: 'Expected this month' }),
            expectedQuarter: translation({
                et: 'Oodatav see kvartal',
                en: 'Expected this quarter',
            }),
            expectedToday: translation({ et: 'Oodatav tänaseks', en: 'Expected by today' }),
            overhours: translation({ et: 'TÖÖAJA SALDO', en: 'WORK BALANCE' }),
            sources: translation({ et: 'ALLIKAD', en: 'SOURCES' }),
            harvestDetail: (tracked: number, planned: number) =>
                translation({
                    et: `${tracked}h / ${planned}h sel kuul`,
                    en: `${tracked}h / ${planned}h planned this month`,
                }),
            addProject: translation({ et: 'Lisa projekt', en: 'Add project' }),
            pendingApprovals: (n: number) =>
                translation({
                    et: `${n} taotlust kinnitamiseks`,
                    en: `${n} request${n === 1 ? '' : 's'} to approve`,
                }),
            pendingApprovalsLabel: translation({
                et: 'Kinnitamiseks',
                en: 'To approve',
            }),
            myPendingRequests: (n: number) =>
                translation({
                    et: `${n} taotlust ootab kinnitust`,
                    en: `${n} request${n === 1 ? '' : 's'} awaiting approval`,
                }),
            myPendingRequestsLabel: translation({
                et: 'Ootab kinnitust',
                en: 'Awaiting approval',
            }),
            signatureToolDescription: translation({
                et: 'Loo ja halda oma professionaalset e-posti signatuuri.',
                en: 'Create and manage your professional email signature.',
            }),
            timeHubDescription: translation({
                et: 'Broneeri koosolekuruume ja ühiskasutatavaid ruume kontoris.',
                en: 'Book meeting rooms and shared spaces in the office.',
            }),
            urlToolDescription: translation({
                et: 'Loo QR-koode ja lühilinke sisekasutuseks.',
                en: 'Generate QR codes and short links for internal use.',
            }),
            ngbDescription: translation({
                et: 'Vaata ja halda töötajate andmeid ning vastavust.',
                en: 'Review and manage employee records and compliance.',
            }),
            assetManagerDescription: translation({
                et: 'Jälgi ja halda ettevõtte riistvara ja tarkvaraasseteid.',
                en: 'Track and manage company hardware and software assets.',
            }),
            pendingCount: (n: number) => translation({ et: `${n} ootel`, en: `${n} pending` }),
            orderedCount: (n: number) => translation({ et: `${n} tellimust`, en: `${n} ordered` }),
            daysUnpaid: (n: number) =>
                translation({
                    et: `${n} päeva maksmata`,
                    en: `${n} day${n === 1 ? '' : 's'} unpaid`,
                }),
            levelTag: (n: number) => translation({ et: `Tase ${n}`, en: `Level ${n}` }),
            phNextVacation: translation({ et: 'JÄRGMINE PUHKUS', en: 'NEXT VACATION' }),
            phNoUpcomingLeave: translation({ et: 'Pole planeeritud', en: 'None planned' }),
            phApproved: translation({ et: 'Kinnitatud', en: 'Approved' }),
            phLeaveRemaining: translation({ et: 'JÄÄK', en: 'LEAVE REMAINING' }),
            phLeaveUsed: translation({ et: 'KASUTATUD', en: 'USED' }),
            phDays: (n: number) =>
                translation({ et: `${n} päeva`, en: `${n} day${n === 1 ? '' : 's'}` }),
        },
        q12Tile: {
            surveyLabel: translation({
                et: 'TÖÖTAJATE RAHULOLU KÜSITLUS',
                en: 'WORK SATISFACTION SURVEY',
            }),
            activeSurveyDescription: translation({
                et: 'Uus küsitlusperiood on alanud!',
                en: 'New survey period is live!',
            }),
            inactiveSurveyDescription: translation({
                et: 'Küsitlus on lõppenud.',
                en: 'Survey period has ended.',
            }),
            deadline: (date: string) =>
                translation({ et: `Täida kuni ${date}`, en: `Fill it out by ${date}` }),
            takeSurvey: translation({ et: 'Täida küsimustik', en: 'Take the survey' }),
        },
        callout: {
            activeSurveyText: translation({
                et: 'Uus Q12 küsitluse periood on alanud! Täida see kohe.',
                en: 'Q12 new survey period is live! Fill it out now.',
            }),
            inactiveSurveyText: translation({
                et: 'Q12 küsitluse tulemused on olemas. Vaata, kuidas läks.',
                en: 'Q12 survey results are in. See how we scored.',
            }),
            answeredSurveyText: translation({
                et: 'Aitäh, et täitsid ära Q12 vormi. Järgmised sammud on 1:1 vestlus tiimijuhiga ning tiimivestlus.',
                en: 'Thank you for completing the Q12 survey. Next up: a 1:1 with your team lead and the team discussion.',
            }),
            startSurvey: translation({ et: 'Asu täitma', en: 'Take the survey' }),
            viewResults: translation({ et: 'Vaata tulemusi', en: 'View results' }),
        },
        search: {
            placeholder: translation({ et: 'Otsi tööriistu...', en: 'Search tools...' }),
            recentLabel: translation({ et: 'Viimati kasutatud', en: 'Recent' }),
            allLabel: translation({ et: 'Kõik tööriistad', en: 'All tools' }),
            noResults: translation({ et: 'Tulemusi ei leitud', en: 'No results found' }),
        },
        nav: {
            notifications: translation({ et: 'Teavitused', en: 'Notifications' }),
            markAllRead: translation({ et: 'Märgi kõik loetuks', en: 'Mark all as read' }),
            noNotifications: translation({ et: 'Teavitusi pole', en: 'No notifications' }),
            logout: translation({ et: 'Logi välja', en: 'Log out' }),
            back: translation({ et: 'Tagasi', en: 'Back' }),
            language: translation({ et: 'Keel', en: 'Language' }),
            profile: translation({ et: 'Profiil', en: 'Profile' }),
            settings: translation({ et: 'Seaded', en: 'Settings' }),
        },
        count: {
            active: (n: number) => translation({ et: `${n} aktiivset`, en: `${n} active` }),
            planned: (n: number) => translation({ et: `${n} planeeritud`, en: `${n} planned` }),
        },
    },
    common: {
        copy: translation({ et: 'Kopeeri', en: 'Copy' }),
        copied: translation({ et: 'Kopeeritud', en: 'Copied' }),
        cancel: translation({ et: 'Tühista', en: 'Cancel' }),
        delete: translation({ et: 'Kustuta', en: 'Delete' }),
        save: translation({ et: 'Salvesta', en: 'Save' }),
        saving: translation({ et: 'Salvestan...', en: 'Saving...' }),
        back: translation({ et: 'Tagasi', en: 'Back' }),
        search: translation({ et: 'Otsi...', en: 'Search...' }),
        noResults: translation({ et: 'Tulemusi ei leitud', en: 'No results' }),
        noImage: translation({ et: 'Pilt puudub', en: 'No image' }),
        noImageSelected: translation({ et: 'Pilti pole valitud', en: 'No image selected' }),
        unknown: translation({ et: 'Tundmatu', en: 'Unknown' }),
        selectEndDate: translation({ et: 'Vali lõppkuupäev', en: 'Select end date' }),
        close: translation({ et: 'Sulge', en: 'Close' }),
        loading: translation({ et: 'Laadin…', en: 'Loading…' }),
        previous: translation({ et: 'Eelmine', en: 'Previous' }),
        next: translation({ et: 'Järgmine', en: 'Next' }),
        previousMonth: translation({ et: 'Eelmine kuu', en: 'Previous month' }),
        nextMonth: translation({ et: 'Järgmine kuu', en: 'Next month' }),
        openMenu: translation({ et: 'Ava menüü', en: 'Open menu' }),
        profileMenu: translation({ et: 'Profiili menüü', en: 'Profile menu' }),
        mobileNavigation: translation({ et: 'Mobiilne navigatsioon', en: 'Mobile navigation' }),
        breadcrumb: translation({ et: 'Asukohatee', en: 'Breadcrumb' }),
        scrollTabsLeft: translation({ et: 'Keri vahekaarte vasakule', en: 'Scroll tabs left' }),
        scrollTabsRight: translation({ et: 'Keri vahekaarte paremale', en: 'Scroll tabs right' }),
        // App shell, legacy menu and the shared HTTP client (http-utils.ts)
        shell: {
            openSidebar: translation({ et: 'Ava menüü', en: 'Open sidebar' }),
            closeSidebar: translation({ et: 'Sulge menüü', en: 'Close sidebar' }),
            infoMenu: translation({ et: 'Info', en: 'Info' }),
        },
        httpError: (status: number) =>
            translation({ et: `Päring ebaõnnestus (${status})`, en: `Request failed (${status})` }),
        logOut: translation({ et: 'Logi välja', en: 'Log out' }),
        admin: translation({ et: 'Admin', en: 'Admin' }),
        toggleMenu: translation({ et: 'Ava või sulge menüü', en: 'Toggle menu' }),
        closeModal: translation({ et: 'Sulge aken', en: 'Close modal' }),
        closeNotification: translation({ et: 'Sulge teade', en: 'Close notification' }),
        sectionNavigation: translation({ et: 'Jaotiste navigatsioon', en: 'Section navigation' }),
        // Name shown when a user record carries no display name.
        userFallback: translation({ et: 'Kasutaja', en: 'User' }),
        cropSourceAlt: translation({ et: 'Kärbitav pilt', en: 'Crop source' }),
        slider: translation({ et: 'Liugur', en: 'Slider' }),
        sliderMinimum: (label: string) =>
            translation({ et: `${label} miinimum`, en: `${label} minimum` }),
        sliderMaximum: (label: string) =>
            translation({ et: `${label} maksimum`, en: `${label} maximum` }),
        screenshotName: (n: number) =>
            translation({ et: `kuvatõmmis-${n}`, en: `screenshot-${n}` }),
        // Tenure badge, e.g. "3 a 2 k" / "3yr 2mo"; the years part is dropped under a year.
        tenure: (years: number, months: number) =>
            translation({
                et: years > 0 ? `${years} a ${months} k` : `${months} k`,
                en: years > 0 ? `${years}yr ${months}mo` : `${months}mo`,
            }),
        priority: {
            high: translation({ et: 'Kõrge', en: 'High' }),
            medium: translation({ et: 'Keskmine', en: 'Medium' }),
            low: translation({ et: 'Madal', en: 'Low' }),
        },
        wysiwyg: {
            bold: translation({ et: 'Rasvane', en: 'Bold' }),
            italic: translation({ et: 'Kaldkiri', en: 'Italic' }),
            bulletList: translation({ et: 'Täpploend', en: 'Bullet list' }),
            numberedList: translation({ et: 'Nummerdatud loend', en: 'Numbered list' }),
        },
        searchOverlay: {
            recent: translation({ et: 'Hiljutised', en: 'Recent' }),
            badges: translation({ et: 'Märgid', en: 'Badges' }),
            tools: translation({ et: 'Tööriistad', en: 'Tools' }),
            links: translation({ et: 'Lingid', en: 'Links' }),
            noToolsFound: translation({ et: 'Tööriistu ei leitud', en: 'No tools found' }),
            comingSoon: translation({ et: 'Tulekul', en: 'Coming soon' }),
            claimed: translation({ et: 'Saadud', en: 'Claimed' }),
            pending: translation({ et: 'Ootel', en: 'Pending' }),
            locked: translation({ et: 'Lukus', en: 'Locked' }),
        },
    },
    feedback: {
        triggerLabel: translation({ et: 'Jäta tagasiside', en: 'Leave feedback' }),
        triggerAriaLabel: translation({ et: 'Saada tagasisidet', en: 'Send feedback' }),
        title: translation({ et: 'Saada tagasisidet', en: 'Send feedback' }),
        closeAriaLabel: translation({ et: 'Sulge', en: 'Close' }),
        hint: translation({
            et: 'Kirjuta, mis juhtus või mida sooviksid näha. Lisaks kogume põhilist tehnilist konteksti (URL, brauser, ekraanisuurus, hiljutised konsoolivead), et viga reprodutseerida.',
            en: "Tell us what happened or what you'd like to see. We also collect basic technical context (URL, browser, viewport, recent console errors) to help reproduce.",
        }),
        messageLabel: translation({ et: 'Sõnum', en: 'Message' }),
        messagePlaceholder: translation({
            et: 'Kirjelda viga, jaga ideed või kirjuta, mis ei tööta…',
            en: "Describe the bug, share an idea, or tell us what's not working…",
        }),
        screenshotsLabel: (count: number) =>
            translation({ et: `Kuvatõmmised (${count})`, en: `Screenshots (${count})` }),
        addScreenshot: translation({ et: 'Lisa kuvatõmmis', en: 'Add screenshot' }),
        pasteHint: translation({
            et: 'Saad pildi ka kleepida (Cmd/Ctrl+V).',
            en: 'You can also paste an image (Cmd/Ctrl+V).',
        }),
        removeAttachmentAriaLabel: (name: string) =>
            translation({ et: `Eemalda ${name}`, en: `Remove ${name}` }),
        cancel: translation({ et: 'Tühista', en: 'Cancel' }),
        send: translation({ et: 'Saada', en: 'Send' }),
        sending: translation({ et: 'Saadan…', en: 'Sending…' }),
        successToast: translation({ et: 'Aitäh tagasiside eest!', en: 'Thanks for the feedback!' }),
        errorRequired: translation({
            et: 'Palun kirjelda, mida soovid raporteerida.',
            en: 'Please describe what you want to report.',
        }),
        errorTooLong: (max: number) =>
            translation({
                et: `Sõnum on liiga pikk (max ${max} tähemärki).`,
                en: `Message is too long (max ${max} characters).`,
            }),
        errorTooManyAttachments: (max: number) =>
            translation({
                et: `Saad lisada kuni ${max} kuvatõmmist.`,
                en: `You can attach up to ${max} screenshots.`,
            }),
        errorUploadFailed: (status: number) =>
            translation({
                et: `Üleslaadimine ebaõnnestus (${status})`,
                en: `Upload failed (${status})`,
            }),
        errorGeneric: translation({
            et: 'Tagasiside saatmine ebaõnnestus',
            en: 'Failed to submit feedback',
        }),
    },
    login: {
        subtitle: translation({ et: 'Sisene, et jätkata', en: 'Sign in to continue' }),
        signInWithGoogle: translation({ et: "Logi sisse Google'iga", en: 'Sign in with Google' }),
        domainNotAllowed: translation({
            et: 'Sisselogimine on lubatud ainult play.ee ja nope.ee kontodega.',
            en: 'Only play.ee and nope.ee accounts can sign in.',
        }),
    },
    profile: {
        unknown: translation({ et: 'Tundmatu', en: 'Unknown' }),
        loading: translation({ et: 'Laen profiili…', en: 'Loading profile…' }),
        overviewTab: translation({ et: 'Ülevaade', en: 'Overview' }),
        signatureTab: translation({ et: 'E-maili signatuur', en: 'Email signature' }),
        tenureTab: translation({ et: 'Tööstaaž', en: 'Tenure' }),
        tenureValue: (years: number, months: number) =>
            translation({ et: `${years} a ${months} k`, en: `${years}y ${months}m` }),
        tenureSince: translation({ et: 'Tööl alates', en: 'Employed since' }),
        noTenure: translation({ et: 'Tööstaaži pole', en: 'No tenure data' }),
        signatureIntro: translation({
            et: 'Vali bränd ja kopeeri oma e-maili signatuur. Signatuur luuakse automaatselt sinu profiili andmetest.',
            en: 'Pick a brand and copy your email signature. It is generated automatically from your profile data.',
        }),
        // Single-brand people (one company) have nothing to choose — drop the
        // "pick a brand" framing.
        signatureIntroSingle: translation({
            et: 'Kopeeri oma e-maili signatuur. Signatuur luuakse automaatselt sinu profiili andmetest.',
            en: 'Copy your email signature. It is generated automatically from your profile data.',
        }),
        signatureCopy: translation({ et: 'Kopeeri signatuur', en: 'Copy signature' }),
        signatureCopied: translation({ et: 'Kopeeritud!', en: 'Copied!' }),
        signatureCopyFailed: translation({
            et: 'Kopeerimine ebaõnnestus',
            en: 'Copy failed',
        }),
        signatureIncludePhone: translation({
            et: 'Lisa telefoninumber',
            en: 'Include phone number',
        }),
        signatureDarkPreview: translation({ et: 'Tume taust', en: 'Dark background' }),
        signatureLightPreview: translation({ et: 'Hele taust', en: 'Light background' }),
        signatureGuideTitle: translation({
            et: 'Kuidas signatuuri postkasti lisada?',
            en: 'How to add your signature to your mailbox',
        }),
        signatureGuideStep1: translation({
            et: 'Vali ülal sobiv bränd ja vajuta „Kopeeri signatuur".',
            en: 'Pick the right brand above and click “Copy signature”.',
        }),
        // Single-brand variant of step 1 (only one card, nothing to pick).
        signatureGuideStep1Single: translation({
            et: 'Vajuta „Kopeeri signatuur".',
            en: 'Click “Copy signature”.',
        }),
        signatureGuideStep2: translation({
            et: 'Ava oma postkasti seaded ja vali „General" → „Signature".',
            en: 'Open your mailbox settings and go to “General” → “Signature”.',
        }),
        signatureGuideStep3: translation({
            et: 'Vajuta „My signature" ja kleebi signatuur avanenud aknasse.',
            en: 'Click “My signature” and paste the signature into the box.',
        }),
        signatureGuideStep4: translation({
            et: 'Vajuta kõige all „Save changes" — signatuur lisatakse nüüd iga su kirja lõppu.',
            en: 'Click “Save changes” at the bottom — your signature is now added to the end of every email.',
        }),
        signatureGuideHelp: translation({
            et: 'Lisaküsimuste korral küsi oma tiimijuhilt või Mariannelt.',
            en: 'Got questions? Ask your team lead or Marianne.',
        }),
        signatureGuideImageAlt: translation({
            et: 'Gmaili signatuuri seadete vaade',
            en: 'Gmail signature settings view',
        }),
    },
    signature: {
        backButton: translation({ et: 'Tagasi', en: 'Back' }),
        createTitlePrefix: translation({ et: 'Lisa uus', en: 'Add new' }),
        editTitlePrefix: translation({ et: 'Muuda', en: 'Edit' }),
        titlePlaySuffix: translation({ et: 'play signatuur', en: 'play signature' }),
        titleNopeSuffix: translation({ et: 'nope signatuur', en: 'nope signature' }),
        requiredFields: translation({ et: '* Kohustuslikud väljad', en: '* Required fields' }),
        nameLabel: translation({ et: 'Eesnimi ja perenimi', en: 'First and last name' }),
        namePlaceholder: translation({ et: 'Peeter Rebane', en: 'John Smith' }),
        positionLabel: translation({ et: 'Ametikoha nimetus', en: 'Job title' }),
        positionPlaceholder: translation({
            et: 'Full-stack developer',
            en: 'Full-stack developer',
        }),
        emailLabel: translation({ et: 'E-posti aadress', en: 'Email address' }),
        emailPlaceholder: translation({ et: 'peeter@play.ee', en: 'john@play.ee' }),
        rowTitle: translation({ et: 'Pealkiri', en: 'Title' }),
        rowContent: translation({ et: 'Sisu', en: 'Content' }),
        rowLink: translation({ et: 'Lingi aadress', en: 'Link URL' }),
        isLink: translation({ et: 'Kas see on link?', en: 'Is this a link?' }),
        saveCreate: translation({ et: 'SALVESTA SIGNATUUR', en: 'SAVE SIGNATURE' }),
        saveEdit: translation({ et: 'SALVESTA MUUDATUSED', en: 'SAVE CHANGES' }),
        cancelCreate: translation({ et: 'TÜHISTA LISAMINE', en: 'CANCEL' }),
        cancelEdit: translation({ et: 'TÜHISTA MUUDATUSED', en: 'CANCEL CHANGES' }),
        addRow: translation({ et: 'LISA RIDA', en: 'ADD ROW' }),
        removeRow: translation({ et: 'KUSTUTA RIDA', en: 'REMOVE ROW' }),
        imageLabel: translation({ et: 'Profiilipilt', en: 'Profile image' }),
        selectImage: translation({ et: 'VALI PILT', en: 'SELECT IMAGE' }),
        removeImage: translation({ et: 'EEMALDA PILT', en: 'REMOVE IMAGE' }),
        preview: translation({ et: 'Eelvaade', en: 'Preview' }),
        toggleOff: translation({ et: 'Ei', en: 'No' }),
        toggleOn: translation({ et: 'Jah', en: 'Yes' }),
        logos: translation({ et: 'Logod', en: 'Logos' }),
        cropTitle: translation({ et: 'Lõika pilt', en: 'Crop image' }),
        headerTitle: translation({ et: 'E-mailide signatuurid', en: 'Email signatures' }),
        copy: translation({ et: 'KOPEERI', en: 'COPY' }),
        edit: translation({ et: 'MUUDA', en: 'EDIT' }),
        deleteSignature: translation({ et: 'KUSTUTA', en: 'DELETE' }),
        addPlaySignature: translation({ et: 'LISA PLAY SIGNATUUR', en: 'ADD PLAY SIGNATURE' }),
        addNopeSignature: translation({ et: 'LISA NOPE SIGNATUUR', en: 'ADD NOPE SIGNATURE' }),
        imageUploadError: translation({
            et: 'Pildi üleslaadimine ebaõnnestus',
            en: 'Image upload failed',
        }),
        signatureCreated: translation({ et: 'Signatuur loodud!', en: 'Signature created!' }),
        signatureSaved: translation({
            et: 'Signatuuri muudatused salvestatud!',
            en: 'Signature changes saved!',
        }),
        signatureSavedHint: translation({
            et: 'Saad seda nüüd kopeerida, muuta või kustutada.',
            en: 'You can now copy, edit, or delete it.',
        }),
        createError: translation({
            et: 'Signatuuri loomine ebaõnnestus',
            en: 'Failed to create signature',
        }),
        editError: translation({
            et: 'Signatuuri uuendamine ebaõnnestus',
            en: 'Failed to update signature',
        }),
        // Tool UI only — the generated signature's own content stays untranslated.
        menuLabel: translation({ et: 'Signatuur', en: 'Signature' }),
        installGuide: translation({
            et: 'Allkirja paigaldamise juhend',
            en: 'Signature setup guide',
        }),
        playLogoAlt: translation({ et: 'Play logo', en: 'Play logo' }),
        nopeLogoAlt: translation({ et: 'NOPE logo', en: 'NOPE logo' }),
        notRendered: translation({
            et: 'Signatuur pole veel renderdatud',
            en: 'The signature has not rendered yet',
        }),
        copied: translation({ et: 'Signatuur kopeeritud!', en: 'Signature copied!' }),
        copyFailed: translation({ et: 'Kopeerimine ebaõnnestus.', en: 'Copying failed.' }),
        deleted: translation({ et: 'Signatuur kustutatud!', en: 'Signature deleted!' }),
        deleteFailed: translation({
            et: 'Signatuuri kustutamine ebaõnnestus',
            en: 'Failed to delete signature',
        }),
        validationRequired: translation({ et: 'Täitke kõik väljad', en: 'Fill in all fields' }),
        validationEmail: translation({
            et: 'Palun sisesta kehtiv e-posti aadress (nt: nimi@example.com)',
            en: 'Please enter a valid email address (e.g. name@example.com)',
        }),
    },
    urlShortener: {
        menuLabel: translation({ et: 'URL tööriist', en: 'URL Tool' }),
        pageTitle: translation({ et: 'URL lühendaja', en: 'URL Shortener' }),
        pageSubtitle: translation({
            et: 'Loo QR-koode ja lühilinke sisekasutuseks.',
            en: 'Generate QR codes and short links for internal use.',
        }),
        newLink: translation({ et: 'Uus lühilink', en: 'New short link' }),
        createLink: translation({ et: 'Loo lühilink', en: 'Create short link' }),
        creating: translation({ et: 'Loon...', en: 'Creating...' }),
        originalUrl: translation({ et: 'Originaal-URL', en: 'Original URL' }),
        originalUrlPlaceholder: translation({
            et: 'https://example.com/pikk-url',
            en: 'https://example.com/long-url',
        }),
        title: translation({ et: 'Pealkiri (valikuline)', en: 'Title (optional)' }),
        titlePlaceholder: translation({
            et: 'nt. Meie uus maandumisleht',
            en: 'e.g. Our new landing page',
        }),
        brand: translation({ et: 'Bränd', en: 'Brand' }),
        shortUrl: translation({ et: 'Lühilink', en: 'Short URL' }),
        hits: translation({ et: 'Klikkide arv', en: 'Hits' }),
        created: translation({ et: 'Loodud', en: 'Created' }),
        lastAccessed: translation({ et: 'Viimati kasutatud', en: 'Last accessed' }),
        never: translation({ et: 'Pole veel kasutatud', en: 'Never' }),
        copyLink: translation({ et: 'Kopeeri link', en: 'Copy link' }),
        copied: translation({ et: 'Kopeeritud!', en: 'Copied!' }),
        viewDetails: translation({ et: 'Vaata detaile', en: 'View details' }),
        deleteLink: translation({ et: 'Kustuta lühilink', en: 'Delete short link' }),
        confirmDelete: translation({
            et: 'Kas oled kindel, et soovid selle lühilingi kustutada?',
            en: 'Are you sure you want to delete this short link?',
        }),
        linkCreated: translation({ et: 'Lühilink loodud!', en: 'Short link created!' }),
        linkDeleted: translation({ et: 'Lühilink kustutatud.', en: 'Short link deleted.' }),
        createError: translation({
            et: 'Lühilingi loomine ebaõnnestus.',
            en: 'Failed to create short link.',
        }),
        noLinks: translation({
            et: 'Sul pole veel ühtegi lühilinki. Loo esimene!',
            en: "You don't have any short links yet. Create your first one!",
        }),
        // Detail view
        backToList: translation({ et: 'Tagasi', en: 'Back' }),
        stats: translation({ et: 'Statistika', en: 'Statistics' }),
        totalHits: translation({ et: 'Klikkide arv kokku', en: 'Total hits' }),
        uniqueIps: translation({ et: 'Unikaalsed IP-d', en: 'Unique IPs' }),
        deviceBreakdown: translation({ et: 'Seadmete jaotus', en: 'Device breakdown' }),
        realTraffic: translation({ et: 'Päris liiklus', en: 'Real traffic' }),
        botTraffic: translation({ et: 'Botid', en: 'Bot traffic' }),
        qrCustomizer: translation({ et: 'QR-koodi seaded', en: 'QR code settings' }),
        fgColor: translation({ et: 'Esiplaan', en: 'Foreground' }),
        bgColor: translation({ et: 'Taust', en: 'Background' }),
        dotStyle: translation({ et: 'Stiil', en: 'Dot style' }),
        dotStyleSquare: translation({ et: 'Ruut', en: 'Square' }),
        dotStyleCircle: translation({ et: 'Ring', en: 'Circle' }),
        dotRadius: translation({ et: 'Ringi raadius', en: 'Circle radius' }),
        logo: translation({ et: 'Logo', en: 'Logo' }),
        logoNone: translation({ et: 'Pole', en: 'None' }),
        logoPlay: translation({ et: 'Play', en: 'Play' }),
        logoNope: translation({ et: 'Nope', en: 'Nope' }),
        logoEmpty: translation({ et: 'Tühi ala', en: 'Empty space' }),
        saveQr: translation({ et: 'Salvesta QR seaded', en: 'Save QR settings' }),
        savingQr: translation({ et: 'Salvestan...', en: 'Saving...' }),
        downloadQr: translation({ et: 'Laadi alla SVG', en: 'Download SVG' }),
        accessLog: translation({ et: 'Ligipääsu logi', en: 'Access log' }),
        logDate: translation({ et: 'Kuupäev', en: 'Date' }),
        logIp: translation({ et: 'IP-aadress', en: 'IP address' }),
        logDevice: translation({ et: 'Seade', en: 'Device' }),
        logBot: translation({ et: 'Bot', en: 'Bot' }),
        logReferer: translation({ et: 'Viitaja', en: 'Referer' }),
        logUserAgent: translation({ et: 'Brauser/seade', en: 'User agent' }),
        noLogs: translation({
            et: 'Ühtegi ligipääsu pole veel logitud.',
            en: 'No access logs yet.',
        }),
        loadMore: translation({ et: 'Laadi veel', en: 'Load more' }),
        notFound: translation({ et: 'Lühilinki ei leitud.', en: 'Short link not found.' }),
        noAccess: translation({
            et: 'Sul pole sellele lühilingile ligipääsu.',
            en: 'You do not have access to this short link.',
        }),
        qrPreviewAlt: translation({ et: 'QR-koodi eelvaade', en: 'QR code preview' }),
        // Device types from url_access_logs.device_type (+ 'unknown'); unknown raw
        // values fall back to the raw value in deviceTypeLabel().
        deviceTypes: {
            desktop: translation({ et: 'Arvuti', en: 'Desktop' }),
            mobile: translation({ et: 'Mobiil', en: 'Mobile' }),
            tablet: translation({ et: 'Tahvel', en: 'Tablet' }),
            bot: translation({ et: 'Bot', en: 'Bot' }),
            unknown: translation({ et: 'Tundmatu', en: 'Unknown' }),
        },
    },
    cropper: {
        save: translation({ et: 'Salvesta', en: 'Save' }),
        cancel: translation({ et: 'Tühista', en: 'Cancel' }),
        processing: translation({ et: 'Töötlemine…', en: 'Processing…' }),
        cropTitle: translation({ et: 'Pildi kärpimine', en: 'Crop image' }),
        backToSelect: translation({ et: 'Tagasi pilti valima', en: 'Back to image selection' }),
        failedToCrop: translation({
            et: 'Pildi kärpimine ebaõnnestus.',
            en: 'Failed to crop image.',
        }),
    },
    portal: {
        menuLabel: translation({ et: 'Portal', en: 'Portal' }),
        // Shell / sidebar
        nav: {
            dashboard: translation({ et: 'Töölaud', en: 'Dashboard' }),
            tasks: translation({ et: 'Ülesanded', en: 'Tasks' }),
            worktime: translation({ et: 'Tööaeg', en: 'Worktime' }),
            viewAll: translation({ et: 'Vaata kõiki', en: 'View all' }),
            people: translation({ et: 'Inimesed', en: 'People' }),
            calendar: translation({ et: 'Kes on eemal', en: "Who's away" }),
            reports: translation({ et: 'Raportid', en: 'Reports' }),
            auditLog: translation({ et: 'Auditilogi', en: 'Audit Log' }),
            errorLog: translation({ et: 'Vealogi', en: 'Error Log' }),
            settings: translation({ et: 'Tööruumi seaded', en: 'Workspace Settings' }),
            adminSection: translation({ et: 'Haldus', en: 'Administration' }),
            viewProfile: translation({ et: 'Vaata profiili', en: 'View profile' }),
            manageNotifications: translation({
                et: 'Halda teavitusi',
                en: 'Manage notifications',
            }),
            changeLanguage: translation({ et: 'Vaheta keelt', en: 'Change language' }),
            logOut: translation({ et: 'Logi välja', en: 'Log out' }),
            myDocuments: translation({ et: 'Dokumendid', en: 'Documents' }),
            more: translation({ et: 'Veel', en: 'More' }),
            links: translation({ et: 'Välised lingid', en: 'External links' }),
            profile: translation({ et: 'Profiil', en: 'Profile' }),
            tools: translation({ et: 'Tööriistad', en: 'Tools' }),

            // New navigation items and groups
            meSection: translation({ et: 'Mina', en: 'Me' }),
            myDashboard: translation({ et: 'Töölaud', en: 'Dashboard' }),
            myAbsences: translation({ et: 'Eemalolek', en: 'Time away' }),
            myTasks: translation({ et: 'Ülesanded', en: 'Tasks' }),
            myWorktime: translation({ et: 'Tööaeg', en: 'Worktime' }),
            myProjects: translation({ et: 'Projektid', en: 'Projects' }),
            myAssets: translation({ et: 'Varad', en: 'Assets' }),
            myThings: translation({ et: 'Asjad', en: 'Stuff' }),
            myData: translation({ et: 'Andmed', en: 'Data' }),
            mySettings: translation({ et: 'Seaded', en: 'Settings' }),
            peopleSection: translation({ et: 'Inimesed', en: 'People' }),
            peopleListEvents: translation({
                et: 'Inimesed (nimekiri + sündmused)',
                en: 'People (list + events)',
            }),
            absences: translation({ et: 'Kes on eemal', en: "Who's away" }),
            cultureSection: translation({ et: 'Kultuur', en: 'Culture' }),
            events: translation({ et: 'Üritused', en: 'Events' }),
            playtime: translation({ et: 'Märkide kogumine', en: 'Badge collecting' }),
        },
        // Header navigation tabs (short labels, distinct from the sidebar nav)
        tabs: {
            home: translation({ et: 'Kodu', en: 'Home' }),
            away: translation({ et: 'Kes on eemal', en: "Who's away" }),
            people: translation({ et: 'Inimesed', en: 'People' }),
            events: translation({ et: 'Üritused', en: 'Events' }),
            reports: translation({ et: 'Raportid', en: 'Reports' }),
        },
        // Documents view (embedded Google Drive)
        documents: {
            title: translation({ et: 'Dokumendid', en: 'Documents' }),
            empty: translation({
                et: 'Dokumentide kausta pole veel seadistatud.',
                en: 'No documents folder has been configured yet.',
            }),
            openInDrive: translation({ et: 'Ava Google Drive’is', en: 'Open in Google Drive' }),
            loading: translation({ et: 'Laadin…', en: 'Loading…' }),
            home: translation({ et: 'Dokumendid', en: 'Documents' }),
            emptyFolder: translation({ et: 'See kaust on tühi.', en: 'This folder is empty.' }),
            loadMore: translation({ et: 'Laadi veel', en: 'Load more' }),
            connect: translation({ et: 'Ühenda Google Drive', en: 'Connect Google Drive' }),
            connectDescription: translation({
                et: 'Ühenda oma Google Drive, et sirvida oma dokumente siin rakenduses.',
                en: 'Connect your Google Drive to browse your documents inside the app.',
            }),
            reconnect: translation({ et: 'Ühenda uuesti', en: 'Reconnect' }),
            reconnectDescription: translation({
                et: 'Google Drive’i ühendus on aegunud. Palun ühenda uuesti.',
                en: 'Your Google Drive access has expired. Please reconnect.',
            }),
            disconnect: translation({ et: 'Katkesta ühendus', en: 'Disconnect' }),
            noDocuments: translation({ et: 'Dokumente pole.', en: 'No documents.' }),
            view: translation({ et: 'Ava', en: 'View' }),
            viewInGoogleDocs: translation({ et: 'Ava Google Docsis', en: 'View in Google Docs' }),
            viewInGoogleDrive: translation({
                et: 'Ava Google Drive’is',
                en: 'View in Google Drive',
            }),
            browseHint: translation({
                et: 'Ühenda Google Drive, et sirvida kaustu siin rakenduses.',
                en: 'Connect Google Drive to browse folders inside the app.',
            }),
            unverifiedHint: translation({
                et: 'Esmakordsel ühendamisel võib ilmuda hoiatus „Google hasn’t verified this app”. See on ootuspärane — vajuta Advanced ja seejärel Continue.',
                en: 'First time connecting? You may see a “Google hasn’t verified this app” screen — that’s expected. Click Advanced, then Continue to finish.',
            }),
        },
        // My assets (/me/assets) — asset-management integration
        assets: {
            notConfigured: translation({
                et: 'Varade süsteem ei ole veel ühendatud.',
                en: 'The asset system is not connected yet.',
            }),
            noAccount: translation({
                et: 'Sinu e-postiga ei leitud varade süsteemist kontot.',
                en: 'No asset-system account matches your email.',
            }),
            error: translation({
                et: 'Varade laadimine ebaõnnestus. Proovi hiljem uuesti.',
                en: 'Failed to load assets. Try again later.',
            }),
            empty: translation({
                et: 'Sinu nimel ei ole praegu ühtegi vara.',
                en: 'No assets are checked out to you right now.',
            }),
            colName: translation({ et: 'Nimi', en: 'Name' }),
            colTag: translation({ et: 'Kood', en: 'Tag' }),
            colModel: translation({ et: 'Mudel', en: 'Model' }),
            colSerial: translation({ et: 'Seerianumber', en: 'Serial' }),
            colSince: translation({ et: 'Alates', en: 'Since' }),
            claimTitle: translation({ et: 'Võta vara enda nimele', en: 'Claim an asset' }),
            claimDescription: translation({
                et: 'Sisesta vara kood või ainult number (kleebiselt), et see enda nimele võtta.',
                en: 'Enter the asset tag or just its number (from its sticker) to claim it.',
            }),
            claimPlaceholder: translation({ et: 'Vara kood…', en: 'Asset tag…' }),
            claimSearch: translation({ et: 'Otsi', en: 'Search' }),
            claimButton: translation({ et: 'Võta enda nimele', en: 'Claim' }),
            claimSuccess: translation({
                et: 'Vara on nüüd sinu nimel',
                en: 'The asset is now checked out to you',
            }),
            lookupNotFound: translation({
                et: 'Selle koodiga vara ei leitud',
                en: 'No asset with this tag',
            }),
            lookupMultiple: translation({
                et: 'Sama numbriga leiti mitu vara — vali õige.',
                en: 'Several assets share this number — pick the right one.',
            }),
            lookupTaken: translation({
                et: 'See vara ei ole vaba',
                en: 'This asset is not available',
            }),
            lookupTakenBy: (name: string) =>
                translation({
                    et: `See vara on ${name} käes`,
                    en: `Checked out to ${name}`,
                }),
            openInAssetSystem: translation({
                et: 'Ava varade süsteem',
                en: 'Open the asset system',
            }),
        },
        // "View as" — admin impersonation (Settings tab + warning banner)
        viewAs: {
            description: translation({
                et: 'Vaata portaali teise kasutajana, et kontrollida, mida keegi näeb ja mida mitte. Kõik tegevused salvestatakse auditilogisse sinu nimel.',
                en: 'View the portal as another user to verify what they can and cannot see. All actions are recorded in the audit log under your name.',
            }),
            searchPlaceholder: translation({
                et: 'Otsi nime või e-posti järgi…',
                en: 'Search by name or email…',
            }),
            viewAsButton: translation({ et: 'Vaata kasutajana', en: 'View as' }),
            emptyList: translation({
                et: 'Ühtegi kasutajat ei leitud',
                en: 'No users found',
            }),
            startError: translation({
                et: 'Vaate alustamine ebaõnnestus',
                en: 'Failed to start viewing as user',
            }),
            bannerViewingAs: (name: string) =>
                translation({
                    et: `Vaatad portaali kasutajana ${name}`,
                    en: `Viewing the portal as ${name}`,
                }),
            bannerExit: translation({ et: 'Välju vaatest', en: 'Exit view' }),
        },
        // Events page (Notion-backed)
        events: {
            title: translation({ et: 'Üritused', en: 'Events' }),
            filterAll: translation({ et: 'Kõik', en: 'All' }),
            filterUpcoming: translation({ et: 'Ainult tulevased', en: 'Upcoming only' }),
            yearSelectAria: translation({ et: 'Vali aasta', en: 'Select year' }),
            loading: translation({ et: 'Laen üritusi...', en: 'Loading events...' }),
            untitled: translation({ et: 'Pealkirjata üritus', en: 'Untitled event' }),
            modalLoading: translation({
                et: 'Laen ürituse infot...',
                en: 'Loading event details...',
            }),
            modalEmpty: translation({
                et: 'Lisainfot pole.',
                en: 'No additional details provided.',
            }),
            openInNotion: translation({ et: 'Ava Notionis', en: 'Open in Notion' }),
            copyLink: translation({ et: 'Kopeeri link', en: 'Copy link' }),
            closeEventDetails: translation({ et: 'Sulge ürituse info', en: 'Close event details' }),
            tba: translation({ et: 'Täpsustamisel', en: 'TBA' }),
            // Labels of the copied plain-text event summary
            copyDateLabel: translation({ et: 'Kuupäev', en: 'Date' }),
            copyLocationLabel: translation({ et: 'Koht', en: 'Location' }),
            copyLinkLabel: translation({ et: 'Link', en: 'Link' }),
        },
        // People directory
        people: {
            title: translation({ et: 'Inimesed', en: 'People' }),
            search: translation({ et: 'Otsi inimesi...', en: 'Search people...' }),
            listView: translation({ et: 'Nimekiri', en: 'List' }),
            gridView: translation({ et: 'Ruudustik', en: 'Grid' }),
            name: translation({ et: 'Nimi', en: 'Name' }),
            jobTitle: translation({ et: 'Ametikoht', en: 'Job title' }),
            team: translation({ et: 'Tiim', en: 'Team' }),
            company: translation({ et: 'Ettevõte', en: 'Company' }),
            status: translation({ et: 'Staatus', en: 'Status' }),
            count: (n: number) =>
                translation({ et: `${n} töötajat`, en: `${n} employee${n === 1 ? '' : 's'}` }),
            // Create employee
            createTitle: translation({ et: 'Lisa uus töötaja', en: 'Add New Employee' }),
            firstName: translation({ et: 'Eesnimi', en: 'First Name' }),
            lastName: translation({ et: 'Perenimi', en: 'Last Name' }),
            companyLabel: translation({ et: 'Ettevõte', en: 'Company' }),
            hireDateLabel: translation({ et: 'Tööle asumise kuupäev', en: 'Hire Date' }),
            workEmail: translation({ et: 'Töö e-post', en: 'Work Email' }),
            createBtn: translation({ et: 'Lisa töötaja', en: 'Add Employee' }),
            creating: translation({ et: 'Lisan...', en: 'Adding...' }),
            createSuccess: translation({ et: 'Töötaja lisatud!', en: 'Employee added!' }),
            createError: translation({
                et: 'Töötaja lisamine ebaõnnestus',
                en: 'Failed to add employee',
            }),
            selectCompany: translation({ et: 'Vali ettevõte', en: 'Select company' }),
            selectTeam: translation({ et: 'Vali tiim', en: 'Select team' }),

            // ── Org chart ──
            orgChartTooltipTitle: translation({ et: 'Organisatsiooni skeem', en: 'Org chart' }),
            orgChartTooltipDesc: translation({
                et: 'Visualiseeri ettevõtte struktuur',
                en: "Visualise your company's structure",
            }),
            orgChartTitle: (n: number) =>
                translation({
                    et: `Organisatsiooni skeem · ${n} liiget`,
                    en: `Org chart · ${n} member${n === 1 ? '' : 's'}`,
                }),
            orgChartLeave: translation({ et: 'Välju', en: 'Leave' }),
            orgChartReports: (n: number) =>
                translation({ et: `${n} alluvat`, en: `${n} report${n === 1 ? '' : 's'}` }),
            orgChartZoomIn: translation({ et: 'Suurenda', en: 'Zoom in' }),
            orgChartZoomOut: translation({ et: 'Vähenda', en: 'Zoom out' }),
            orgChartFit: translation({ et: 'Mahuta vaatesse', en: 'Fit to screen' }),
            orgChartAllTeams: translation({ et: 'Kõik tiimid', en: 'All teams' }),
            orgChartTeamFilter: translation({ et: 'Filtreeri tiimi järgi', en: 'Filter by team' }),

            // ── Tabs ──
            activeTab: translation({ et: 'Aktiivsed', en: 'Active' }),
            eventsTab: translation({ et: 'Sündmused', en: 'Events' }),
            offboardedTab: translation({ et: 'Lahkunud', en: 'Offboarded' }),

            // Row sub-line connector: "{job title} in {company}". Estonian word
            // order makes a literal "in" awkward, so use a separator there.
            inCompany: translation({ et: '·', en: 'in' }),

            // ── Filter / Sort ──
            filterBtn: translation({ et: 'Filtreeri', en: 'Filter' }),
            sortBtn: translation({ et: 'Sorteeri', en: 'Sort' }),
            filterPlaceholder: translation({ et: 'Filtreeri...', en: 'Filter...' }),
            sortPlaceholder: translation({ et: 'Sorteeri...', en: 'Sort...' }),
            clearAll: translation({ et: 'Tühjenda', en: 'Clear all' }),

            // Filter dimension labels
            placeOfWork: translation({ et: 'Töökoht', en: 'Place of work' }),
            country: translation({ et: 'Riik', en: 'Country' }),
            contractType: translation({ et: 'Lepingu tüüp', en: 'Contract type' }),
            manager: translation({ et: 'Juht', en: 'Manager' }),

            // Contract type values
            contractTypes: {
                permanent: translation({ et: 'Tähtajatu', en: 'Permanent' }),
                fixed_term: translation({ et: 'Tähtajaline', en: 'Fixed term' }),
                intern: translation({ et: 'Praktikant', en: 'Intern' }),
                flex: translation({ et: 'Paindlik', en: 'Flex' }),
                part_time: translation({ et: 'Osakoormus', en: 'Part time' }),
                full_time: translation({ et: 'Täiskoormus', en: 'Full time' }),
            },

            // Filter "all" placeholders
            allCompanies: translation({ et: 'Kõik ettevõtted', en: 'All companies' }),
            allTeams: translation({ et: 'Kõik tiimid', en: 'All teams' }),
            allLocations: translation({ et: 'Kõik töökohad', en: 'All locations' }),
            allCountries: translation({ et: 'Kõik riigid', en: 'All countries' }),
            allContractTypes: translation({ et: 'Kõik lepingu tüübid', en: 'All contract types' }),
            allManagers: translation({ et: 'Kõik juhid', en: 'All managers' }),

            // Sort options
            sortFirstName: translation({ et: 'Eesnimi', en: 'First name' }),
            sortLastName: translation({ et: 'Perenimi', en: 'Last name' }),
            sortStartDate: translation({
                et: 'Tööle asumise kuupäev',
                en: 'Employment start date',
            }),
            sortLeaveType: translation({ et: 'Puhkuse liik', en: 'Leave type' }),

            // ── Events ──
            upcomingEvents: translation({ et: 'Tulevased sündmused', en: 'Upcoming events' }),
            noEvents: translation({ et: 'Tulevasi sündmusi pole', en: 'No upcoming events' }),
            evBirthday: translation({ et: 'Sünnipäev', en: 'Birthday' }),
            evAnniversary: translation({ et: 'Tööaastapäev', en: 'Work anniversary' }),
            evFirstDay: translation({ et: 'Esimene tööpäev', en: 'First day' }),
            evProbationEnd: translation({ et: 'Katseaja lõpp', en: 'Probation ends' }),
            evOffboarding: translation({ et: 'Viimane tööpäev', en: 'Last day' }),
            evAnniversaryYears: (n: number) =>
                translation({ et: `${n}. tööaastapäev`, en: `${n}-year anniversary` }),

            // Relative future-day phrasing
            relToday: translation({ et: 'täna', en: 'today' }),
            relTomorrow: translation({ et: 'homme', en: 'tomorrow' }),
            relInDays: (n: number) => translation({ et: `${n} päeva pärast`, en: `in ${n} days` }),
            // Inline directory-row badge for a future hire who isn't working yet.
            joiningBadge: translation({ et: 'Liitub', en: 'Joining' }),
            // Inline directory-row badge for someone with a known last day ahead.
            leavingBadge: translation({ et: 'Lahkub', en: 'Leaving' }),
        },
        // Post-login integrations gate
        onboarding: {
            title: translation({ et: 'Seadista oma liidestused', en: 'Set up your integrations' }),
            description: translation({
                et: 'Harvesti ja Google Calendari liidestused on isikupõhised ja nendest sõltuvad ka teised — näiteks koosolekute planeerimine ja töömahu hindamine. Palun ühenda need ära.',
                en: 'The Harvest and Google Calendar integrations are personal, and colleagues rely on them — e.g. for meeting planning and capacity checks. Please connect them.',
            }),
            harvestTitle: translation({ et: 'Harvest', en: 'Harvest' }),
            harvestDescription: translation({
                et: 'Ajaarvestus — sinu tööaja täituvus.',
                en: 'Time tracking — your work-time fulfilment.',
            }),
            calendarTitle: translation({ et: 'Google Calendar', en: 'Google Calendar' }),
            calendarDescription: translation({
                et: 'Eemalolekud ilmuvad automaatselt sinu kalendrisse.',
                en: 'Time away appears automatically in your calendar.',
            }),
            connect: translation({ et: 'Ühenda', en: 'Connect' }),
            connected: translation({ et: 'Ühendatud', en: 'Connected' }),
            skip: translation({ et: 'Jäta praegu vahele', en: 'Skip for now' }),
            note: translation({
                et: 'Hiljem leiad need: Minu andmed → Seaded',
                en: 'Later available under: My data → Settings',
            }),
        },
        // Employee profile
        profile: {
            loading: translation({ et: 'Laen profiili...', en: 'Loading profile...' }),
            overviewTab: translation({ et: 'Ülevaade', en: 'Overview' }),
            personalTab: translation({ et: 'Isiklik', en: 'Personal' }),
            employmentHistoryTab: translation({ et: 'Töökäik', en: 'Work History' }),

            timeAwayTab: translation({ et: 'Eemalolek', en: 'Time away' }),

            // Consolidated profile tabs (Andmed / Eemalolekud / Seaded). The
            // five old data tabs are stacked under "Andmed" as labelled groups.
            dataTab: translation({ et: 'Andmed', en: 'Data' }),
            settingsTab: translation({ et: 'Seaded', en: 'Settings' }),
            projectsTab: translation({ et: 'Projektid', en: 'Projects' }),
            generalGroup: translation({ et: 'Üldandmed', en: 'General' }),
            settingsLanguageTitle: translation({ et: 'Keel', en: 'Language' }),
            settingsAppearanceTitle: translation({ et: 'Välimus', en: 'Appearance' }),
            settingsNotificationsTitle: translation({ et: 'Teavitused', en: 'Notifications' }),
            settingsWeeklyDigestLabel: translation({
                et: 'Soovin reedel saada uue nädala kogu ettevõtte eemalviibimiste kokkuvõtet',
                en: "Send me a company-wide summary of next week's absences on Fridays",
            }),
            settingsWeeklyDigestNote: translation({
                et: 'Kokkuvõte saadetakse sulle Slackis privaatsõnumina.',
                en: 'The summary is sent to you as a Slack direct message.',
            }),
            settingsApiKeysTitle: translation({ et: 'API võtmed', en: 'API keys' }),
            settingsAvatarTitle: translation({ et: 'Profiilipilt', en: 'Profile picture' }),
            settingsAvatarDescription: translation({
                et: 'Lae üles oma profiilipilt. See kuvatakse sinu profiilil, ülal navigatsiooniribas ja e-maili signatuuris.',
                en: 'Upload your profile picture. It will be shown on your profile, in the top navigation bar, and in your email signature.',
            }),
            settingsAvatarUpload: translation({ et: 'Lae pilt üles', en: 'Upload image' }),
            settingsAvatarRemove: translation({ et: 'Eemalda pilt', en: 'Remove image' }),
            settingsAvatarUploading: translation({ et: 'Laen üles...', en: 'Uploading...' }),
            settingsAvatarMaxSize: translation({
                et: 'Max 5 MB (JPG, PNG, WEBP)',
                en: 'Max 5 MB (JPG, PNG, WEBP)',
            }),
            avatarPopoverNoImage: translation({
                et: 'Lae üles oma profiilipilt. Seda kuvatakse sinu profiilil, ülal navigatsiooniribas ja e-maili signatuuris.',
                en: 'Upload your profile picture. It will be shown on your profile, in the top navigation bar, and in your email signature.',
            }),
            avatarPopoverHasImage: translation({
                et: 'Profiilipilti kuvatakse sinu profiilil, ülal navigatsiooniribas ja e-maili signatuuris.',
                en: 'Your profile picture is shown on your profile, in the top navigation bar, and in your email signature.',
            }),
            avatarPopoverMaxSize: translation({
                et: 'Maksimum suurus 5 MB (JPG, PNG, WEBP).',
                en: 'Maximum size 5 MB (JPG, PNG, WEBP).',
            }),
            avatarPopoverChoose: translation({ et: 'Vali pilt', en: 'Choose image' }),
            avatarPopoverChange: translation({ et: 'Vaheta pilt', en: 'Change image' }),
            avatarPopoverRemove: translation({ et: 'Eemalda', en: 'Remove' }),
            saveContact: translation({ et: 'Salvesta kontakt', en: 'Save contact' }),

            integrations: {
                tabLabel: translation({ et: 'Lõimingud', en: 'Integrations' }),
                connected: translation({ et: 'Ühendatud ✓', en: 'Connected ✓' }),
                disconnect: translation({ et: 'Eemalda', en: 'Disconnect' }),
                loading: translation({ et: 'Laen...', en: 'Loading...' }),
                harvestTitle: translation({ et: 'Harvest', en: 'Harvest' }),
                harvestDescription: translation({
                    et: 'Ühenda oma Harvesti konto ajaarvestuse jaoks.',
                    en: 'Connect your Harvest account for time tracking.',
                }),
                connectHarvest: translation({ et: 'Ühenda Harvest', en: 'Connect Harvest' }),
                driveTitle: translation({ et: 'Google Drive', en: 'Google Drive' }),
                driveDescription: translation({
                    et: 'Ühenda oma Google Drive, et sirvida oma dokumente rakenduses.',
                    en: 'Connect your Google Drive to browse your documents in the app.',
                }),
                connectDrive: translation({
                    et: 'Ühenda Google Drive',
                    en: 'Connect Google Drive',
                }),
                reconnectDrive: translation({ et: 'Ühenda uuesti', en: 'Reconnect' }),
                calendarTitle: translation({ et: 'Google Kalender', en: 'Google Calendar' }),
                calendarDescription: translation({
                    et: 'Ühenda oma Google Kalender, et kinnitatud puhkused lisataks automaatselt sinu kalendrisse (Out of Office).',
                    en: 'Connect your Google Calendar so approved leave is added to your calendar as Out of Office.',
                }),
                connectCalendar: translation({
                    et: 'Ühenda Google Kalender',
                    en: 'Connect Google Calendar',
                }),
                reconnectCalendar: translation({ et: 'Ühenda uuesti', en: 'Reconnect' }),
            },

            orgChart: translation({ et: 'Organisatsioonipuu', en: 'Org Chart' }),
            reportsTo: translation({ et: 'Tiimijuht', en: 'Team lead' }),
            directReports: translation({ et: 'Tiimi liikmed', en: 'Team members' }),
            moreReports: (n: number) => translation({ et: `+${n} veel`, en: `+${n} more` }),
            noTimeOff: translation({
                et: 'Eemaloleku andmed puuduvad',
                en: 'No time off data available',
            }),
            // Overview fields
            basicInfo: translation({ et: 'Põhiandmed', en: 'Basic Info' }),
            // Profile "Andmed" section titles (TASK-438 restructure)
            generalInfoSection: translation({ et: 'Üldinfo', en: 'General info' }),
            employmentWorkloadSection: translation({
                et: 'Töösuhe ja -koormus',
                en: 'Employment & workload',
            }),
            compensationBonusesSection: translation({
                et: 'Tasustamine ja boonused',
                en: 'Compensation & bonuses',
            }),
            bankDetailsSection: translation({ et: 'Pangaandmed', en: 'Bank details' }),
            workloadSubsection: translation({
                et: 'Tööaeg ja töökoormus',
                en: 'Working time & workload',
            }),
            employmentHistorySection: translation({
                et: 'Ametikohtade ja töösuhte ajalugu',
                en: 'Position & employment history',
            }),
            // Shown in the merged Üldinfo edit form above the dated employment
            // fields — changing them creates a new history record.
            employmentChangeHint: translation({
                et: 'Ametinimetuse, tiimi, juhi, asukoha või töörežiimi muutmine loob valitud kuupäevast uue kirje ajalukku.',
                en: 'Changing job title, team, manager, location or work mode creates a new history record from the chosen date.',
            }),
            department: translation({ et: 'Osakond', en: 'Department' }),
            team: translation({ et: 'Tiim', en: 'Team' }),
            location: translation({ et: 'Asukoht', en: 'Location' }),
            // Manager-confirmed work-location change flow
            locationRequestChange: translation({ et: 'Muuda asukohta', en: 'Change location' }),
            locationSubmit: translation({ et: 'Saada kinnitamiseks', en: 'Send for approval' }),
            locationPending: (label: string) =>
                translation({
                    et: `Ootab juhi kinnitust: ${label}`,
                    en: `Pending manager approval: ${label}`,
                }),
            locationApprove: translation({ et: 'Kinnita', en: 'Approve' }),
            locationReject: translation({ et: 'Lükka tagasi', en: 'Reject' }),
            locationNote: translation({
                et: 'Muudatuse peab kinnitama sinu otsene juht.',
                en: 'Your direct manager must confirm the change.',
            }),
            locationOther: translation({ et: 'Muu...', en: 'Other...' }),
            locationCountry: translation({ et: 'Riik', en: 'Country' }),
            locationCity: translation({ et: 'Linn', en: 'City' }),
            workMode: translation({ et: 'Töörežiim', en: 'Work Mode' }),
            // Work-mode options (portal_employment_records.work_mode). Stable
            // keys (office/hybrid/remote/abroad) — `remote` reads as "home office".
            workModes: {
                office: translation({ et: 'Kontor', en: 'Office' }),
                hybrid: translation({ et: 'Hübriid', en: 'Hybrid' }),
                remote: translation({ et: 'Kodukontor', en: 'Home office' }),
                abroad: translation({ et: 'Välismaa', en: 'Abroad' }),
            },
            manager: translation({ et: 'Otsene juht', en: 'Direct Manager' }),
            jobTitle: translation({ et: 'Ametikoht', en: 'Job Title' }),
            // Admin-editable identity fields (in-place, on portal_employees / the active record)
            firstName: translation({ et: 'Eesnimi', en: 'First Name' }),
            lastName: translation({ et: 'Perenimi', en: 'Last Name' }),
            workEmail: translation({ et: 'Töö e-post', en: 'Work Email' }),
            // Admin-only "edit employment" affordance (creates a new dated SCD2 record)
            editEmployment: translation({ et: 'Muuda töösuhet', en: 'Edit employment' }),
            // Shown above the read-only employment rows while editing personal info,
            // explaining why team/location/work-mode/manager can't be changed here.
            managedByHr: translation({
                et: 'Keelatud välju saavad muuta juhid',
                en: 'Disabled fields can be edited by managers',
            }),
            effectiveFrom: translation({ et: 'Kehtib alates', en: 'Effective from' }),
            hireDate: translation({ et: 'Tööle asumise kuupäev', en: 'Hire Date' }),
            collaborationType: translation({ et: 'Koostöövorm', en: 'Collaboration form' }),
            collaborationTypes: {
                employment_full_time: translation({
                    et: 'Tööleping, täistööaeg',
                    en: 'Employment contract, full-time',
                }),
                employment_part_time: translation({
                    et: 'Tööleping, osaline tööaeg',
                    en: 'Employment contract, part-time',
                }),
                employment_flex: translation({
                    et: 'Tööleping, FLEX',
                    en: 'Employment contract, FLEX',
                }),
                intern: translation({ et: 'Praktikant', en: 'Intern' }),
                board_member: translation({ et: 'Juhatuse liige', en: 'Board member' }),
                partner: translation({ et: 'Koostööpartner', en: 'Partner' }),
            },
            workPhone: translation({ et: 'Töötelefon', en: 'Work Phone' }),
            personalPhone: translation({ et: 'Isiklik telefon', en: 'Personal Phone' }),
            phoneCountryCodeHint: translation({
                et: 'Lisa suunakood, nt +372 5123 4567',
                en: 'Add the country code, e.g. +372 5123 4567',
            }),
            roleDetails: translation({ et: 'Rolli kirjeldus', en: 'Role Details' }),
            employmentEndDate: translation({
                et: 'Töösuhte lõpp',
                en: 'Employment End Date',
            }),
            leavingFile: translation({
                et: 'Lahkumisavalduse link',
                en: 'Resignation letter link',
            }),
            turnoverImpact: translation({ et: 'Lahkumismõju', en: 'Turnover Impact' }),
            turnoverImpactLevels: {
                regrettable: translation({ et: 'Kahetsusväärne', en: 'Regrettable' }),
                not_regrettable: translation({
                    et: 'Ei ole kahetsusväärne',
                    en: 'Not regrettable',
                }),
                cannot_assess: translation({ et: 'Ei saa hinnata', en: 'Cannot assess' }),
            },
            leavingReason: translation({ et: 'Lahkumise põhjus', en: 'Leaving reason' }),
            leavingReasons: {
                own_wish: translation({ et: 'Omal soovil', en: 'Voluntary' }),
                extraordinary_termination: translation({
                    et: 'Lepingu erakorraline ülesütlemine',
                    en: 'Extraordinary termination',
                }),
                redundancy: translation({ et: 'Koondamine', en: 'Redundancy' }),
                fixed_term_end: translation({
                    et: 'Tähtajalise lepingu lõpp',
                    en: 'End of fixed-term contract',
                }),
                other: translation({ et: 'Muu', en: 'Other' }),
            },
            offboardingSection: translation({ et: 'Lahkumine', en: 'Offboarding' }),
            // Shown once leaving details exist (the post-offboard state).
            offboardingDataSection: translation({
                et: 'Lahkumisandmed',
                en: 'Offboarding details',
            }),
            terminationDate: translation({
                et: 'Töölepingu lõpetamise kuupäev',
                en: 'Contract termination date',
            }),
            employmentStatus: translation({ et: 'Staatus', en: 'Status' }),
            offboardAction: translation({ et: 'Lõpeta töösuhe', en: 'Offboard' }),
            reactivateAction: translation({ et: 'Taasta töösuhe', en: 'Reactivate' }),
            reactivateConfirm: translation({
                et: 'Kas oled kindel, et soovid selle töötaja töösuhte taastada?',
                en: 'Are you sure you want to reactivate this employee?',
            }),
            // Personal fields
            personalInfo: translation({ et: 'Isiklikud andmed', en: 'Personal Info' }),
            homeAddress: translation({ et: 'Kodune aadress', en: 'Home Address' }),
            personalEmail: translation({ et: 'Isiklik e-post', en: 'Personal Email' }),
            iban: translation({ et: 'IBAN', en: 'IBAN' }),
            copyIban: translation({ et: 'Kopeeri IBAN', en: 'Copy IBAN' }),
            ibanInvalid: translation({
                et: 'Kontrolli IBAN-i: riigikood, pikkus ja kontrollnumbrid ei klapi',
                en: 'Check the IBAN: country code, length or check digits do not match',
            }),
            swiftInvalid: translation({
                et: 'SWIFT/BIC on 8 või 11 märki, nt HABAEE2X',
                en: 'A SWIFT/BIC is 8 or 11 characters, e.g. HABAEE2X',
            }),
            swift: translation({ et: 'SWIFT', en: 'SWIFT' }),
            bankName: translation({ et: 'Panga nimi', en: 'Bank name' }),
            bankAccountHolder: translation({ et: 'Konto omanik', en: 'Account Holder' }),
            emergencyContacts: (n: number) =>
                translation({
                    et: `Hädaabikontaktid (${n})`,
                    en: `Emergency Contacts (${n})`,
                }),
            noContacts: translation({ et: 'Kontaktid puuduvad', en: 'No contacts' }),

            notFound: translation({ et: 'Töötajat ei leitud', en: 'Employee not found' }),
            // About section (Overview tab, Public)
            aboutSection: translation({ et: 'Lisainfo', en: 'About' }),
            nationalities: translation({ et: 'Kodakondsus(ed)', en: 'Nationalities' }),
            spokenLanguages: translation({ et: 'Suhtluskeel(ed)', en: 'Spoken Languages' }),
            funFacts: translation({ et: 'Huvitavad faktid', en: 'Fun Facts' }),
            // Health & Dietary section (Overview tab, Public)
            healthDietarySection: translation({
                et: 'Tervis ja toitumine',
                en: 'Health & Dietary',
            }),
            dietaryPreference: translation({ et: 'Dieedivalik', en: 'Dietary Preference' }),
            allergies: translation({ et: 'Allergiad', en: 'Allergies' }),
            preferNotToEat: translation({ et: 'Ei soovi süüa', en: 'Prefer Not to Eat' }),
            // Custom fields section (profile)
            customFieldsSection: translation({ et: 'Lisaväljad', en: 'Custom Fields' }),
            customFieldsSaved: translation({
                et: 'Lisaväljad salvestatud!',
                en: 'Custom fields saved!',
            }),
            // Kogemused section (TASK-300)
            experiencesSection: translation({ et: 'Kogemused', en: 'Experiences' }),
            addEntry: translation({ et: 'Lisa', en: 'Add' }),
            noEntries: translation({ et: 'Kirjed puuduvad', en: 'No entries' }),
            entryPresent: translation({ et: 'praeguseni', en: 'present' }),
            deleteEntry: translation({ et: 'Kustuta kirje', en: 'Delete entry' }),
            editEntry: translation({ et: 'Muuda', en: 'Edit' }),
            confirmDeleteEntry: translation({
                et: 'Kas oled kindel, et soovid selle kirje kustutada?',
                en: 'Are you sure you want to delete this entry?',
            }),
            periodStartMonth: translation({ et: 'Algus (kuu)', en: 'Start (month)' }),
            periodEndMonth: translation({ et: 'Lõpp (kuu)', en: 'End (month)' }),
            entryExtraInfo: translation({ et: 'Lisainfo', en: 'Additional info' }),
            periodEndBeforeStart: translation({
                et: 'Lõpp ei saa olla enne algust',
                en: 'End cannot be before start',
            }),
            editContact: translation({ et: 'Muuda kontakti', en: 'Edit contact' }),
            doneEditing: translation({ et: 'Valmis', en: 'Done' }),
            // Gender (Personal tab, Private)
            gender: translation({ et: 'Sugu', en: 'Gender' }),
            birthDate: translation({ et: 'Sünnikuupäev', en: 'Date of Birth' }),
            personalIdCode: translation({ et: 'Isikukood', en: 'Personal ID Code' }),
            // Edit mode
            editProfile: translation({ et: 'Muuda', en: 'Edit' }),
            saveProfile: translation({ et: 'Salvesta', en: 'Save' }),
            savingProfile: translation({ et: 'Salvestan...', en: 'Saving...' }),
            cancelEdit: translation({ et: 'Tühista', en: 'Cancel' }),
            profileUpdated: translation({
                et: 'Profiil uuendatud!',
                en: 'Profile updated!',
            }),
            profileUpdateError: translation({
                et: 'Profiili uuendamine ebaõnnestus',
                en: 'Failed to update profile',
            }),
            // Emergency contacts
            addContact: translation({ et: 'Lisa kontakt', en: 'Add contact' }),
            contactName: translation({ et: 'Nimi', en: 'Name' }),
            contactRelationship: translation({ et: 'Suhe', en: 'Relationship' }),
            contactPhone: translation({ et: 'Telefon', en: 'Phone' }),
            deleteContact: translation({ et: 'Kustuta kontakt', en: 'Delete contact' }),
            confirmDeleteContact: translation({
                et: 'Kas oled kindel, et soovid selle kontakti kustutada?',
                en: 'Are you sure you want to delete this contact?',
            }),
            // Shared form buttons
            saveChanges: translation({ et: 'Salvesta', en: 'Save' }),
            // Documents link (admin/manager, per-employee)
            documentsLink: translation({ et: 'Dokumentide link', en: 'Documents Link' }),
            googleDriveUrl: translation({ et: 'Google Drive URL', en: 'Google Drive URL' }),
            save: translation({ et: 'Salvesta', en: 'Save' }),
            // Document folders (multiple, per-employee)
            documentFolders: translation({ et: 'Dokumentide kaustad', en: 'Document folders' }),
            addFolder: translation({ et: 'Lisa kaust', en: 'Add folder' }),
            folderName: translation({ et: 'Kausta nimi', en: 'Folder name' }),
            driveFolderUrl: translation({
                et: 'Google Drive kausta URL',
                en: 'Google Drive folder URL',
            }),
            folderCategory: translation({ et: 'Kategooria', en: 'Category' }),
            editFolder: translation({ et: 'Muuda', en: 'Edit' }),
            deleteFolder: translation({ et: 'Kustuta', en: 'Delete' }),
            noFolders: translation({
                et: 'Kaustu pole veel lisatud.',
                en: 'No folders added yet.',
            }),
            confirmDeleteFolder: translation({
                et: 'Kas oled kindel, et soovid selle kausta kustutada?',
                en: 'Are you sure you want to delete this folder?',
            }),
            // Time away
            bookTimeAway: translation({ et: 'Puhkan/olen eemal', en: 'Book time away' }),
            addTimeAway: translation({ et: 'Lisa eemalolek', en: 'Add time away' }),
            // Gender display labels (stored values stay Mees/Naine/Muu)
            genderMale: translation({ et: 'Mees', en: 'Male' }),
            genderFemale: translation({ et: 'Naine', en: 'Female' }),
            genderOther: translation({ et: 'Muu', en: 'Other' }),
            avatarUploadFailed: translation({
                et: 'Pildi üleslaadimine ebaõnnestus',
                en: 'Upload failed',
            }),
        },
        // Status labels
        status: {
            active: translation({ et: 'Aktiivne', en: 'Active' }),
            inactive: translation({ et: 'Mitteaktiivne', en: 'Inactive' }),
            onLeave: translation({ et: 'Puhkusel', en: 'On Leave' }),
        },
        // Dashboard
        dashboard: {
            greeting: (name: string) => translation({ et: `Tere, ${name}!`, en: `Hi, ${name}!` }),
            addEmployee: translation({ et: 'Lisa töötaja', en: 'Add Employee' }),
            actionsLabel: translation({ et: 'Toimingud', en: 'Actions' }),
            filterAllTeams: translation({ et: 'Kõik meeskonnad', en: 'All Teams' }),
            filterNoTeam: translation({ et: 'Meeskonnata', en: 'No team' }),
            mandatory: {
                heading: translation({ et: 'Esile toodud', en: 'Featured' }),
            },
            stats: {
                headcount: translation({ et: 'Töötajaid kokku', en: 'Total Employees' }),
                headcountChange: (n: number) =>
                    translation({
                        et: n >= 0 ? `+${n} sel kuul` : `${n} sel kuul`,
                        en: n >= 0 ? `+${n} this month` : `${n} this month`,
                    }),
                absences: translation({ et: 'Eemalviibimisi täna', en: 'Absences Today' }),
                absencesNone: translation({ et: 'Kõik kohal', en: 'All present' }),
                absencesBreakdown: (vacation: number, sick: number, other: number) =>
                    translation({
                        et: `${vacation} puhkusel · ${sick} haiguslehel · ${other} muu`,
                        en: `${vacation} on leave · ${sick} sick · ${other} other`,
                    }),
                activeLeaves: translation({ et: 'Aktiivsed puhkused', en: 'Active Leaves' }),
                activeLeavesNone: translation({
                    et: 'Hetkel puhkuseid ei ole',
                    en: 'No active leaves',
                }),
                pendingApprovals: (n: number) =>
                    translation({
                        et: `${n} ootel kinnitust`,
                        en: `${n} pending approval${n === 1 ? '' : 's'}`,
                    }),
                avgFte: translation({ et: 'Keskmine koormus', en: 'Average FTE' }),
                avgFteLabel: translation({ et: 'Täiskoormusega', en: 'Full-time equivalent' }),
                totalFteResource: (fte: number) =>
                    translation({
                        et: `Koguressurss: ${fte.toFixed(1)} FTE`,
                        en: `Total resource: ${fte.toFixed(1)} FTE`,
                    }),
            },
            leaveBalance: {
                title: translation({ et: 'Puhkusejääk', en: 'Leave Balance' }),
                used: translation({ et: 'Kasutatud', en: 'Used' }),
                available: translation({ et: 'Saadaval', en: 'Available' }),
                healthDays: translation({ et: 'Tervisepäevad', en: 'Health Days' }),
                request: translation({ et: 'Taotle puhkust', en: 'Request Time Off' }),
                notConfigured: translation({
                    et: 'Puhkuseandmed pole veel seadistatud',
                    en: 'Leave data is not yet configured',
                }),
                days: translation({ et: 'päeva', en: 'days' }),
                nextLeave: (range: string) =>
                    translation({ et: `Järgmine: ${range}`, en: `Next: ${range}` }),
            },
            tasks: {
                title: translation({ et: 'Ülesanded', en: 'Tasks' }),
                empty: translation({ et: 'Ülesandeid ei ole.', en: 'No tasks.' }),
                notConfigured: translation({
                    et: 'Notioni ülesannete andmebaas pole seadistatud.',
                    en: 'Notion tasks are not set up.',
                }),
                noAccount: translation({
                    et: 'Sinu e-postiga Notioni kasutajat ei leitud.',
                    en: 'No Notion user matches your email.',
                }),
                error: translation({
                    et: 'Notioni ühendus ebaõnnestus.',
                    en: 'Could not reach Notion.',
                }),
                otherGroup: translation({ et: 'Muu', en: 'Other' }),
                untitled: translation({ et: '(pealkirjata)', en: '(untitled)' }),
                colTask: translation({ et: 'Ülesanne', en: 'Task' }),
                colStatus: translation({ et: 'Olek', en: 'Status' }),
                colDue: translation({ et: 'Tähtaeg', en: 'Due' }),
                colPriority: translation({ et: 'Prioriteet', en: 'Priority' }),
            },
            absences: {
                todayTitle: translation({ et: 'Täna eemal', en: 'Absent Today' }),
                upcomingTitle: translation({
                    et: 'Eelseisvad eemalolekud',
                    en: 'Upcoming Absences',
                }),
                emptyToday: translation({ et: 'Kõik on kohal!', en: 'Everyone is present!' }),
                emptyUpcoming: translation({
                    et: 'Lähiajal eemalolekuid pole',
                    en: 'No upcoming absences',
                }),
                openCalendar: translation({ et: 'Ava kalender', en: 'Open calendar' }),
                until: (date: Date) => {
                    const etMonths = [
                        'jaan',
                        'veebr',
                        'märts',
                        'apr',
                        'mai',
                        'juuni',
                        'juuli',
                        'aug',
                        'sept',
                        'okt',
                        'nov',
                        'dets',
                    ]
                    const enMonths = [
                        'Jan',
                        'Feb',
                        'Mar',
                        'Apr',
                        'May',
                        'Jun',
                        'Jul',
                        'Aug',
                        'Sep',
                        'Oct',
                        'Nov',
                        'Dec',
                    ]
                    return translation({
                        et: `Kuni ${date.getDate()}. ${etMonths[date.getMonth()]}`,
                        en: `Until ${enMonths[date.getMonth()]} ${date.getDate()}`,
                    })
                },
                from: (date: Date) => {
                    const etMonths = [
                        'jaan',
                        'veebr',
                        'märts',
                        'apr',
                        'mai',
                        'juuni',
                        'juuli',
                        'aug',
                        'sept',
                        'okt',
                        'nov',
                        'dets',
                    ]
                    const enMonths = [
                        'Jan',
                        'Feb',
                        'Mar',
                        'Apr',
                        'May',
                        'Jun',
                        'Jul',
                        'Aug',
                        'Sep',
                        'Oct',
                        'Nov',
                        'Dec',
                    ]
                    return translation({
                        et: `Alates ${date.getDate()}. ${etMonths[date.getMonth()]}`,
                        en: `From ${enMonths[date.getMonth()]} ${date.getDate()}`,
                    })
                },
                daysCount: (days: number) =>
                    translation({
                        et: `${days} päev${days === 1 ? '' : 'a'}`,
                        en: `${days} day${days === 1 ? '' : 's'}`,
                    }),
                moreCount: (n: number) =>
                    translation({
                        et: `+${n} veel`,
                        en: `+${n} more`,
                    }),
            },
            notifications: {
                title: translation({ et: 'Teavitused', en: 'Notifications' }),
                markRead: translation({ et: 'Märgi loetuks', en: 'Mark all read' }),
                empty: translation({ et: 'Teavitusi pole', en: 'No notifications' }),
            },
            cards: {
                headcount: {
                    title: translation({ et: 'Töötajate arv', en: 'Headcount' }),
                    description: translation({
                        et: 'Töötajate arv, liitujad/lahkujad, voolavus ja trend.',
                        en: 'Headcount, joiners/leavers, turnover and trend.',
                    }),
                    trend: (delta: number) =>
                        translation({
                            et: `${delta >= 0 ? '+' : ''}${delta} sel kuul`,
                            en: `${delta >= 0 ? '+' : ''}${delta} this month`,
                        }),
                    joiners: (n: number) => translation({ et: `+${n} liitus`, en: `+${n} joined` }),
                    leavers: (n: number) => translation({ et: `−${n} lahkus`, en: `−${n} left` }),
                    turnover: (pct: number) =>
                        translation({ et: `Voolavus ${pct}%`, en: `Turnover ${pct}%` }),
                    scopeNope: translation({ et: 'N', en: 'N' }),
                    scopePlay: translation({ et: 'P', en: 'P' }),
                    scopeNp: translation({ et: 'N+P', en: 'N+P' }),
                    scopeTotal: translation({ et: 'Kokku', en: 'Total' }),
                    period6: translation({ et: '6 kuud', en: '6 mo' }),
                    period12: translation({ et: '12 kuud', en: '12 mo' }),
                },
                leave_balance: {
                    title: translation({ et: 'Minu puhkus ja eemalolekud', en: 'My time off' }),
                    description: translation({
                        et: 'Sinu põhipuhkus: praegune, järgmine, jääk ja tänane saldo.',
                        en: 'Your main vacation: current, next, remaining and balance as of today.',
                    }),
                    currentlyAway: translation({ et: 'Praegu eemal', en: 'Currently away' }),
                    nextLeave: translation({ et: 'Järgmine eemalolek', en: 'Next time off' }),
                    noUpcoming: translation({ et: 'Eelseisvaid pole', en: 'None upcoming' }),
                    remaining: translation({
                        et: 'Saadaval kuni 31. detsembrini',
                        en: 'Available until 31 December',
                    }),
                    balanceAsOfToday: translation({
                        et: 'Saldo tänase seisuga',
                        en: 'Balance as of today',
                    }),
                    days: (n: number) => translation({ et: `${n} päeva`, en: `${n} days` }),
                    // Special-day balances: a "left/max" fraction, plus a used/not-used
                    // state for the one-shot Töövaba nädal. Health days show only the
                    // remaining count under a dedicated label.
                    specialFraction: (left: number, total: number) =>
                        translation({ et: `${left}/${total}`, en: `${left}/${total}` }),
                    healthDaysAvailable: translation({
                        et: 'Tervisepäevi saadaval',
                        en: 'Health days available',
                    }),
                    specialUsed: translation({ et: 'Kasutatud', en: 'Used' }),
                    specialUnused: translation({ et: 'Kasutamata', en: 'Not used' }),
                    viewLink: translation({ et: 'Minu eemalolekud', en: 'My time off' }),
                    addLeave: translation({ et: 'Puhkan/olen eemal', en: 'Add leave' }),
                },
                my_tasks: {
                    title: translation({ et: 'Notion taskid', en: 'Notion tasks' }),
                    description: translation({
                        et: 'Sinu Notioni ülesanded.',
                        en: 'Your Notion tasks.',
                    }),
                    notConfigured: translation({
                        et: 'Notioni ülesannete andmebaas pole seadistatud.',
                        en: 'Notion tasks are not set up.',
                    }),
                    noAccount: translation({
                        et: 'Sinu e-postiga Notioni kasutajat ei leitud.',
                        en: 'No Notion user matches your email.',
                    }),
                    addToken: translation({ et: 'Seadista', en: 'Set up' }),
                    empty: translation({ et: 'Ülesandeid pole', en: 'No tasks' }),
                    error: translation({
                        et: 'Notioni ühendus ebaõnnestus.',
                        en: 'Could not reach Notion.',
                    }),
                    otherGroup: translation({ et: 'Muu', en: 'Other' }),
                    untitled: translation({ et: '(pealkirjata)', en: '(untitled)' }),
                    viewAll: translation({ et: 'Vaata kõiki', en: 'View all' }),
                },
                balancetime: {
                    title: translation({ et: 'Minu tööaeg', en: 'My work time' }),
                    description: translation({
                        et: 'Tehtud vs oodatud tunnid — kuu või kvartal.',
                        en: 'Tracked vs expected hours — month or quarter.',
                    }),
                    tracked: translation({ et: 'Tehtud', en: 'Tracked' }),
                    expected: translation({ et: 'Oodatud', en: 'Expected' }),
                    over: translation({ et: 'üle normi', en: 'over' }),
                    under: translation({ et: 'alla normi', en: 'under' }),
                    periodMonth: translation({ et: 'Kuu', en: 'Month' }),
                    periodQuarter: translation({ et: 'Kvartal', en: 'Quarter' }),
                    // Period dropdown labels carry the live period (the old gray
                    // subtitle under the bar is gone), e.g. "Käesolev kuu - Juuni".
                    periodMonthCurrent: (month: string) =>
                        translation({
                            et: `Käesolev kuu - ${month}`,
                            en: `Current month - ${month}`,
                        }),
                    periodQuarterCurrent: (months: string) =>
                        translation({
                            et: `Käesolev kvartal - ${months}`,
                            en: `Current quarter - ${months}`,
                        }),
                    unavailable: translation({
                        et: 'Ühenda Harvest, et näha saldot',
                        en: 'Connect Harvest to see your balance',
                    }),
                    // Progress-bar marker: where you should be by today (vs the full period).
                    expectedToday: (h: number) =>
                        translation({ et: `Eeldatav täna: ${h}h`, en: `Expected by today: ${h}h` }),
                    paceAhead: (h: number) =>
                        translation({ et: `${h}h ees (ületöö)`, en: `${h}h ahead (overtime)` }),
                    paceBehind: (h: number) =>
                        translation({ et: `${h}h graafikust maas`, en: `${h}h behind` }),
                    paceOnTrack: translation({ et: 'graafikus', en: 'on track' }),
                    // Tooltip on the TÖÖAJA SALDO stat.
                    saldoOver: translation({
                        et: 'Oled märkinud rohkem kui on oodatud.',
                        en: "You've tracked more than expected.",
                    }),
                    saldoUnder: translation({
                        et: 'Oled märkinud vähem kui on oodatud.',
                        en: "You've tracked less than expected.",
                    }),
                    viewLink: translation({ et: 'Ava Minu tööaeg', en: 'Open my work time' }),
                },
                next_joiner_leaver: {
                    title: translation({ et: 'Liitujad ja lahkujad', en: 'Joiners & leavers' }),
                    description: translation({
                        et: 'Järgmine uus töötaja ja järgmine lahkuja.',
                        en: 'The next new hire and the next departure.',
                    }),
                    nextJoinerLabel: translation({ et: 'Liitub', en: 'Joining' }),
                    nextLeaverLabel: translation({ et: 'Lahkub', en: 'Leaving' }),
                    none: translation({ et: '—', en: '—' }),
                },
                turnover: {
                    title: translation({ et: 'Tööjõu voolavus', en: 'Turnover' }),
                    description: translation({
                        et: 'Lahkujate osakaal viimase 12 kuu jooksul.',
                        en: 'Share of employees who left in the past 12 months.',
                    }),
                    subtext: (count: number) =>
                        translation({
                            et: `${count} lahkujat · viimased 12 kuud`,
                            en: `${count} leavers · last 12 mo`,
                        }),
                },
                my_tenure: {
                    title: translation({ et: 'Minu tööstaaž', en: 'My tenure' }),
                    description: translation({
                        et: 'Sinu tööstaaž ja teenitud märgid.',
                        en: 'Your tenure and earned badges.',
                    }),
                    value: (years: number, months: number) =>
                        translation({
                            et: `${years}a ${months}k`,
                            en: `${years}y ${months}m`,
                        }),
                    badges: (count: number) =>
                        translation({
                            et: `${count} märki`,
                            en: `${count} badges`,
                        }),
                    empty: translation({ et: '—', en: '—' }),
                },
                tenure: {
                    title: translation({
                        et: 'Töötajate keskmine tööstaaž',
                        en: 'Average tenure',
                    }),
                    description: translation({
                        et: 'Töölepinguga töötajate keskmine tööstaaž ettevõtte kaupa.',
                        en: 'Average tenure of contracted employees by company.',
                    }),
                    value: (years: number, months: number) =>
                        translation({
                            et: `${years}a ${months}k`,
                            en: `${years}y ${months}m`,
                        }),
                    total: translation({ et: 'Kokku', en: 'Total' }),
                    nope: translation({ et: 'NOPE', en: 'NOPE' }),
                    play: translation({ et: 'Play', en: 'Play' }),
                    both: translation({ et: 'N&P', en: 'N&P' }),
                    empty: translation({ et: '—', en: '—' }),
                    byCompany: translation({ et: 'Ettevõtte kaupa', en: 'By company' }),
                    byTeam: translation({ et: 'Tiimi kaupa', en: 'By team' }),
                },
                team_workload: {
                    title: translation({ et: 'Tiimi tööplaan', en: 'Team work plan' }),
                    description: translation({
                        et: 'Tiimi kohalolek täna, praegused ja eelseisvad eemalolekud.',
                        en: 'Team availability today, current and upcoming absences.',
                    }),
                    available: (avail: number, total: number) =>
                        translation({
                            et: `${avail} / ${total} kohal`,
                            en: `${avail} of ${total} available`,
                        }),
                    summedFte: (value: number) =>
                        translation({
                            et: `Tiimi FTE kokku: ${value}`,
                            en: `Team FTE total: ${value}`,
                        }),
                    currentHeading: translation({ et: 'Praegu eemal', en: 'Currently away' }),
                    upcomingHeading: translation({
                        et: 'Eelseisvad (3 nädalat)',
                        en: 'Upcoming (3 weeks)',
                    }),
                    currentEmpty: translation({ et: 'Keegi pole eemal', en: 'Nobody away' }),
                    upcomingEmpty: translation({ et: 'Eelseisvaid pole', en: 'None upcoming' }),
                    daysCount: (days: number) => translation({ et: `${days}p`, en: `${days}d` }),
                    empty: translation({ et: '—', en: '—' }),
                    noTeam: translation({ et: 'Tiim määramata', en: 'No team assigned' }),
                    viewLink: translation({ et: 'Eemalolekud', en: 'Time off' }),
                },
                team_norm: {
                    title: translation({ et: 'Tiimi tööaja täituvus', en: 'Team norm fulfilment' }),
                    description: translation({
                        et: 'Tiimiliikmete märgitud vs oodatud tunnid jooksval kuul.',
                        en: "Team members' tracked vs expected hours this month.",
                    }),
                    fte: (value: string) =>
                        translation({ et: `${value} koormus`, en: `${value} FTE` }),
                    hoursOfExpected: (tracked: string, expected: string) =>
                        translation({
                            et: `${tracked} / ${expected}`,
                            en: `${tracked} / ${expected}`,
                        }),
                    notConnected: translation({
                        et: 'Harvest ühendamata',
                        en: 'Harvest not connected',
                    }),
                    noTeam: translation({ et: 'Tiim määramata', en: 'No team assigned' }),
                    empty: translation({ et: 'Tiimis pole teisi liikmeid', en: 'No teammates' }),
                    unavailable: translation({
                        et: 'Ühenda Harvest, et näha tiimi tööaega',
                        en: "Connect Harvest to see the team's hours",
                    }),
                    truncated: (n: number) =>
                        translation({ et: `+ ${n} liiget peidetud`, en: `+ ${n} more hidden` }),
                },
                worktime: {
                    title: translation({ et: 'Minu projektid', en: 'My projects' }),
                    description: translation({
                        et: 'Sinu projektid ja tööaja ülevaade.',
                        en: 'Your projects and time overview.',
                    }),
                    label: translation({ et: 'Projektid', en: 'Projects' }),
                    recentLabel: translation({ et: 'Viimased', en: 'Latest' }),
                    viewLink: translation({ et: 'Vaata projekte', en: 'View projects' }),
                    empty: translation({ et: 'Sisestatud projekte ei ole', en: 'No projects yet' }),
                },
                eat_debt: {
                    title: translation({
                        et: 'Minu köögikata tellimused',
                        en: 'My Köögikata orders',
                    }),
                    description: translation({
                        et: 'Maksmata gotoAndEat tellimused.',
                        en: 'Unpaid gotoAndEat orders.',
                    }),
                    amount: (total: number) =>
                        translation({ et: `${total.toFixed(2)} €`, en: `€${total.toFixed(2)}` }),
                    summary: (count: number, days: number | null) =>
                        days === null
                            ? translation({
                                  et: `${count} tellimust · vanim teadmata`,
                                  en: `${count} orders · oldest unknown`,
                              })
                            : translation({
                                  et: `${count} tellimust · ${days} päeva maksmata`,
                                  en: `${count} orders · ${days} days unpaid`,
                              }),
                    empty: translation({ et: 'Tellimusi ei ole', en: 'No orders' }),
                    comingSoon: translation({ et: 'Tuleb peatselt', en: 'Coming soon' }),
                },
                next_internal_event: {
                    title: translation({ et: 'Järgmine siseüritus', en: 'Next internal event' }),
                    description: translation({
                        et: 'Lähim sisemine üritus (kuni 7 päeva).',
                        en: 'The next internal event (within 7 days).',
                    }),
                    empty: translation({ et: 'Lähiajal üritusi pole', en: 'No upcoming events' }),
                },
                next_event: {
                    title: translation({ et: 'Järgmine üritus', en: 'Next event' }),
                    description: translation({
                        et: 'Järgmine tiimiüritus — nimi ja aeg.',
                        en: 'The next team event — name and time.',
                    }),
                    empty: translation({ et: 'Tulevasi üritusi ei ole', en: 'No upcoming events' }),
                    viewLink: translation({ et: 'Vaata kõiki üritusi', en: 'View all events' }),
                },
                leaderboard: {
                    title: translation({ et: 'Saavutuste edetabel', en: 'Achievements' }),
                    description: translation({
                        et: 'Sinu koht edetabelis ja viimane badge.',
                        en: 'Your leaderboard position and latest badge.',
                    }),
                    rankingsHeading: translation({ et: 'Edetabel', en: 'Leaderboard' }),
                    points: (n: number) => translation({ et: `${n} punkti`, en: `${n} pts` }),
                    lastBadgeLabel: translation({
                        et: 'Viimane lunastatud märk',
                        en: 'Last redeemed badge',
                    }),
                    viewLink: translation({ et: 'Vaata kõiki', en: 'View all' }),
                },
                next_7_days_events: {
                    title: translation({
                        et: 'Eelseisvad sündmused',
                        en: 'Upcoming events',
                    }),
                    description: translation({
                        et: 'Sünnipäevad, tööaastapäevad, liitumised ja lahkumised — 5 lähimat või järgmise 7 päeva jooksul.',
                        en: 'Birthdays, anniversaries, joiners and leavers — the 5 soonest or the next 7 days.',
                    }),
                    today: translation({ et: 'Täna', en: 'Today' }),
                    tomorrow: translation({ et: 'Homme', en: 'Tomorrow' }),
                    inDays: (n: number) =>
                        translation({
                            et: `${n} päeva`,
                            en: `In ${n} days`,
                        }),
                    typeBirthday: translation({ et: 'Sünnipäev', en: 'Birthday' }),
                    typeAnniversary: translation({ et: 'Tööaastapäev', en: 'Work anniversary' }),
                    typeAnniversaryYears: (n: number) =>
                        translation({ et: `${n}. tööaastapäev`, en: `${n}-year anniversary` }),
                    typeNewJoiner: translation({ et: 'Uus liituja', en: 'New joiner' }),
                    typeOffboarding: translation({ et: 'Lahkub', en: 'Leaving' }),
                    empty: translation({
                        et: 'Eelseisvaid sündmusi pole.',
                        en: 'No upcoming events.',
                    }),
                },
                life360: {
                    title: translation({ et: 'Katamobiil', en: 'Katamobile' }),
                    description: translation({
                        et: 'Kata lõunasaadetis kaardil — aku ja viimati nähtud aeg.',
                        en: 'Tracking Kata, the lunch delivery — battery and last-seen time.',
                    }),
                    notConfigured: translation({
                        et: 'Life360 pole seadistatud',
                        en: 'Life360 is not configured',
                    }),
                    error: translation({
                        et: 'Asukohti ei õnnestunud laadida.',
                        en: 'Could not load locations.',
                    }),
                    empty: translation({
                        et: 'Keegi ei jaga praegu asukohta.',
                        en: 'Nobody is sharing a location right now.',
                    }),
                    battery: translation({ et: 'Aku', en: 'Battery' }),
                    charging: translation({ et: 'laeb', en: 'charging' }),
                    office: translation({ et: 'Kontor', en: 'Office' }),
                    fromOffice: translation({ et: 'kontorist', en: 'from office' }),
                    updated: (time: string) =>
                        translation({ et: `Uuendatud ${time}`, en: `Updated ${time}` }),
                },
            },
            edit: {
                mobileNote: translation({
                    et: 'Muuda töölauda arvutis',
                    en: 'Edit your dashboard on desktop',
                }),
                customize: translation({ et: 'Kohanda vaadet', en: 'Customize view' }),
                save: translation({ et: 'Salvesta', en: 'Save' }),
                cancel: translation({ et: 'Tühista', en: 'Cancel' }),
                saveFailed: translation({ et: 'Salvestamine ebaõnnestus.', en: 'Save failed.' }),
                addCard: translation({ et: 'Lisa kaart', en: 'Add card' }),
                addSmallCard: translation({ et: 'Lisa väike kaart', en: 'Add small card' }),
                addBigCard: translation({ et: 'Lisa suur kaart', en: 'Add big card' }),
                pickerEmpty: translation({
                    et: 'Kõik saadaolevad kaardid on juba sinu töölaual.',
                    en: 'All available cards are already on your dashboard.',
                }),
                smallCards: translation({ et: 'Väikesed kaardid', en: 'Small cards' }),
                bigCards: translation({ et: 'Suured kaardid', en: 'Big cards' }),
                cardError: translation({
                    et: 'Laadimine ebaõnnestus.',
                    en: 'Failed to load.',
                }),
                dragToReorder: translation({
                    et: 'Lohista ümber järjestamiseks',
                    en: 'Drag to reorder',
                }),
                removeCard: translation({ et: 'Eemalda kaart', en: 'Remove card' }),
                closePicker: translation({ et: 'Sulge valijaaken', en: 'Close picker' }),
                moreOptions: translation({ et: 'Rohkem valikuid', en: 'More options' }),
                width: translation({ et: 'Laius', en: 'Width' }),
                height: translation({ et: 'Kõrgus', en: 'Height' }),
                widthThird: translation({ et: 'Kolmandik', en: 'One third' }),
                widthHalf: translation({ et: 'Pool', en: 'Half' }),
                widthTwoThirds: translation({ et: 'Kaks kolmandikku', en: 'Two thirds' }),
                widthFull: translation({ et: 'Täislaius', en: 'Full width' }),
                heightShort: translation({ et: 'Madal', en: 'Short' }),
                heightMedium: translation({ et: 'Keskmine', en: 'Medium' }),
                heightTall: translation({ et: 'Kõrge', en: 'Tall' }),
            },
        },
        // M3: Time Away
        timeOff: {
            tabLabel: translation({ et: 'Eemalolekud', en: 'Time Away' }),
            calendarTitle: translation({ et: 'Kes on eemal', en: "Who's away" }),
            legendPublicHoliday: translation({ et: 'Riigipüha', en: 'Public holiday' }),
            viewBtn: translation({ et: 'Vaate seaded', en: 'View settings' }),
            viewCapacity: translation({ et: 'Kuva töökoormuse info', en: 'Show workload info' }),
            viewFlex: translation({ et: 'Kuva töörütmi info', en: 'Show work rhythm info' }),
            capacityFlex: translation({ et: 'FLEX', en: 'FLEX' }),
            bookTimeAway: translation({ et: 'Puhkan/olen eemal', en: 'Book time away' }),
            bookTimeAwayFor: (name: string) =>
                translation({ et: `Lisa eemalolek — ${name}`, en: `Add time away — ${name}` }),
            editTimeAwayTitle: translation({ et: 'Muuda eemalolekut', en: 'Edit time away' }),
            employeeProfileMissing: translation({
                et: 'Töötaja profiil puudub. Palun proovige uuesti.',
                en: 'Employee profile not found. Please try again.',
            }),
            requestTitle: translation({ et: 'Planeeri eemalolek', en: 'Plan Time Off' }),
            myBalance: translation({ et: 'Minu jääk', en: 'My Balance' }),
            startDate: translation({ et: 'Algus', en: 'Start Date' }),
            endDate: translation({ et: 'Lõpp', en: 'End Date' }),
            note: translation({ et: 'Märkus', en: 'Note' }),
            notePlaceholder: translation({
                et: 'Lisa märkus (valikuline)...',
                en: 'Add a note (optional)...',
            }),
            policy: translation({ et: 'Poliitika', en: 'Policy' }),
            period: translation({ et: 'Periood', en: 'Period' }),
            periodPlaceholder: translation({ et: 'Vali kuupäevad', en: 'Select dates' }),
            reason: translation({ et: 'Põhjus', en: 'Reason' }),
            typesLoadFailed: translation({
                et: 'Eemalolekutüüpide laadimine ebaõnnestus.',
                en: 'Failed to load leave types.',
            }),
            retry: translation({ et: 'Proovi uuesti', en: 'Try again' }),
            requestingDays: (n: number) =>
                translation({ et: `Taotled ${n} päeva`, en: `Requesting ${n} days` }),
            daysLeft: (n: number) =>
                translation({ et: `jääb ${n} päeva`, en: `you'll have ${n} days left` }),
            alsoAway: (n: number) =>
                translation({
                    et: `${n} ${n === 1 ? 'inimene' : 'inimest'} on samuti eemal`,
                    en: `${n} ${n === 1 ? 'person is' : 'people are'} also away`,
                }),
            selfOverlapWarning: translation({
                et: 'Sul on sellel perioodil juba eemalolek planeeritud:',
                en: 'You already have time away during this period:',
            }),
            requestTimeAway: translation({ et: 'Esita taotlus', en: 'Request time away' }),
            close: translation({ et: 'Sulge', en: 'Close' }),
            businessDays: (n: number) =>
                translation({
                    et: `${n} ${n === 1 ? 'päev' : 'päeva'}`,
                    en: `${n} ${n === 1 ? 'day' : 'days'}`,
                }),
            submit: translation({ et: 'Esita taotlus', en: 'Submit Request' }),
            submitting: translation({ et: 'Esitan...', en: 'Submitting...' }),
            cancel: translation({ et: 'Tühista', en: 'Cancel' }),
            edit: translation({ et: 'Muuda', en: 'Edit' }),
            editConfirm: translation({
                et: 'Salvesta muudatused',
                en: 'Save changes',
            }),
            editCancel: translation({ et: 'Loobu', en: 'Discard' }),
            requestEdited: translation({
                et: 'Eemalolek muudetud',
                en: 'Time-off request updated',
            }),
            cancelConfirm: translation({
                et: 'Kas soovid selle eemaloleku tühistada?',
                en: 'Cancel this time off request?',
            }),
            cancelRequestConfirm: translation({
                et: 'Kustutamissoov saadetakse sinu juhile kinnitamiseks. Kas jätkad?',
                en: 'Your deletion request will be sent to your manager for approval. Continue?',
            }),
            cancelRequestSent: translation({
                et: 'Kustutamissoov saadetud juhile kinnitamiseks',
                en: 'Deletion request sent for approval',
            }),
            requestCancelled: translation({
                et: 'Eemalolek tühistatud',
                en: 'Time off cancelled',
            }),
            approveCancellation: translation({
                et: 'Kinnita kustutamine',
                en: 'Approve deletion',
            }),
            rejectCancellation: translation({
                et: 'Jäta alles',
                en: 'Keep',
            }),
            editReapprovalConfirm: translation({
                et: 'Muudatus saadetakse sinu juhile uuesti kinnitamiseks. Kas jätkad?',
                en: 'Your change will be sent back to your manager for re-approval. Continue?',
            }),
            editReapprovalSubmit: translation({
                et: 'Saada kinnitamiseks',
                en: 'Send for approval',
            }),
            approve: translation({ et: 'Kinnita', en: 'Approve' }),
            reject: translation({ et: 'Keeldu', en: 'Reject' }),
            rejectionReason: translation({
                et: 'Keeldumise põhjus',
                en: 'Rejection reason',
            }),
            status: {
                pending: translation({ et: 'Ootel', en: 'Pending' }),
                approved: translation({ et: 'Kinnitatud', en: 'Approved' }),
                rejected: translation({ et: 'Keeldutud', en: 'Rejected' }),
                cancelled: translation({ et: 'Tühistatud', en: 'Cancelled' }),
                cancelRequested: translation({ et: 'Kustutamine ootel', en: 'Deletion pending' }),
            },
            balance: {
                type: translation({ et: 'Tüüp', en: 'Type' }),
                available: translation({ et: 'Saadaval', en: 'Available' }),
                used: translation({ et: 'Kasutatud', en: 'Used' }),
                pending: translation({ et: 'Ootel', en: 'Pending' }),
                total: translation({ et: 'Kokku', en: 'Total' }),
                carryover: translation({ et: 'Üleviimine', en: 'Carryover' }),
                days: translation({ et: 'päeva', en: 'days' }),
                remainingDec: translation({ et: 'Jääk 31. dets', en: 'Remaining (31 Dec)' }),
                unplanned: translation({ et: 'Planeerimata', en: 'Unplanned' }),
                negativeWarning: translation({
                    et: 'Saldo on miinuses',
                    en: 'Balance is negative',
                }),
                otherLeaveTypes: translation({
                    et: 'Muud eemalolekute tüübid',
                    en: 'Other leave types',
                }),
            },
            pendingApprovals: translation({
                et: 'Ootel kinnitamised',
                en: 'Pending Approvals',
            }),
            noPending: translation({
                et: 'Kinnitamata taotlusi pole',
                en: 'No pending requests',
            }),
            noRequests: translation({
                et: 'Eemalolekutaotlusi pole',
                en: 'No time-off requests',
            }),
            requestCreated: translation({
                et: 'Eemalolekutaotlus esitatud!',
                en: 'Time-off request submitted!',
            }),
            requestApproved: translation({
                et: 'Taotlus kinnitatud',
                en: 'Request approved',
            }),
            requestRejected: translation({
                et: 'Taotlus keeldutud',
                en: 'Request rejected',
            }),
            filter: {
                department: translation({ et: 'Osakond', en: 'Department' }),
                team: translation({ et: 'Tiim', en: 'Team' }),
                leaveType: translation({ et: 'Eemalolekute tüüp', en: 'Leave Type' }),
                allDepartments: translation({ et: 'Kõik osakonnad', en: 'All departments' }),
                allTeams: translation({ et: 'Kõik tiimid', en: 'All teams' }),
                allTypes: translation({ et: 'Kõik tüübid', en: 'All types' }),
                search: translation({ et: 'Otsi töötajat...', en: 'Search employee...' }),
                filters: translation({ et: 'Filtrid', en: 'Filters' }),
            },
            ledger: {
                title: translation({ et: 'Tehingute ajalugu', en: 'Transaction Ledger' }),
                type: translation({ et: 'Tüüp', en: 'Type' }),
                amount: translation({ et: 'Summa', en: 'Amount' }),
                date: translation({ et: 'Kuupäev', en: 'Date' }),
                noteLabel: translation({ et: 'Märkus', en: 'Note' }),
                initialBalance: translation({ et: 'Algsaldo', en: 'Initial Balance' }),
                accrual: translation({ et: 'Arvestus', en: 'Accrual' }),
                deduction: translation({ et: 'Mahaarvamine', en: 'Deduction' }),
                expiration: translation({ et: 'Aegumine', en: 'Expiration' }),
                manualAdjustment: translation({ et: 'Käsitsi parandus', en: 'Manual Adjustment' }),
                effectiveDate: translation({ et: 'Seisuga', en: 'As of' }),
                actor: translation({ et: 'Tegija', en: 'By' }),
            },
            holidays: {
                title: translation({ et: 'Riigipühad', en: 'Public Holidays' }),
                publicHolidayLabel: translation({
                    et: 'Riigipüha Eestis',
                    en: 'Public holiday in Estonia',
                }),
            },
            membersAway: (count: number) =>
                translation({
                    et: `${count} ${count === 1 ? 'liige' : 'liiget'} eemal`,
                    en: `${count} member${count === 1 ? '' : 's'} away`,
                }),
            upcomingTitle: translation({
                et: 'Praegused ja eelseisvad eemalolekud',
                en: 'Current & Upcoming Time Off',
            }),
            pastTitle: translation({ et: 'Varasemad eemalolekud', en: 'Past Time Off' }),
            // Humaans-style time-away cards (profile tab + /me/time-away)
            view: {
                availableUntil: (year: number) =>
                    translation({
                        et: `Saadaval kuni 31. dets ${year}`,
                        en: `Available until 31 Dec ${year}`,
                    }),
                freeToBook: translation({ et: 'Vaba broneerimiseks', en: 'Free to book' }),
                balanceAsOfToday: translation({
                    et: 'Saldo seisuga täna',
                    en: 'Balance as of today',
                }),
                // `date` is the first day of the accrual window — 1 Jan for the
                // standing workforce, the hire / policy effective date for a
                // mid-year joiner (TASK-348).
                accruedSince: (date: string) => {
                    const etMonths = [
                        'jaan',
                        'veebr',
                        'märts',
                        'apr',
                        'mai',
                        'juuni',
                        'juuli',
                        'aug',
                        'sept',
                        'okt',
                        'nov',
                        'dets',
                    ]
                    const enMonths = [
                        'Jan',
                        'Feb',
                        'Mar',
                        'Apr',
                        'May',
                        'Jun',
                        'Jul',
                        'Aug',
                        'Sep',
                        'Oct',
                        'Nov',
                        'Dec',
                    ]
                    const year = Number(date.slice(0, 4))
                    const month = Number(date.slice(5, 7))
                    const day = Number(date.slice(8, 10))
                    return translation({
                        et: `Kogunenud alates ${day}. ${etMonths[month - 1]} ${year}`,
                        en: `Accrued since ${day} ${enMonths[month - 1]} ${year}`,
                    })
                },
                totalAccrued: translation({ et: 'Kogunenud kokku', en: 'Total accrued' }),
                balanceLabel: translation({ et: 'Saldo', en: 'Balance' }),
                allowance: translation({ et: 'Norm', en: 'Allowance' }),
                totalAllowance: translation({ et: 'Norm kokku', en: 'Total allowance' }),
                upcomingBooked: translation({ et: 'Eelseisvad', en: 'Upcoming' }),
                daysTakenTitle: translation({ et: 'Võetud päevad', en: 'Days taken' }),
                daysTakenOf: (used: number, total: number) =>
                    translation({ et: `${used} / ${total} päeva`, en: `${used} of ${total} days` }),
                dayCount: (n: number) =>
                    translation({
                        et: `${n} ${n === 1 ? 'päev' : 'päeva'}`,
                        en: `${n} ${n === 1 ? 'day' : 'days'}`,
                    }),
                // Töövaba nädal is all-or-nothing (a single 7-day block), so it
                // reads as used / not used rather than a day count.
                toovabaUsed: translation({ et: 'Kasutatud', en: 'Used' }),
                toovabaUnused: translation({ et: 'Kasutamata', en: 'Not used' }),
                dayWord: (n: number) =>
                    translation({
                        et: n === 1 ? 'päev' : 'päeva',
                        en: n === 1 ? 'day' : 'days',
                    }),
                // Per-year day split of a year-spanning absence (ⓘ tooltip),
                // Humaans-style: "1 päev: periood 1. jaan 2026 – 31. dets 2026".
                daysInPeriod: (days: number, year: number) =>
                    translation({
                        et: `${days} ${days === 1 ? 'päev' : 'päeva'}: periood 1. jaan ${year} – 31. dets ${year}`,
                        en: `${days} ${days === 1 ? 'day' : 'days'} taken: period 1 Jan ${year} – 31 Dec ${year}`,
                    }),
                showMore: (n: number) => translation({ et: `Veel ${n}`, en: `${n} more` }),
                showLess: translation({ et: 'Näita vähem', en: 'Show less' }),
                detailsTitle: translation({ et: 'Üksikasjad', en: 'Details' }),
                // Stats-card header, shown only with the admin "Muuda" action (TASK-567)
                balanceTitle: translation({ et: 'Puhkusesaldo', en: 'Leave balance' }),
                adjustments: translation({ et: 'Korrigeeritud', en: 'Adjusted' }),
                approvalFlow: translation({ et: 'Kinnitusvoog', en: 'Approval flow' }),
                approvalManager: translation({ et: 'Juht kinnitab', en: 'Manager approves' }),
                approvalAutomatic: translation({ et: 'Automaatne', en: 'Automatic' }),
                holidayCountry: translation({ et: 'Eesti', en: 'Estonia' }),
                nextHoliday: (name: string, date: string) =>
                    translation({ et: `järgmine: ${name}, ${date}`, en: `next: ${name}, ${date}` }),
                toovabaNote: translation({
                    et: '+ Töövaba nädal (3+ a staaži)',
                    en: '+ Free week (3+ yrs tenure)',
                }),
                // Leave-policy bundle editing from the profile (TASK-348)
                policyEdit: translation({ et: 'Muuda', en: 'Edit' }),
                policyEditTitle: translation({
                    et: 'Muuda puhkusepoliitikat',
                    en: 'Change leave policy',
                }),
                policyBundle: translation({ et: 'Puhkusepoliitika', en: 'Leave policy' }),
                policyNone: translation({ et: 'Määramata', en: 'Not assigned' }),
                policyEffectiveFrom: translation({ et: 'Kehtib alates', en: 'Effective from' }),
                policyEffectiveFromHint: translation({
                    et: 'Uued eemaloleku tüübid hakkavad kehtima ja päevi koguma sellest kuupäevast. Tühjaks jättes arvestatakse töösuhte alguse järgi.',
                    en: 'Newly assigned time-away types take effect and accrue from this date. Leave empty to accrue from the employment start.',
                }),
                policySaved: translation({
                    et: 'Puhkusepoliitika salvestatud',
                    en: 'Leave policy saved',
                }),
                currentYearTitle: (year: number) =>
                    translation({ et: `1. jaan ${year} – täna`, en: `1 Jan ${year} – Today` }),
                showHistory: translation({ et: 'Näita ajalugu', en: 'Show history' }),
                // Year-card summary ↔ full breakdown toggle (TASK-232)
                showDetailed: translation({ et: 'Näita detailselt', en: 'Full breakdown' }),
                showSummary: translation({ et: 'Näita kokkuvõtet', en: 'Show summary' }),
                // Read-only TLS §69/§71 final-settlement preview (admin only)
                settlementTitle: translation({
                    et: 'Lõpparve eelvaade',
                    en: 'Final settlement preview',
                }),
                settlementShow: translation({ et: 'Näita eelvaadet', en: 'Show preview' }),
                settlementHide: translation({ et: 'Peida eelvaade', en: 'Hide preview' }),
                settlementSubtitle: (date: string) =>
                    translation({
                        et: `Kui tööleping lõpeks ${date}`,
                        en: `If employment ended on ${date}`,
                    }),
                settlementEarned: translation({
                    et: 'Teenitud tänavu',
                    en: 'Earned this year',
                }),
                settlementUnearned: translation({
                    et: 'Ette antud, teenimata',
                    en: 'Granted but unearned',
                }),
                settlementBalance: translation({ et: 'Saldo lõpparvel', en: 'Settlement balance' }),
                settlementPayout: (days: number) =>
                    translation({
                        et: `Hüvitatakse ${days} päeva (TLS §71)`,
                        en: `Pay out ${days} days (§71)`,
                    }),
                settlementWithhold: (days: number) =>
                    translation({
                        et: `Peetakse kinni ${days} päeva (TLS §69)`,
                        en: `Withhold ${days} days (§69)`,
                    }),
                settlementEven: translation({ et: 'Saldo on tasakaalus', en: 'Settled even' }),
            },
        },
        // M4: Reports
        reports: {
            title: translation({ et: 'Raportid', en: 'Reports' }),
            balanceOverview: translation({ et: 'Saldode ülevaade', en: 'Balance Overview' }),
            usageSummary: translation({ et: 'Kasutuse kokkuvõte', en: 'Usage Summary' }),
            usageByDepartment: translation({
                et: 'Kasutus osakondade lõikes',
                en: 'Usage by Department',
            }),
            usageByType: translation({
                et: 'Kasutus tüübi lõikes',
                en: 'Usage by Leave Type',
            }),
            monthlyTrend: translation({ et: 'Kuude lõikes', en: 'Monthly Trend' }),
            export: translation({ et: 'Ekspordi CSV', en: 'Export CSV' }),
            exporting: translation({ et: 'Ekspordin...', en: 'Exporting...' }),
            totalDaysUsed: translation({ et: 'Päevi kasutatud', en: 'Days Used' }),
            employeesOnLeave: translation({ et: 'Töötajaid puhkusel', en: 'Employees on Leave' }),
            avgPerEmployee: translation({ et: 'Keskmine töötaja kohta', en: 'Avg per Employee' }),
            year: translation({ et: 'Aasta', en: 'Year' }),
            employee: translation({ et: 'Töötaja', en: 'Employee' }),
            department: translation({ et: 'Osakond', en: 'Department' }),
            team: translation({ et: 'Tiim', en: 'Team' }),
            leaveType: translation({ et: 'Tüüp', en: 'Type' }),
            policy: translation({ et: 'Poliitika', en: 'Policy' }),
            entitlement: translation({ et: 'Norm', en: 'Entitlement' }),
            used: translation({ et: 'Kasutatud', en: 'Used' }),
            pending: translation({ et: 'Ootel', en: 'Pending' }),
            available: translation({ et: 'Saadaval', en: 'Available' }),
            carryover: translation({ et: 'Üleviimine', en: 'Carryover' }),
            days: translation({ et: 'päeva', en: 'days' }),
            requests: translation({ et: 'taotlust', en: 'requests' }),
            noData: translation({
                et: 'Andmeid pole',
                en: 'No data available',
            }),
            allDepartments: translation({ et: 'Kõik osakonnad', en: 'All departments' }),
            allTeams: translation({ et: 'Kõik tiimid', en: 'All teams' }),
            // M6: Standard reports
            standardReports: translation({ et: 'Standardraportid', en: 'Standard Reports' }),
            reportBuilder: translation({ et: 'Raportikoostaja', en: 'Report Builder' }),
            savedReports: translation({ et: 'Salvestatud raportid', en: 'Saved Reports' }),
            createCustomReport: translation({
                et: 'Koosta kohandatud raport',
                en: 'Create custom report',
            }),
            openReport: translation({ et: 'Ava', en: 'Open' }),
            deleteReport: translation({ et: 'Kustuta', en: 'Delete' }),
            noSavedReports: translation({
                et: 'Salvestatud raporteid pole',
                en: 'No saved reports',
            }),
            // Standard report names
            absencesSummary: translation({ et: 'Eemalolekute kokkuvõte', en: 'Absences Summary' }),
            absencesSummaryDesc: translation({
                et: 'Kinnitatud/ootel taotlused kuupäevavahemikus',
                en: 'Approved/pending requests in date range',
            }),
            payrollReport: translation({ et: 'Palgainfo raport', en: 'Payroll Info Report' }),
            payrollReportDesc: translation({
                et: 'Kuu palgainfo (gotoAndPlay & NOPE) — saadetakse automaatselt iga kuu 6. kuupäeval e-postiga',
                en: 'Monthly payroll info (gotoAndPlay & NOPE) — emailed automatically on the 6th each month',
            }),
            payrollOpenPreview: translation({ et: 'Ava eelvaade', en: 'Open preview' }),
            payrollMonth: translation({ et: 'Kuu', en: 'Month' }),
            payrollYear: translation({ et: 'Aasta', en: 'Year' }),
            q12Report: translation({ et: 'Q12 tulemused', en: 'Q12 Results' }),
            q12ReportDesc: translation({
                et: 'Rahulolu-küsitluse tulemused gruppide lõikes, kõik perioodid',
                en: 'Satisfaction survey results by group, across all periods',
            }),
            q12Campaign: translation({ et: 'Periood', en: 'Period' }),
            q12Question: translation({ et: 'Küsimus', en: 'Question' }),
            q12Responses: translation({ et: 'Vastuseid', en: 'Responses' }),
            q12Average: translation({ et: 'Keskmine', en: 'Average' }),
            q12Respondents: (n: number) =>
                translation({ et: `${n} vastajat`, en: `${n} respondents` }),
            q12NoGroups: translation({
                et: 'Selles perioodis pole sulle nähtavaid tiime.',
                en: 'No teams visible to you in this period.',
            }),
            q12Participation: translation({ et: 'Osalus', en: 'Participation' }),
            q12ParticipationByGroup: translation({
                et: 'Osalus tiimide kaupa',
                en: 'Participation by team',
            }),
            q12Overall: translation({ et: 'Üldkeskmine', en: 'Overall average' }),
            q12RespondentsLabel: translation({ et: 'Vastajaid', en: 'Respondents' }),
            q12Compare: translation({ et: 'Võrdlus', en: 'Compare with' }),
            q12CompareNone: translation({ et: 'Ilma võrdluseta', en: 'No comparison' }),
            q12History: translation({
                et: 'Üldkeskmine perioodide lõikes (kõik vastused)',
                en: 'Overall average by period (all responses)',
            }),
            q12HistoryTeam: translation({
                et: 'Tiimi üldkeskmine perioodide lõikes',
                en: 'Team overall average by period',
            }),
            q12Previous: translation({ et: 'Eelmine', en: 'Previous' }),
            q12ExportSection: translation({ et: 'Jaotis', en: 'Section' }),
            q12ExportChange: translation({ et: 'Muutus', en: 'Change' }),
            q12Notes: translation({ et: 'Märkmed', en: 'Notes' }),
            q12AnonymityNote: translation({
                et: 'Anonüümsuse kaitseks ei kuvata keskmist, kui küsimusele on alla 4 vastuse.',
                en: 'To protect anonymity, averages are hidden for questions with fewer than 4 responses.',
            }),
            q12ResultsHiddenUntil: (date: string) =>
                translation({
                    et: `Kampaania on veel avatud — tulemused avalikustatakse pärast tähtaega (${date}) või kohe, kui kõik on vastanud. Seni näed vaid osalust.`,
                    en: `The campaign is still open — results are published after the deadline (${date}) or as soon as everyone has answered. Until then only participation is shown.`,
                }),
            q12OpenMarker: translation({ et: 'avatud', en: 'open' }),
            /** Heading over a manager's own scope; singular when they see one team. */
            q12MyTeamTitle: (teamCount: number) =>
                translation({
                    et: teamCount === 1 ? 'Minu tiim' : 'Minu tiimid',
                    en: teamCount === 1 ? 'My team' : 'My teams',
                }),
            q12TeamFilter: translation({ et: 'Tiim', en: 'Team' }),
            q12AllTeams: translation({ et: 'Kõik tiimid', en: 'All teams' }),
            /** Admin's default in the team filter: only campaign-wide figures. */
            q12NoTeamSelected: translation({ et: 'Tiim valimata', en: 'No team selected' }),
            q12TeamOverview: translation({ et: 'Tiimi ülevaade', en: 'Team overview' }),
            q12TextAnswers: translation({ et: 'Tekstilised vastused', en: 'Text answers' }),
            q12AnswersCount: (n: number) =>
                translation({ et: `${n} vastust`, en: `${n} answer${n === 1 ? '' : 's'}` }),
            q12SelectTeamHint: translation({
                et: 'Vali ülevalt filtrist "Tiim", et näha tiimi osalust ja tulemusi.',
                en: 'Pick a team in the "Team" filter above to see its participation and results.',
            }),
            q12Programme: translation({ et: 'Kampaania ülevaade', en: 'Programme overview' }),
            q12StatusOpen: translation({ et: 'Käimas', en: 'Running' }),
            q12StatusClosed: translation({ et: 'Lõppenud', en: 'Ended' }),
            q12StatusUpcoming: translation({ et: 'Tulekul', en: 'Upcoming' }),
            q12EndsIn: (days: number) =>
                translation({
                    et:
                        days === 0
                            ? 'Lõpeb täna'
                            : days === 1
                              ? 'Lõpeb homme'
                              : `Lõpeb ${days} päeva pärast`,
                    en:
                        days === 0
                            ? 'Ends today'
                            : days === 1
                              ? 'Ends tomorrow'
                              : `Ends in ${days} days`,
                }),
            q12EndedOn: (date: string) =>
                translation({ et: `Lõppes ${date}`, en: `Ended ${date}` }),
            q12StartsOn: (date: string) =>
                translation({ et: `Algab ${date}`, en: `Starts ${date}` }),
            q12Responded: translation({ et: 'Vastanud', en: 'Responded' }),
            q12Timeline: translation({ et: 'Ajavahemik', en: 'Timeline' }),
            q12SurveyLink: translation({ et: 'Vormi link', en: 'Survey link' }),
            q12CopyLink: translation({ et: 'Kopeeri', en: 'Copy' }),
            q12Copied: translation({ et: 'Kopeeritud', en: 'Copied' }),
            q12VsPrevious: translation({ et: 'vs eelmine periood', en: 'vs previous period' }),
            q12CompanyTitle: translation({
                et: 'Kogu ettevõte (kõik tiimid kokku)',
                en: 'Whole company (all teams combined)',
            }),
            q12Answered: (done: number, total: number) =>
                translation({ et: `${done}/${total} vastanud`, en: `${done}/${total} answered` }),
            headcountSnapshot: translation({ et: 'Personaliarv', en: 'Headcount Snapshot' }),
            headcountSnapshotDesc: translation({
                et: 'Aktiivsed töötajad osakondade lõikes',
                en: 'Active employees by department',
            }),
            leaveBalancesReport: translation({ et: 'Puhkusesaldod', en: 'Leave Balances' }),
            leaveBalancesReportDesc: translation({
                et: 'Kõik puhkusesaldod töötajate lõikes',
                en: 'All leave balances by employee',
            }),
            birthdaysReport: translation({ et: 'Sünnipäevad', en: 'Birthdays' }),
            birthdaysReportDesc: translation({
                et: 'Eelseisvad sünnipäevad',
                en: 'Upcoming birthdays',
            }),
            workAnniversaries: translation({ et: 'Tööaastapäevad', en: 'Work Anniversaries' }),
            workAnniversariesDesc: translation({
                et: 'Eelseisvad tööaastapäevad',
                en: 'Upcoming work anniversaries',
            }),
            probationUpcoming: translation({
                et: 'Katseaeg lõppemas',
                en: 'Probation Ending Soon',
            }),
            probationUpcomingDesc: translation({
                et: 'Katseajad, mis lõppevad 90 päeva jooksul',
                en: 'Probation periods ending within 90 days',
            }),
            compensationChanges: translation({
                et: 'Töötasude muutused',
                en: 'Compensation Changes',
            }),
            compensationChangesDesc: translation({
                et: 'Töötasu ja koormuse muutused valitud perioodil',
                en: 'Salary and FTE changes in the selected period',
            }),
            // Builder
            selectColumns: translation({ et: 'Vali veerud', en: 'Select Columns' }),
            applyFilters: translation({ et: 'Rakenda filtrid', en: 'Apply Filters' }),
            preview: translation({ et: 'Eelvaade', en: 'Preview' }),
            previewReport: translation({ et: 'Eelvaata', en: 'Preview' }),
            saveReport: translation({ et: 'Salvesta raport', en: 'Save Report' }),
            reportName: translation({ et: 'Raporti nimi', en: 'Report Name' }),
            reportSaved: translation({ et: 'Raport salvestatud!', en: 'Report saved!' }),
            reportDeleted: translation({ et: 'Raport kustutatud.', en: 'Report deleted.' }),
            noColumnsSelected: translation({
                et: 'Veergusid pole valitud',
                en: 'No columns selected',
            }),
            // Column categories (mirror the Humaans "Export data" groups)
            categoryBasics: translation({ et: 'Põhiandmed', en: 'Basics' }),
            categoryEmployment: translation({ et: 'Töösuhe', en: 'Employment' }),
            categoryBanking: translation({ et: 'Pangainfo', en: 'Banking' }),
            categoryCompensation: translation({ et: 'Töötasu', en: 'Compensation' }),
            categorySocial: translation({ et: 'Sotsiaalne', en: 'Social' }),
            categoryLeaveDays: translation({
                et: 'Eemalviibitud päevad',
                en: 'Time away days taken',
            }),
            categoryLeaveDetails: translation({ et: 'Puhkusejäägid', en: 'Time away details' }),
            // Export
            exportReport: translation({ et: 'Ekspordi raport', en: 'Export Report' }),
            exportCsv: translation({ et: 'Ekspordi CSV', en: 'Export CSV' }),
            exportXlsx: translation({ et: 'Ekspordi Excel', en: 'Export Excel' }),
            exportPdf: translation({ et: 'Ekspordi PDF', en: 'Export PDF' }),
            exportSuccess: translation({ et: 'Raport eksporditud!', en: 'Report exported!' }),
            backToReports: translation({ et: 'Tagasi raportitesse', en: 'Back to Reports' }),
            rowCount: (count: number) => translation({ et: `${count} rida`, en: `${count} rows` }),
            // Filters
            filters: translation({ et: 'Filtrid', en: 'Filters' }),
            filterDepartment: translation({ et: 'Osakond', en: 'Department' }),
            filterTeam: translation({ et: 'Tiim', en: 'Team' }),
            filterCompany: translation({ et: 'Ettevõte', en: 'Company' }),
            filterStatus: translation({ et: 'Staatus', en: 'Status' }),
            filterDateRange: translation({ et: 'Kuupäevavahemik', en: 'Date Range' }),
            filterStartDate: translation({ et: 'Algus', en: 'Start' }),
            filterEndDate: translation({ et: 'Lõpp', en: 'End' }),
            allDepartmentsFilter: translation({ et: 'Kõik osakonnad', en: 'All departments' }),
            allTeamsFilter: translation({ et: 'Kõik tiimid', en: 'All teams' }),
            allCompaniesFilter: translation({ et: 'Kõik ettevõtted', en: 'All companies' }),
            allStatuses: translation({ et: 'Kõik staatused', en: 'All statuses' }),
            statusActive: translation({ et: 'Aktiivne', en: 'Active' }),
            statusInactive: translation({ et: 'Mitteaktiivne', en: 'Inactive' }),
            statusOnLeave: translation({ et: 'Puhkusel', en: 'On Leave' }),
            includeInactive: translation({
                et: 'Kaasa mitteaktiivsed',
                en: 'Include inactive',
            }),
            exportDropdown: translation({ et: 'Ekspordi ▾', en: 'Export ▾' }),
            clearFilters: translation({ et: 'Tühjenda filtrid', en: 'Clear filters' }),
            // Fallbacks for a column / category key the server did not label
            unknownColumn: (key: string) =>
                translation({ et: `Tundmatu veerg (${key})`, en: `Unknown column (${key})` }),
            unknownCategory: (key: string) =>
                translation({ et: `Muu (${key})`, en: `Other (${key})` }),
        },
        // M4: Audit Log
        audit: {
            title: translation({ et: 'Audit logi', en: 'Audit Log' }),
            date: translation({ et: 'Kuupäev', en: 'Date' }),
            employee: translation({ et: 'Töötaja', en: 'Employee' }),
            action: translation({ et: 'Tegevus', en: 'Action' }),
            amount: translation({ et: 'Summa', en: 'Amount' }),
            actor: translation({ et: 'Tegija', en: 'Actor' }),
            note: translation({ et: 'Märkus', en: 'Note' }),
            policy: translation({ et: 'Poliitika', en: 'Policy' }),
            allTypes: translation({ et: 'Kõik tüübid', en: 'All types' }),
            searchEmployee: translation({
                et: 'Otsi töötajat...',
                en: 'Search employee...',
            }),
            system: translation({ et: 'Süsteem', en: 'System' }),
            page: (current: number, total: number) =>
                translation({
                    et: `Lehekülg ${current} / ${total}`,
                    en: `Page ${current} of ${total}`,
                }),
            prev: translation({ et: 'Eelmine', en: 'Previous' }),
            next: translation({ et: 'Järgmine', en: 'Next' }),
            noEntries: translation({
                et: 'Logikanded puuduvad',
                en: 'No log entries found',
            }),
            showing: (from: number, to: number, total: number) =>
                translation({
                    et: `Näitan ${from}–${to} / ${total}`,
                    en: `Showing ${from}–${to} of ${total}`,
                }),
            entries: translation({ et: 'kirjet', en: 'entries' }),
            allActors: translation({ et: 'Kõik kasutajad', en: 'All actors' }),
            allTargets: translation({ et: 'Kõik sihtmärgid', en: 'All targets' }),
            fromDate: translation({ et: 'Alguskuupäev', en: 'From date' }),
            toDate: translation({ et: 'Lõppkuupäev', en: 'To date' }),
            clear: translation({ et: 'Tühista', en: 'Clear' }),
            target: translation({ et: 'Sihtmärk', en: 'Target' }),
            details: translation({ et: 'Detailid', en: 'Details' }),
            impersonatingUser: translation({
                et: 'Kehastub kasutajaks',
                en: 'Impersonating user',
            }),
            // Actor-kind filter (human vs automated/system events)
            actorKind: translation({ et: 'Tegija tüüp', en: 'Actor type' }),
            human: translation({ et: 'Inimene', en: 'Human' }),
            automated: translation({ et: 'Automaatne', en: 'Automated' }),
            // Human-readable action labels (keyed by AUDIT_ACTIONS value). A
            // humanize() fallback in the view covers any not listed here.
            actions: {
                LOGIN_SUCCESS: translation({ et: 'Sisselogimine', en: 'Login' }),
                LOGIN_FAILED: translation({
                    et: 'Ebaõnnestunud sisselogimine',
                    en: 'Login failed',
                }),
                LOGIN_DENIED_DOMAIN: translation({
                    et: 'Sisselogimine keelatud (vale domeen)',
                    en: 'Login denied (invalid domain)',
                }),
                LOGOUT: translation({ et: 'Väljalogimine', en: 'Logout' }),
                LOGIN_AS: translation({ et: 'Kehastumine', en: 'Impersonation' }),
                LOGIN_AS_END: translation({ et: 'Kehastumise lõpp', en: 'Impersonation ended' }),
                EMPLOYEE_CREATED: translation({ et: 'Töötaja loodud', en: 'Employee created' }),
                EMPLOYEE_UPDATED: translation({ et: 'Töötaja muudetud', en: 'Employee updated' }),
                EMPLOYEE_STATUS_CHANGED: translation({
                    et: 'Staatus muudetud',
                    en: 'Status changed',
                }),
                EMPLOYEE_OFFBOARDED: translation({
                    et: 'Töösuhe lõpetatud',
                    en: 'Employee offboarded',
                }),
                COMPENSATION_CREATED: translation({
                    et: 'Töötasu lisatud',
                    en: 'Compensation created',
                }),
                COMPENSATION_UPDATED: translation({
                    et: 'Töötasu muudetud',
                    en: 'Compensation updated',
                }),
                COMPENSATION_DELETED: translation({
                    et: 'Töötasu kustutatud',
                    en: 'Compensation deleted',
                }),
                COMPENSATION_REVEALED: translation({
                    et: 'Töötasu vaadatud',
                    en: 'Compensation viewed',
                }),
                COMPENSATION_ACTIVATED: translation({
                    et: 'Töötasu jõustus',
                    en: 'Compensation activated',
                }),
                EMPLOYMENT_RECORD_DELETED: translation({
                    et: 'Töösuhte kirje kustutatud',
                    en: 'Employment record deleted',
                }),
                EMPLOYEE_CONTRACT_CREATED: translation({
                    et: 'Leping loodud',
                    en: 'Contract created',
                }),
                EMPLOYEE_CONTRACT_UPDATED: translation({
                    et: 'Leping muudetud',
                    en: 'Contract updated',
                }),
                EMPLOYEE_CONTRACT_PROBATION_EXTENDED: translation({
                    et: 'Katseaeg pikendatud',
                    en: 'Probation extended',
                }),
                EMERGENCY_CONTACT_ADDED: translation({
                    et: 'Hädaabikontakt lisatud',
                    en: 'Emergency contact added',
                }),
                EMERGENCY_CONTACT_REMOVED: translation({
                    et: 'Hädaabikontakt eemaldatud',
                    en: 'Emergency contact removed',
                }),
                EMERGENCY_CONTACT_UPDATED: translation({
                    et: 'Hädaabikontakt muudetud',
                    en: 'Emergency contact updated',
                }),
                EXPERIENCE_ADDED: translation({
                    et: 'Kogemuse kirje lisatud',
                    en: 'Experience entry added',
                }),
                EXPERIENCE_UPDATED: translation({
                    et: 'Kogemuse kirje muudetud',
                    en: 'Experience entry updated',
                }),
                EXPERIENCE_REMOVED: translation({
                    et: 'Kogemuse kirje eemaldatud',
                    en: 'Experience entry removed',
                }),
                DOCUMENT_UPLOADED: translation({
                    et: 'Dokument üles laaditud',
                    en: 'Document uploaded',
                }),
                DOCUMENT_UPDATED: translation({ et: 'Dokument muudetud', en: 'Document updated' }),
                DOCUMENT_DELETED: translation({
                    et: 'Dokument kustutatud',
                    en: 'Document deleted',
                }),
                WORKFLOW_TASK_CREATED: translation({ et: 'Ülesanne loodud', en: 'Task created' }),
                WORKFLOW_TASK_COMPLETED: translation({
                    et: 'Ülesanne täidetud',
                    en: 'Task completed',
                }),
                WORKFLOW_TASK_DISMISSED: translation({
                    et: 'Ülesanne tagasi lükatud',
                    en: 'Task dismissed',
                }),
                TIME_OFF_REQUESTED: translation({
                    et: 'Puhkus taotletud',
                    en: 'Time-off requested',
                }),
                TIME_OFF_APPROVED: translation({
                    et: 'Puhkus kinnitatud',
                    en: 'Time-off approved',
                }),
                TIME_OFF_REJECTED: translation({
                    et: 'Puhkus tagasi lükatud',
                    en: 'Time-off rejected',
                }),
                TIME_OFF_CANCELLED: translation({
                    et: 'Puhkus tühistatud',
                    en: 'Time-off cancelled',
                }),
                TIME_OFF_CANCEL_REQUESTED: translation({
                    et: 'Puhkuse kustutamine taotletud',
                    en: 'Time-off cancellation requested',
                }),
                TIME_OFF_CANCEL_REJECTED: translation({
                    et: 'Puhkuse kustutamisest keeldutud',
                    en: 'Time-off cancellation rejected',
                }),
                TIME_OFF_EDITED: translation({
                    et: 'Puhkust muudetud',
                    en: 'Time-off edited',
                }),
                LEAVE_LEDGER_ADJUSTED: translation({
                    et: 'Puhkusejääk muudetud',
                    en: 'Leave balance adjusted',
                }),
                LEAVE_ACCRUED: translation({ et: 'Puhkust arvestatud', en: 'Leave accrued' }),
                LEAVE_EXPIRED: translation({ et: 'Puhkus aegunud', en: 'Leave expired' }),
                POLICY_CREATED: translation({ et: 'Poliitika loodud', en: 'Policy created' }),
                POLICY_UPDATED: translation({ et: 'Poliitika muudetud', en: 'Policy updated' }),
                ROLE_CREATED: translation({ et: 'Roll loodud', en: 'Role created' }),
                ROLE_UPDATED: translation({ et: 'Roll muudetud', en: 'Role updated' }),
                ROLE_DELETED: translation({ et: 'Roll kustutatud', en: 'Role deleted' }),
                ROLE_MEMBER_ADDED: translation({ et: 'Liige lisatud', en: 'Member added' }),
                ROLE_MEMBER_REMOVED: translation({ et: 'Liige eemaldatud', en: 'Member removed' }),
                EXPORT_REPORT: translation({ et: 'Raport eksporditud', en: 'Report exported' }),
                EXPORT_EMPLOYEE_LIST: translation({
                    et: 'Töötajate nimekiri eksporditud',
                    en: 'Employee list exported',
                }),
                SETTINGS_UPDATED: translation({ et: 'Seaded muudetud', en: 'Settings updated' }),
                INTEGRATION_CONNECTED: translation({
                    et: 'Integratsioon ühendatud',
                    en: 'Integration connected',
                }),
                INTEGRATION_DISCONNECTED: translation({
                    et: 'Integratsioon lahti ühendatud',
                    en: 'Integration disconnected',
                }),
            } as Record<string, Translation | undefined>,
            // Verbs for the composed SETTINGS_UPDATED label (keyed by metadata.action).
            settingsVerbs: {
                created: translation({ et: 'loodud', en: 'created' }),
                updated: translation({ et: 'muudetud', en: 'updated' }),
                deleted: translation({ et: 'kustutatud', en: 'deleted' }),
                archived: translation({ et: 'arhiveeritud', en: 'archived' }),
                restored: translation({ et: 'taastatud', en: 'restored' }),
                deactivated: translation({ et: 'deaktiveeritud', en: 'deactivated' }),
                member_added: translation({ et: 'liige lisatud', en: 'member added' }),
                member_removed: translation({ et: 'liige eemaldatud', en: 'member removed' }),
                assignments_updated: translation({
                    et: 'määrangud muudetud',
                    en: 'assignments updated',
                }),
                custom_fields_updated: translation({
                    et: 'lisaväljad muudetud',
                    en: 'custom fields updated',
                }),
                policy_membership_reconciled: translation({
                    et: 'poliitikad sünkroonitud',
                    en: 'policies reconciled',
                }),
            } as Record<string, Translation | undefined>,
            // Target entity-type labels (keyed by target_entity_type).
            entityTypes: {
                employee: translation({ et: 'Töötaja', en: 'Employee' }),
                time_off_request: translation({ et: 'Puhkusetaotlus', en: 'Time-off request' }),
                user: translation({ et: 'Kasutaja', en: 'User' }),
                role: translation({ et: 'Roll', en: 'Role' }),
                leave_policy: translation({ et: 'Puhkusepoliitika', en: 'Leave policy' }),
                leave_policy_bundle: translation({
                    et: 'Puhkusepoliitika komplekt',
                    en: 'Leave policy bundle',
                }),
                company_holiday: translation({ et: 'Riigipüha', en: 'Company holiday' }),
                custom_field: translation({ et: 'Lisaväli', en: 'Custom field' }),
                location: translation({ et: 'Asukoht', en: 'Location' }),
                team: translation({ et: 'Tiim', en: 'Team' }),
                leave_type: translation({ et: 'Puhkuse tüüp', en: 'Leave type' }),
                working_pattern: translation({ et: 'Töögraafik', en: 'Working pattern' }),
                working_pattern_allocation: translation({
                    et: 'Töögraafiku määrang',
                    en: 'Working pattern allocation',
                }),
                compensation: translation({ et: 'Töötasu', en: 'Compensation' }),
                workflow_task: translation({ et: 'Ülesanne', en: 'Task' }),
            } as Record<string, Translation | undefined>,
            userAgent: translation({ et: 'Kasutajaagent', en: 'User agent' }),
            metadata: translation({ et: 'Metaandmed', en: 'Metadata' }),
        },
        // Production error log
        errorLog: {
            title: translation({ et: 'Vealogi', en: 'Error Log' }),
            time: translation({ et: 'Aeg', en: 'Time' }),
            severity: translation({ et: 'Raskusaste', en: 'Severity' }),
            source: translation({ et: 'Allikas', en: 'Source' }),
            error: translation({ et: 'Viga', en: 'Error' }),
            location: translation({ et: 'Asukoht', en: 'Location' }),
            details: translation({ et: 'Detailid', en: 'Details' }),
            stackTrace: translation({ et: 'Veajälg', en: 'Stack trace' }),
            requestContext: translation({ et: 'Päringu kontekst', en: 'Request context' }),
            metadata: translation({ et: 'Metaandmed', en: 'Metadata' }),
            user: translation({ et: 'Kasutaja', en: 'User' }),
            allSeverities: translation({ et: 'Kõik raskusastmed', en: 'All severities' }),
            allSources: translation({ et: 'Kõik allikad', en: 'All sources' }),
            searchMessage: translation({ et: 'Otsi sõnumit...', en: 'Search message...' }),
            fromDate: translation({ et: 'Alguskuupäev', en: 'From date' }),
            toDate: translation({ et: 'Lõppkuupäev', en: 'To date' }),
            clear: translation({ et: 'Tühista', en: 'Clear' }),
            entries: translation({ et: 'viga', en: 'errors' }),
            noEntries: translation({ et: 'Vigu ei leitud', en: 'No errors found' }),
            prev: translation({ et: 'Eelmine', en: 'Previous' }),
            next: translation({ et: 'Järgmine', en: 'Next' }),
            page: (current: number, total: number) =>
                translation({
                    et: `Lehekülg ${current} / ${total}`,
                    en: `Page ${current} of ${total}`,
                }),
            showing: (from: number, to: number, total: number) =>
                translation({
                    et: `Näitan ${from}–${to} / ${total}`,
                    en: `Showing ${from}–${to} of ${total}`,
                }),
            severities: {
                fatal: translation({ et: 'Kriitiline', en: 'Fatal' }),
                error: translation({ et: 'Viga', en: 'Error' }),
                warning: translation({ et: 'Hoiatus', en: 'Warning' }),
            } as Record<string, Translation | undefined>,
            sources: {
                http: translation({ et: 'HTTP päring', en: 'HTTP request' }),
                cron: translation({ et: 'Ajastatud töö', en: 'Scheduled job' }),
                process: translation({ et: 'Protsess', en: 'Process' }),
            } as Record<string, Translation | undefined>,
            userAgent: translation({ et: 'Kasutajaagent', en: 'User agent' }),
        },
        // Admin balance correction as of a date (TASK-567)
        manualAdjustment: {
            title: translation({ et: 'Korrigeeri saldot', en: 'Adjust balance' }),
            amount: translation({ et: 'Summa (päevades) ±', en: 'Amount (in days) ±' }),
            amountHint: translation({
                et: 'Positiivne = lisa, negatiivne = vähenda. Kehtib täies mahus alates valitud kuupäevast.',
                en: 'Positive = credit, negative = debit. Counts in full from the chosen date.',
            }),
            effectiveDate: translation({ et: 'Kuupäeva seisuga', en: 'As of date' }),
            effectiveDateHint: translation({
                et: 'Täna või varasem kuupäev, mille seisuga korrigeeritud jääk kehtib.',
                en: 'Today or an earlier date the corrected balance is true as of.',
            }),
            note: translation({ et: 'Põhjendus', en: 'Reason' }),
            notePlaceholder: translation({
                et: 'Nt raamatupidaja 30.06 jääk 27 päeva',
                en: 'e.g. accountant balance 27 days as of 30.06',
            }),
            noteHint: translation({
                et: 'Kohustuslik — kuvatakse tehingute ajaloos.',
                en: 'Required — shown in the transaction history.',
            }),
            submit: translation({ et: 'Salvesta korrigeerimine', en: 'Save adjustment' }),
            submitting: translation({ et: 'Salvestan...', en: 'Saving...' }),
            success: translation({
                et: 'Saldo korrigeeritud',
                en: 'Balance adjusted',
            }),
            selectPolicy: translation({
                et: 'Eemaloleku tüüp',
                en: 'Leave type',
            }),
            selectPolicyPlaceholder: translation({ et: 'Vali tüüp', en: 'Select type' }),
            noLimitedPolicies: translation({
                et: 'Sellel inimesel pole ühtegi limiidiga eemaloleku tüüpi.',
                en: 'This person has no leave types with a limit.',
            }),
            // Live preview: "Jääk 30.06.2026 seisuga: 8,0 → 11,0 päeva"
            preview: (date: string) =>
                translation({ et: `Jääk ${date} seisuga`, en: `Balance as of ${date}` }),
            previewEarned: translation({
                et: 'Väljateenitud saldo (nagu „Saldo tänase seisuga")',
                en: 'Earned balance (as in "Balance as of today")',
            }),
            previewAvailable: translation({
                et: 'Saadaolev jääk',
                en: 'Available balance',
            }),
            previewLoading: translation({ et: 'Arvutan...', en: 'Calculating...' }),
        },
        // M4: Compensation & Contracts
        compensation: {
            tabLabel: translation({ et: 'Töö ja tasustamine', en: 'Job & Pay' }),
            sectionTitle: translation({ et: 'Tasustamine', en: 'Compensation' }),
            // Block titles inside "Tasustamine ja boonused" (TASK-438 wording)
            salariesTitle: translation({ et: 'Töötasud', en: 'Salaries' }),
            lineItemsTitle: translation({
                et: 'Lisatasud',
                en: 'Additional compensation',
            }),
            lineItemsEmpty: translation({
                et: 'Lisatasusid pole',
                en: 'No additional compensation recorded',
            }),
            addLineItem: translation({ et: 'Lisa lisatasu', en: 'Add additional compensation' }),
            lineItemType: translation({ et: 'Tüüp', en: 'Type' }),
            amount: translation({ et: 'Summa', en: 'Amount' }),
            period: translation({ et: 'Periood', en: 'Period' }),
            periodQuarterly: translation({ et: 'Kvartaalne', en: 'Quarterly' }),
            periodAnnual: translation({ et: 'Aastane', en: 'Annual' }),
            periodOneOff: translation({ et: 'Ühekordne', en: 'One-off' }),
            editAction: translation({ et: 'Muuda', en: 'Edit' }),
            deleteAction: translation({ et: 'Kustuta', en: 'Delete' }),
            deleteLineItemConfirm: translation({
                et: 'Kas oled kindel, et soovid selle lisatasu kustutada?',
                en: 'Delete this additional compensation entry?',
            }),
            contractSection: translation({ et: 'Töökoormus', en: 'Workload' }),
            grossSalary: translation({ et: 'Brutopalk (FTE 1.0)', en: 'Gross salary (1.0 FTE)' }),
            fte: translation({ et: 'Koormus (FTE)', en: 'FTE' }),
            currency: translation({ et: 'Valuuta', en: 'Currency' }),
            payPeriod: translation({ et: 'Makseperiood', en: 'Pay Period' }),
            effectiveFrom: translation({ et: 'Kehtib alates', en: 'Effective From' }),
            lineItemDateChangeHint: translation({
                et: 'Kuupäeva muutmine loob uue kande ja säilitab praeguse ajaloos.',
                en: 'Changing the date creates a new entry and keeps the current one as history.',
            }),
            effectiveTo: translation({ et: 'Kehtib kuni', en: 'Effective Until' }),
            normalizedAmount: translation({
                et: 'Normaliseeritud (FTE 1.0)',
                en: 'Normalized (FTE 1.0)',
            }),
            revealSalary: translation({ et: 'Näita summasid', en: 'Show amounts' }),
            hideSalary: translation({ et: 'Peida summad', en: 'Hide amounts' }),
            addChange: translation({ et: 'Lisa muudatus', en: 'Add Change' }),
            saveChange: translation({ et: 'Salvesta', en: 'Save' }),
            saving: translation({ et: 'Salvestan...', en: 'Saving...' }),
            cancelAction: translation({ et: 'Tühista', en: 'Cancel' }),
            note: translation({ et: 'Märkus', en: 'Note' }),
            notePlaceholder: translation({
                et: 'Lisa märkus muudatuse kohta...',
                en: 'Add a note about this change...',
            }),
            current: translation({ et: 'Praegune', en: 'Current' }),
            future: translation({ et: 'Tulevane', en: 'Future' }),
            futureEffective: (date: string) =>
                translation({ et: `Jõustub ${date}`, en: `Effective ${date}` }),
            history: translation({ et: 'Ajalugu', en: 'History' }),
            noData: translation({
                et: 'Tasustamise andmed puuduvad',
                en: 'No compensation data',
            }),
            // Pay periods
            monthly: translation({ et: 'Igakuine', en: 'Monthly' }),
            yearly: translation({ et: 'Aastane', en: 'Yearly' }),
            hourly: translation({ et: 'Tunnitasu', en: 'Hourly' }),
            // Contract types
            contractType: translation({ et: 'Töökoormuse tüüp', en: 'Workload Type' }),
            startDate: translation({ et: 'Alguskuupäev', en: 'Start Date' }),
            endDate: translation({ et: 'Lõppkuupäev', en: 'End Date' }),
            probationEnd: translation({ et: 'Katseaja lõpp', en: 'Probation End' }),
            // Employment-dates section (first in Töö ja tasustamine)
            employmentSection: translation({ et: 'Töösuhe', en: 'Employment' }),
            contractStartDate: translation({
                et: 'Töölepingu sõlmimise kuupäev',
                en: 'Contract date',
            }),
            extendProbation: translation({
                et: 'Pikenda katseaega',
                en: 'Extend Probation',
            }),
            addContract: translation({ et: 'Lisa töökoormus', en: 'Add Workload' }),
            contractTypes: {
                permanent: translation({ et: 'Alaline', en: 'Permanent' }),
                fixed_term: translation({ et: 'Tähtajaline', en: 'Fixed Term' }),
                b2b: translation({ et: 'Töövõtuleping (B2B)', en: 'B2B Contract' }),
                intern: translation({ et: 'Praktikant', en: 'Intern' }),
                flex: translation({ et: 'Paindlik', en: 'Flex' }),
                part_time: translation({ et: 'Osaline tööaeg', en: 'Part-time' }),
                full_time: translation({ et: 'Täiskoormus', en: 'Full-time' }),
            },
            workingPatterns: {
                sectionTitle: translation({ et: 'Tööajad', en: 'Working hours' }),
                name: translation({ et: 'Nimi', en: 'Name' }),
                workingSchedule: translation({ et: 'Töögraafik', en: 'Working schedule' }),
                fte: translation({ et: 'FTE', en: 'FTE' }),
                effectiveDate: translation({ et: 'Kehtib alates', en: 'Effective date' }),
                endDate: translation({ et: 'Kehtib kuni', en: 'Effective until' }),
                noData: translation({
                    et: 'Töögraafiku andmed puuduvad',
                    en: 'No working pattern data',
                }),
            },
            probationActive: translation({ et: 'Katseajal', en: 'On Probation' }),
            noContract: translation({
                et: 'Töökoormuse andmed puuduvad',
                en: 'No workload data',
            }),
            saveContract: translation({ et: 'Salvesta töökoormus', en: 'Save Workload' }),
            savingContract: translation({ et: 'Salvestan...', en: 'Saving...' }),
            cancelContract: translation({ et: 'Tühista', en: 'Cancel' }),
            fixedTermEndRequired: translation({
                et: 'Tähtajalise lepingu puhul on lõppkuupäev kohustuslik',
                en: 'End date is required for fixed-term contracts',
            }),
            // Employment timeline
            employmentTimeline: translation({
                et: 'Ametikohtade ajalugu',
                en: 'Employment History',
            }),
            position: translation({ et: 'Ametikoht', en: 'Position' }),
            noEmploymentHistory: translation({
                et: 'Ametikoha ajalugu puudub',
                en: 'No employment history',
            }),
            // FTE Analytics
            fteAnalytics: translation({ et: 'FTE analüütika', en: 'FTE Analytics' }),
            totalFte: translation({ et: 'FTE kokku', en: 'Total FTE' }),
            headcount: translation({ et: 'Töötajate arv', en: 'Headcount' }),
            avgFte: translation({ et: 'Keskmine FTE', en: 'Avg FTE' }),
            deleteFuture: translation({ et: 'Kustuta', en: 'Delete' }),
            confirmDelete: translation({
                et: 'Kas kustutada see kirje?',
                en: 'Delete this record?',
            }),
        },
        placeholder: {
            coming: translation({ et: 'Tulekul', en: 'Coming soon' }),
            description: translation({
                et: 'See vaade on arendamisel.',
                en: 'This view is under development.',
            }),
        },
        globalSearch: {
            placeholder: translation({
                et: 'Otsi inimesi ja tööruum...',
                en: 'Search people and workspace...',
            }),
            people: translation({ et: 'Inimesed', en: 'People' }),
            workspace: translation({ et: 'Kiirlingid', en: 'Quick links' }),
            searching: translation({ et: 'Otsin...', en: 'Searching...' }),
            noResults: translation({ et: 'Tulemusi ei leitud', en: 'No results found' }),
            navigate: translation({ et: 'navigeeri', en: 'navigate' }),
            select: translation({ et: 'vali', en: 'select' }),
            close: translation({ et: 'sulge', en: 'close' }),
        },
        // Shared component translations
        shared: {
            paginationShowing: (from: number, to: number, total: number) =>
                translation({
                    et: `Näitan ${from}–${to} / ${total}`,
                    en: `Showing ${from}–${to} of ${total}`,
                }),
            paginationPage: (current: number, total: number) =>
                translation({
                    et: `Lehekülg ${current} / ${total}`,
                    en: `Page ${current} of ${total}`,
                }),
            paginationPrev: translation({ et: 'Eelmine', en: 'Previous' }),
            paginationNext: translation({ et: 'Järgmine', en: 'Next' }),
            confirmTitle: translation({ et: 'Kinnita tegevus', en: 'Confirm Action' }),
            confirmCancel: translation({ et: 'Tühista', en: 'Cancel' }),
            confirmDelete: translation({ et: 'Kustuta', en: 'Delete' }),
            confirmDiscard: translation({ et: 'Loobu', en: 'Discard' }),
            clearFilters: translation({ et: 'Tühjenda', en: 'Clear' }),
        },
        // Shared inline labels
        inline: {
            peopleAway: (n: number) =>
                translation({ et: `${n} inimest eemal`, en: `${n} people away` }),
            activeCount: (n: number) => translation({ et: `${n} aktiivset`, en: `${n} active` }),
            allPolicies: translation({ et: 'Kõik poliitikad', en: 'All policies' }),
            noEntries: translation({ et: 'Kanded puuduvad', en: 'No entries' }),
            submitFailed: translation({
                et: 'Taotluse esitamine ebaõnnestus.',
                en: 'Failed to submit request.',
            }),
            name: translation({ et: 'Nimi', en: 'Name' }),
            dates: translation({ et: 'Kuupäevad', en: 'Dates' }),
            businessDays: translation({ et: 'Päevi', en: 'Days' }),
            statusLabel: translation({ et: 'Staatus', en: 'Status' }),
            department: translation({ et: 'Osakond', en: 'Department' }),
            team: translation({ et: 'Meeskond', en: 'Team' }),
            today: translation({ et: 'Täna', en: 'Today' }),
        },
        // Relative time labels (notifications)
        relTime: {
            justNow: translation({ et: 'just nüüd', en: 'just now' }),
            minAgo: (n: number) => translation({ et: `${n} min tagasi`, en: `${n} min ago` }),
            hoursAgo: (n: number) => translation({ et: `${n} h tagasi`, en: `${n} h ago` }),
            daysAgo: (n: number) => translation({ et: `${n} p tagasi`, en: `${n} d ago` }),
        },
        // Settings
        settings: {
            description: translation({
                et: 'Halda tööruumi seadeid.',
                en: 'Manage workspace settings.',
            }),
            close: translation({ et: 'Sulge', en: 'Close' }),
            // M9: Settings tabs
            tabs: {
                general: translation({ et: 'Üldine', en: 'General' }),
                leavePolicies: translation({
                    et: 'Puhkusepoliitikad',
                    en: 'Leave Policies',
                }),
                leaveTypes: translation({ et: 'Eemaloleku tüübid', en: 'Leave Types' }),
                holidays: translation({ et: 'Riiklikud pühad', en: 'Public Holidays' }),
                workingPatterns: translation({
                    et: 'Töögraafikud',
                    en: 'Working Patterns',
                }),
                teams: translation({ et: 'Tiimid', en: 'Teams' }),
                locations: translation({ et: 'Asukohad', en: 'Locations' }),
                customFields: translation({
                    et: 'Kohandatavad väljad',
                    en: 'Custom Fields',
                }),
                compensationTypes: translation({
                    et: 'Lisatasude tüübid',
                    en: 'Compensation Types',
                }),
                roles: translation({ et: 'Rollid', en: 'Roles' }),
                viewAs: translation({ et: 'Vaata kasutajana', en: 'View as' }),
            },
            // M10: Sidebar groups
            groups: {
                organisation: translation({ et: 'Organisatsioon', en: 'Organisation' }),
                timeOff: translation({ et: 'Eemalolek', en: 'Time off' }),
                system: translation({ et: 'Süsteem', en: 'System' }),
            },
            settingsTitle: translation({
                et: 'Tööruumi seaded',
                en: 'Workspace Settings',
            }),
            settingsDescription: translation({
                et: 'Halda tööruumi seadeid, poliitikaid ja konfiguratsioone.',
                en: 'Manage workspace settings, policies, and configurations.',
            }),
            // M9.2: General
            general: {
                companyInfo: translation({ et: 'Ettevõtte andmed', en: 'Company Info' }),
                companyName: translation({ et: 'Nimi', en: 'Name' }),
                regCode: translation({ et: 'Registrikood', en: 'Reg. Code' }),
                // TASK-527: the full-time norm day behind BalanceTime's expected hours
                standardDayHours: translation({
                    et: 'Normtööpäev',
                    en: 'Standard workday',
                }),
                noCompanies: translation({
                    et: 'Ettevõtteid pole seadistatud',
                    en: 'No companies configured',
                }),
                notificationsTitle: translation({
                    et: 'Teavituste seaded',
                    en: 'Notification Settings',
                }),
                notificationsInfo: translation({
                    et: 'Portal saadab automaatselt teavitusi järgmiste sündmuste korral:',
                    en: 'Portal automatically sends notifications for the following events:',
                }),
                notifLeaveRequest: translation({
                    et: '• Uus eemalolekutaotlus — teavitatakse kinnitajat',
                    en: '• New time-off request — notifies the approver',
                }),
                notifLeaveApproved: translation({
                    et: '• Taotlus kinnitatud/keeldutud — teavitatakse töötajat',
                    en: '• Request approved/rejected — notifies the employee',
                }),
                notifBirthday: translation({
                    et: '• Sünnipäeva meeldetuletus — teavitatakse meeskonna liikmeid',
                    en: '• Birthday reminder — notifies team members',
                }),
                notifProbation: translation({
                    et: '• Katseaeg lõppemas — teavitatakse juhti 30 päeva enne',
                    en: '• Probation ending — notifies manager 30 days before',
                }),
                notifAnniversary: translation({
                    et: '• Tööaastapäev — teavitatakse meeskonna liikmeid',
                    en: '• Work anniversary — notifies team members',
                }),
                googleDriveUrl: translation({
                    et: "Google Drive'i URL",
                    en: 'Google Drive URL',
                }),
                save: translation({ et: 'Salvesta', en: 'Save' }),
                saving: translation({ et: 'Salvestan...', en: 'Saving...' }),
            },
            // M9.3: Leave Policies
            policies: {
                allTypes: translation({ et: 'Kõik', en: 'All' }),
                createPolicy: translation({ et: 'Loo poliitika', en: 'Create Policy' }),
                name: translation({ et: 'Nimi', en: 'Name' }),
                leaveType: translation({ et: 'Eemaloleku tüüp', en: 'Leave Type' }),
                annualLimit: translation({
                    et: 'Aastane limiit (päevad)',
                    en: 'Annual Limit (days)',
                }),
                accrualMethod: translation({ et: 'Arvestusmeetod', en: 'Accrual Method' }),
                minNoticeDays: translation({
                    et: 'Etteteatamine (päevad)',
                    en: 'Notice Period (days)',
                }),
                minDurationDays: translation({
                    et: 'Minimaalne kestus (päevad)',
                    en: 'Minimum Duration (days)',
                }),
                mustTakeFullWeek: translation({
                    et: 'Tuleb võtta täisnädal',
                    en: 'Must Take Full Week',
                }),
                requiresApproval: translation({
                    et: 'Vajab kinnitust',
                    en: 'Requires Approval',
                }),
                allocationRule: translation({ et: 'Maht', en: 'Allocation' }),
                allocationNoLimit: translation({
                    et: 'Piiranguta — saldot ei arvestata',
                    en: 'No limit — no balance tracking',
                }),
                allocationLimited: translation({
                    et: 'Piiratud maht (aastane limiit)',
                    en: 'Limited allocations (annual limit)',
                }),
                usage: translation({ et: 'Kasutus', en: 'Usage' }),
                usageFullAllocation: translation({
                    et: 'Saab võtta kogu aastase mahu ette',
                    en: 'Allow taking full allocation',
                }),
                usageAccruedOnly: translation({
                    et: 'Saab võtta ainult kogunenud päevi (igakuine arvestus)',
                    en: 'Allow taking accrued only (monthly accrual)',
                }),
                carryover: translation({
                    et: 'Ülekanne järgmisse aastasse',
                    en: 'Carry over',
                }),
                carryoverNone: translation({
                    et: 'Ei kandu üle — aegub aasta lõpus',
                    en: 'No carry over — expires at year end',
                }),
                carryover12: translation({
                    et: 'Kandub üle — kasutatav 12 kuud',
                    en: 'Carries over — usable for 12 months',
                }),
                carryover18: translation({
                    et: 'Kandub üle — kasutatav 18 kuud',
                    en: 'Carries over — usable for 18 months',
                }),
                carryoverCustom: translation({
                    et: 'Kandub üle — kohandatud periood',
                    en: 'Carries over — custom period',
                }),
                carryoverCustomMonths: translation({
                    et: 'Kasutusperiood pärast aasta lõppu (kuud)',
                    en: 'Usage period past year end (months)',
                }),
                allowanceRounding: translation({
                    et: 'Mahu ümardamine',
                    en: 'Allowance rounding',
                }),
                roundingHalf: translation({
                    et: 'Ümarda üles poole päeva täpsusega',
                    en: 'Round up to the nearest half',
                }),
                roundingFull: translation({
                    et: 'Ümarda üles täispäevani',
                    en: 'Round up to the nearest full day',
                }),
                roundingNone: translation({ et: 'Ära ümarda', en: 'No rounding' }),
                maxDurationDays: translation({
                    et: 'Maksimaalne kestus korraga (päevad, tühi = piiranguta)',
                    en: 'Maximum duration per request (days, empty = no cap)',
                }),
                appliesTo: translation({ et: 'Kehtib', en: 'Applies To' }),
                appliesToAll: translation({ et: 'Kõigile', en: 'Everyone' }),
                appliesToIntern: translation({ et: 'Praktikantidele', en: 'Interns Only' }),
                appliesToCustom: translation({ et: 'Kohandatud', en: 'Custom' }),
                active: translation({ et: 'Aktiivne', en: 'Active' }),
                inactive: translation({ et: 'Mitteaktiivne', en: 'Inactive' }),
                accrualUpfront: translation({ et: 'Ette', en: 'Upfront' }),
                accrualMonthly: translation({ et: 'Igakuine', en: 'Monthly' }),
                accrualNone: translation({ et: 'Puudub', en: 'None' }),
                editPolicy: translation({ et: 'Muuda', en: 'Edit' }),
                deletePolicy: translation({ et: 'Kustuta', en: 'Delete' }),
                confirmDelete: translation({
                    et: 'Kas oled kindel, et soovid selle poliitika kustutada?',
                    en: 'Are you sure you want to delete this policy?',
                }),
                cannotDelete: translation({
                    et: 'Poliitikale on töötajaid määratud, ei saa kustutada.',
                    en: 'Policy has assigned employees and cannot be deleted.',
                }),
                noPolicies: translation({
                    et: 'Poliitikaid pole veel loodud.',
                    en: 'No policies created yet.',
                }),
                save: translation({ et: 'Salvesta', en: 'Save' }),
                saving: translation({ et: 'Salvestan...', en: 'Saving...' }),
                created: translation({ et: 'Poliitika loodud!', en: 'Policy created!' }),
                updated: translation({ et: 'Poliitika uuendatud!', en: 'Policy updated!' }),
                deleted: translation({ et: 'Poliitika kustutatud.', en: 'Policy deleted.' }),
                yes: translation({ et: 'Jah', en: 'Yes' }),
                no: translation({ et: 'Ei', en: 'No' }),
                assignedEmployees: translation({
                    et: 'Määratud töötajad',
                    en: 'Assigned Employees',
                }),
                searchEmployees: translation({
                    et: 'Otsi töötajaid...',
                    en: 'Search employees...',
                }),
                selectedCount: (n: number) =>
                    translation({
                        et: `${n} töötaja${n === 1 ? '' : 't'} valitud`,
                        en: `${n} employee${n === 1 ? '' : 's'} selected`,
                    }),
                noEmployeesFound: translation({
                    et: 'Töötajaid ei leitud',
                    en: 'No employees found',
                }),
                noEmployeesAssigned: translation({
                    et: 'Töötajaid pole veel määratud',
                    en: 'No employees assigned yet',
                }),
                selectAll: translation({ et: 'Vali kõik', en: 'Select all' }),
                clearAll: translation({ et: 'Eemalda kõik', en: 'Remove all' }),
                available: translation({ et: 'Saadaval', en: 'Available' }),
                // TASK-307: Leave policy bundles (puhkusepoliitikad)
                bundles: {
                    title: translation({ et: 'Puhkusepoliitikad', en: 'Leave policies' }),
                    description: translation({
                        et: 'Poliitika on kimp eemaloleku tüüpe, mis määratakse töötajale rolli ja staaži järgi.',
                        en: 'A policy is a set of time-away types assigned to an employee by role and tenure.',
                    }),
                    createBundle: translation({ et: 'Loo poliitika', en: 'Create policy' }),
                    editBundle: translation({ et: 'Muuda poliitikat', en: 'Edit policy' }),
                    name: translation({ et: 'Nimi', en: 'Name' }),
                    types: translation({ et: 'Tüübid', en: 'Types' }),
                    typesCountSuffix: translation({ et: 'tüüpi', en: 'types' }),
                    employeesCountSuffix: translation({ et: 'töötajat', en: 'employees' }),
                    members: translation({ et: 'Liikmed', en: 'Members' }),
                    noBundles: translation({
                        et: 'Poliitikaid pole veel loodud.',
                        en: 'No policies created yet.',
                    }),
                    confirmDelete: translation({
                        et: 'Kas oled kindel, et soovid selle poliitika kustutada?',
                        en: 'Are you sure you want to delete this policy?',
                    }),
                    typeConfigsTitle: translation({ et: 'Tüüpide seaded', en: 'Type settings' }),
                    typeConfigsDescription: translation({
                        et: 'Iga eemaloleku tüübi limiidid ja reeglid (kehtivad kõigis poliitikates).',
                        en: 'Limits and rules for each time-away type (apply across all policies).',
                    }),
                },
            },
            // M9.4: Holidays
            holidays: {
                title: translation({ et: 'Riiklikud pühad', en: 'Public Holidays' }),
                year: translation({ et: 'Aasta', en: 'Year' }),
                company: translation({ et: 'Ettevõte', en: 'Company' }),
                addHoliday: translation({ et: 'Lisa püha', en: 'Add Holiday' }),
                date: translation({ et: 'Kuupäev', en: 'Date' }),
                name: translation({ et: 'Nimi', en: 'Name' }),
                editHoliday: translation({ et: 'Muuda', en: 'Edit' }),
                deleteHoliday: translation({ et: 'Kustuta', en: 'Delete' }),
                noHolidays: translation({
                    et: 'Pühasid pole sel aastal lisatud.',
                    en: 'No holidays added for this year.',
                }),
                duplicateDate: translation({
                    et: 'Sellel kuupäeval on püha juba olemas.',
                    en: 'A holiday already exists on this date.',
                }),
                added: translation({ et: 'Püha lisatud!', en: 'Holiday added!' }),
                deleted: translation({ et: 'Püha kustutatud.', en: 'Holiday deleted.' }),
                save: translation({ et: 'Lisa', en: 'Add' }),
            },
            // M9.5: Custom Fields
            customFields: {
                title: translation({ et: 'Kohandatavad väljad', en: 'Custom Fields' }),
                createField: translation({ et: 'Loo väli', en: 'Create Field' }),
                fieldName: translation({ et: 'Välja nimi', en: 'Field Name' }),
                fieldKey: translation({ et: 'Välja võti', en: 'Field Key' }),
                fieldType: translation({ et: 'Tüüp', en: 'Type' }),
                visibility: translation({ et: 'Nähtavus', en: 'Visibility' }),
                options: translation({ et: 'Valikud', en: 'Options' }),
                optionsHint: translation({
                    et: 'Komadega eraldatud valikud (ainult tüübi "select" puhul)',
                    en: 'Comma-separated options (only for "select" type)',
                }),
                typeText: translation({ et: 'Tekst', en: 'Text' }),
                typeTextarea: translation({ et: 'Pikk tekst', en: 'Long text' }),
                typeNumber: translation({ et: 'Arv', en: 'Number' }),
                typeDate: translation({ et: 'Kuupäev', en: 'Date' }),
                typeSelect: translation({ et: 'Valik', en: 'Select' }),
                visPublic: translation({ et: 'Avalik', en: 'Public' }),
                visManager: translation({ et: 'Juht', en: 'Manager' }),
                visPrivate: translation({ et: 'Privaatne', en: 'Private' }),
                editField: translation({ et: 'Muuda', en: 'Edit' }),
                deleteField: translation({ et: 'Kustuta', en: 'Delete' }),
                confirmDelete: translation({
                    et: 'Kas oled kindel? Kõik selle välja väärtused kustutatakse.',
                    en: 'Are you sure? All values for this field will be deleted.',
                }),
                noFields: translation({
                    et: 'Kohandatavaid välju pole veel loodud.',
                    en: 'No custom fields created yet.',
                }),
                save: translation({ et: 'Salvesta', en: 'Save' }),
                saving: translation({ et: 'Salvestan...', en: 'Saving...' }),
                created: translation({ et: 'Väli loodud!', en: 'Field created!' }),
                updated: translation({ et: 'Väli uuendatud!', en: 'Field updated!' }),
                deleted: translation({ et: 'Väli kustutatud.', en: 'Field deleted.' }),
            },
            // M10.2: Locations
            locations: {
                title: translation({ et: 'Asukohad', en: 'Locations' }),
                createLocation: translation({ et: 'Loo asukoht', en: 'Create Location' }),
                editLocation: translation({ et: 'Muuda asukohta', en: 'Edit Location' }),
                name: translation({ et: 'Nimi', en: 'Name' }),
                countryCode: translation({ et: 'Riigikood', en: 'Country Code' }),
                countryCodeHint: translation({
                    et: 'Kahetäheline ISO kood (nt EE, FI, US)',
                    en: 'Two-letter ISO code (e.g. EE, FI, US)',
                }),
                employees: translation({ et: 'Töötajaid', en: 'Employees' }),
                edit: translation({ et: 'Muuda', en: 'Edit' }),
                delete: translation({ et: 'Kustuta', en: 'Delete' }),
                save: translation({ et: 'Salvesta', en: 'Save' }),
                saving: translation({ et: 'Salvestan...', en: 'Saving...' }),
                cannotDelete: translation({
                    et: 'Asukoht on töötajate juures kasutusel, ei saa kustutada.',
                    en: 'Location is referenced by employees and cannot be deleted.',
                }),
                noLocations: translation({
                    et: 'Asukohti pole veel loodud.',
                    en: 'No locations created yet.',
                }),
                created: translation({ et: 'Asukoht loodud!', en: 'Location created!' }),
                updated: translation({ et: 'Asukoht uuendatud!', en: 'Location updated!' }),
                deleted: translation({ et: 'Asukoht kustutatud.', en: 'Location deleted.' }),
            },
            // M10.1: Teams
            teams: {
                title: translation({ et: 'Tiimid', en: 'Teams' }),
                createTeam: translation({ et: 'Loo tiim', en: 'Create Team' }),
                editTeam: translation({ et: 'Muuda tiimi', en: 'Edit Team' }),
                name: translation({ et: 'Nimi', en: 'Name' }),
                company: translation({ et: 'Ettevõte', en: 'Company' }),
                members: translation({ et: 'Liikmed', en: 'Members' }),
                memberCount: translation({ et: 'Liikmeid', en: 'Members' }),
                addMember: translation({ et: 'Lisa liige', en: 'Add Member' }),
                removeMember: translation({ et: 'Eemalda', en: 'Remove' }),
                companyChangeBlocked: translation({
                    et: 'Tiimi ei saa teise ettevõtte alla liigutada, kui sellel on aktiivseid liikmeid.',
                    en: 'Team cannot be moved to a different company while it has active members.',
                }),
                cannotDelete: translation({
                    et: 'Tiimil on liikmeid (praeguseid või ajaloolisi), ei saa kustutada.',
                    en: 'Team has members (current or historical) and cannot be deleted.',
                }),
                noTeams: translation({
                    et: 'Tiime pole veel loodud.',
                    en: 'No teams created yet.',
                }),
                edit: translation({ et: 'Muuda', en: 'Edit' }),
                delete: translation({ et: 'Kustuta', en: 'Delete' }),
                save: translation({ et: 'Salvesta', en: 'Save' }),
                saving: translation({ et: 'Salvestan...', en: 'Saving...' }),
                created: translation({ et: 'Tiim loodud!', en: 'Team created!' }),
                updated: translation({ et: 'Tiim uuendatud!', en: 'Team updated!' }),
                deleted: translation({ et: 'Tiim kustutatud.', en: 'Team deleted.' }),
                filterAllCompanies: translation({
                    et: 'Kõik ettevõtted',
                    en: 'All companies',
                }),
            },
            // M10.3: Leave Types
            leaveTypes: {
                title: translation({ et: 'Eemaloleku tüübid', en: 'Leave Types' }),
                createType: translation({ et: 'Loo tüüp', en: 'Create Type' }),
                editType: translation({ et: 'Muuda tüüpi', en: 'Edit Type' }),
                name: translation({ et: 'Nimi', en: 'Name' }),
                slug: translation({ et: 'Lühikood', en: 'Slug' }),
                slugHint: translation({
                    et: 'Lühike identifikaator (auto-genereeritud nimest)',
                    en: 'Short identifier (auto-generated from name)',
                }),
                badgeType: translation({ et: 'Märgise värv', en: 'Badge Color' }),
                customColor: translation({ et: 'Kohandatud', en: 'Custom' }),
                preview: translation({ et: 'Eelvaade', en: 'Preview' }),
                isPaid: translation({ et: 'Tasustatud', en: 'Paid' }),
                accruesLeave: translation({
                    et: 'Koguneb puhkust',
                    en: 'Accrues Leave',
                }),
                accruesLeaveHint: translation({
                    et: 'Kas selle eemaloleku ajal kogub töötaja põhipuhkust juurde',
                    en: 'Whether the employee accrues vacation during this absence',
                }),
                category: translation({ et: 'Kategooria', en: 'Category' }),
                categoryWorking: translation({ et: 'Töine tegevus', en: 'Work activity' }),
                categoryTimeOff: translation({ et: 'Puhkus / eemalolek', en: 'Time off' }),
                categoryHint: translation({
                    et: 'Töised tegevused (nt arengupäev, üritused) vähendavad BalanceTime’is oodatavaid töötunde ega lähe palgaraportisse puhkusepäevadena; Forecasti sünkroonitakse ainult puhkused.',
                    en: 'Work activities (e.g. development day, events) reduce expected hours in BalanceTime and are not reported as leave days in the payroll report; only time off is synced to Forecast.',
                }),
                policies: translation({ et: 'Poliitikaid', en: 'Policies' }),
                cannotDeleteInUse: translation({
                    et: 'Tüüp on poliitikates kasutusel, ei saa kustutada.',
                    en: 'Type is used by one or more policies and cannot be deleted.',
                }),
                noTypes: translation({
                    et: 'Tüüpe pole veel loodud.',
                    en: 'No types created yet.',
                }),
                dragToReorder: translation({
                    et: 'Lohista järjekorra muutmiseks',
                    en: 'Drag to reorder',
                }),
                orderUpdated: translation({ et: 'Järjekord salvestatud.', en: 'Order saved.' }),
                edit: translation({ et: 'Muuda', en: 'Edit' }),
                delete: translation({ et: 'Kustuta', en: 'Delete' }),
                save: translation({ et: 'Salvesta', en: 'Save' }),
                saving: translation({ et: 'Salvestan...', en: 'Saving...' }),
                created: translation({ et: 'Tüüp loodud!', en: 'Type created!' }),
                updated: translation({ et: 'Tüüp uuendatud!', en: 'Type updated!' }),
                deleted: translation({ et: 'Tüüp kustutatud.', en: 'Type deleted.' }),
                yes: translation({ et: 'Jah', en: 'Yes' }),
                no: translation({ et: 'Ei', en: 'No' }),
                // Short "days" unit after the annual limit, e.g. "28 p" / "28 d"
                daysShort: translation({ et: 'p', en: 'd' }),
                // Display labels of the LEAVE_BADGE_TYPES vocabulary
                badgeTypes: {
                    vacation: translation({ et: 'Puhkus', en: 'Vacation' }),
                    sick: translation({ et: 'Haigus', en: 'Sick leave' }),
                    child: translation({ et: 'Lapsepuhkus', en: 'Child leave' }),
                    paternity: translation({ et: 'Isapuhkus', en: 'Paternity leave' }),
                    maternity: translation({ et: 'Emapuhkus', en: 'Maternity leave' }),
                    unpaid: translation({ et: 'Palgata puhkus', en: 'Unpaid leave' }),
                    study: translation({ et: 'Õppepuhkus', en: 'Study leave' }),
                    training: translation({ et: 'Koolitus', en: 'Training' }),
                    development: translation({ et: 'Arengupäev', en: 'Development day' }),
                    health: translation({ et: 'Tervisepäev', en: 'Health day' }),
                    personal: translation({ et: 'Isiklik', en: 'Personal' }),
                    bereavement: translation({ et: 'Leinapuhkus', en: 'Bereavement' }),
                    care: translation({ et: 'Hoolduspuhkus', en: 'Care leave' }),
                    military: translation({ et: 'Kaitseväeteenistus', en: 'Military service' }),
                    weekoff: translation({ et: 'Vaba nädal', en: 'Week off' }),
                    client_event: translation({ et: 'Kliendiüritus', en: 'Client event' }),
                    internal_event: translation({ et: 'Siseüritus', en: 'Internal event' }),
                    remote: translation({ et: 'Kaugtöö', en: 'Remote work' }),
                    other: translation({ et: 'Muu', en: 'Other' }),
                } as Record<string, Translation | undefined>,
            },
            // Compensation Types (Lisatasud line-item picker)
            compensationTypes: {
                title: translation({ et: 'Lisatasude tüübid', en: 'Compensation Types' }),
                createType: translation({ et: 'Loo tüüp', en: 'Create Type' }),
                editType: translation({ et: 'Muuda tüüpi', en: 'Edit Type' }),
                name: translation({ et: 'Nimi', en: 'Name' }),
                usage: translation({ et: 'Kasutusel kirjetes', en: 'Used in entries' }),
                archived: translation({ et: 'Arhiveeritud', en: 'Archived' }),
                showArchived: translation({ et: 'Näita arhiveerituid', en: 'Show archived' }),
                archive: translation({ et: 'Arhiveeri', en: 'Archive' }),
                restore: translation({ et: 'Taasta', en: 'Restore' }),
                imported: translation({ et: 'Imporditud Humaansist', en: 'Imported from Humaans' }),
                noTypes: translation({
                    et: 'Tüüpe pole veel loodud.',
                    en: 'No types created yet.',
                }),
                edit: translation({ et: 'Muuda', en: 'Edit' }),
                save: translation({ et: 'Salvesta', en: 'Save' }),
                saving: translation({ et: 'Salvestan...', en: 'Saving...' }),
                created: translation({ et: 'Tüüp loodud!', en: 'Type created!' }),
                updated: translation({ et: 'Tüüp uuendatud!', en: 'Type updated!' }),
                archived_msg: translation({ et: 'Tüüp arhiveeritud.', en: 'Type archived.' }),
                restored: translation({ et: 'Tüüp taastatud.', en: 'Type restored.' }),
            },
            // M10.4: Working Patterns
            workingPatterns: {
                title: translation({ et: 'Töögraafikud', en: 'Working Patterns' }),
                createPattern: translation({ et: 'Loo muster', en: 'Create Pattern' }),
                editPattern: translation({ et: 'Muuda mustrit', en: 'Edit Pattern' }),
                name: translation({ et: 'Nimi', en: 'Name' }),
                summary: translation({ et: 'Lühikirjeldus', en: 'Summary' }),
                weeklySchedule: translation({ et: 'Nädala graafik', en: 'Weekly Schedule' }),
                weeklyScheduleHint: translation({
                    et: 'Töötunnid päevas (0–24)',
                    en: 'Working hours per day (0–24)',
                }),
                standardDayHours: translation({
                    et: 'Päeva standardtunnid',
                    en: 'Standard Day Hours',
                }),
                fullTimeHours: translation({
                    et: 'Täiskoormuse tunnid nädalas',
                    en: 'Full-time Hours per Week',
                }),
                workingHours: translation({
                    et: 'Töötunnid nädalas',
                    en: 'Working Hours per Week',
                }),
                workingHoursAuto: translation({
                    et: 'Arvutatakse graafikust automaatselt',
                    en: 'Calculated automatically from schedule',
                }),
                fte: translation({ et: 'FTE', en: 'FTE' }),
                fteAuto: translation({
                    et: 'Arvutatakse: töötunnid / täiskoormus',
                    en: 'Computed: working hours / full-time',
                }),
                fteOverride: translation({ et: 'Määra FTE käsitsi', en: 'Set FTE manually' }),
                allocationsCount: translation({
                    et: 'Aktiivseid määranguid',
                    en: 'Active allocations',
                }),
                archived: translation({ et: 'Arhiveeritud', en: 'Archived' }),
                showArchived: translation({
                    et: 'Näita arhiveerituid',
                    en: 'Show archived',
                }),
                archive: translation({ et: 'Arhiveeri', en: 'Archive' }),
                restore: translation({ et: 'Taasta', en: 'Restore' }),
                imported: translation({
                    et: 'Imporditud Humaansist',
                    en: 'Imported from Humaans',
                }),
                noPatterns: translation({
                    et: 'Mustreid pole veel loodud.',
                    en: 'No patterns created yet.',
                }),
                edit: translation({ et: 'Muuda', en: 'Edit' }),
                save: translation({ et: 'Salvesta', en: 'Save' }),
                saving: translation({ et: 'Salvestan...', en: 'Saving...' }),
                created: translation({ et: 'Muster loodud!', en: 'Pattern created!' }),
                updated: translation({ et: 'Muster uuendatud!', en: 'Pattern updated!' }),
                archived_msg: translation({
                    et: 'Muster arhiveeritud.',
                    en: 'Pattern archived.',
                }),
                restored: translation({ et: 'Muster taastatud.', en: 'Pattern restored.' }),
                day: {
                    mon: translation({ et: 'E', en: 'Mon' }),
                    tue: translation({ et: 'T', en: 'Tue' }),
                    wed: translation({ et: 'K', en: 'Wed' }),
                    thu: translation({ et: 'N', en: 'Thu' }),
                    fri: translation({ et: 'R', en: 'Fri' }),
                    sat: translation({ et: 'L', en: 'Sat' }),
                    sun: translation({ et: 'P', en: 'Sun' }),
                },
                assignPattern: translation({
                    et: 'Määra muster',
                    en: 'Assign Pattern',
                }),
                changePattern: translation({
                    et: 'Muuda mustrit',
                    en: 'Change Pattern',
                }),
                pickPattern: translation({ et: 'Vali muster', en: 'Pick Pattern' }),
                modeLabel: translation({ et: 'Mustri tüüp', en: 'Pattern type' }),
                modeExisting: translation({ et: 'Olemasolev muster', en: 'Existing pattern' }),
                modeCustom: translation({ et: 'Kohandatud', en: 'Custom' }),
                modeFixed: translation({ et: 'Fikseeritud tunnid', en: 'Fixed hours' }),
                modeFlex: translation({ et: 'Paindlik', en: 'Flex' }),
                weeklyHours: translation({ et: 'Tunde nädalas', en: 'Hours per week' }),
                ftePreview: (fte: string) =>
                    translation({ et: `Koormus: ${fte} FTE`, en: `Load: ${fte} FTE` }),
                workingSummary: (hours: string, days: string) =>
                    translation({
                        et: `${hours}h/nädalas, ${days} päeva nädalas`,
                        en: `${hours}h/week, ${days} days a week`,
                    }),
                effectiveFrom: translation({
                    et: 'Jõustub alates',
                    en: 'Effective From',
                }),
                effectiveFromHint: translation({
                    et: 'Ainult täna või tulevikus',
                    en: 'Today or in the future only',
                }),
                allocationCreated: translation({
                    et: 'Muster määratud.',
                    en: 'Pattern assigned.',
                }),
                allocationDeleted: translation({
                    et: 'Määrang kustutatud.',
                    en: 'Allocation deleted.',
                }),
            },
        },

        // RBAC Roles configurator
        roles: {
            createRole: translation({ et: 'Loo roll', en: 'Create Role' }),
            noRoles: translation({ et: 'Rolle pole veel loodud.', en: 'No roles created yet.' }),
            typeSystem: translation({ et: 'Süsteemne', en: 'System' }),
            typeCustom: translation({ et: 'Kohandatud', en: 'Custom' }),
            editRole: translation({ et: 'Muuda', en: 'Edit' }),
            memberCount: (n: number) =>
                translation({ et: `${n} liiget`, en: `${n} member${n === 1 ? '' : 's'}` }),
            confirmDelete: (members: number) =>
                translation({
                    et: `Kas kustutada see roll? (${members} liiget)`,
                    en: `Delete this role? (${members} member${members === 1 ? '' : 's'})`,
                }),
            namePlaceholder: translation({ et: 'Rolli nimi', en: 'Role name' }),
            descriptionPlaceholder: translation({
                et: 'Rolli kirjeldus (valikuline)...',
                en: 'Role description (optional)...',
            }),
            unsavedWarning: translation({
                et: 'Salvestamata muudatused lähevad kaotsi. Kas jätkata?',
                en: 'Unsaved changes will be lost. Continue?',
            }),
            selectAll: translation({ et: 'Vali kõik', en: 'Select all' }),
            colView: translation({ et: 'Vaata', en: 'View' }),
            colEdit: translation({ et: 'Muuda', en: 'Edit' }),
            // "Muuda enda": edit on the holder's own profile only, scope-independent.
            colEditOwn: translation({ et: 'Muuda enda', en: 'Edit own' }),
            colManage: translation({ et: 'Halda', en: 'Manage' }),
            // Sections
            membersSection: translation({ et: 'Liikmed', en: 'Members' }),
            scopeSection: translation({ et: 'Ulatus', en: 'Scope' }),
            workspaceSection: translation({ et: 'Tööruumi õigused', en: 'Workspace Permissions' }),
            profileSection: translation({ et: 'Profiili õigused', en: 'Profile Permissions' }),
            adminSection: translation({ et: 'Admin õigused', en: 'Admin Permissions' }),
            fieldsSection: translation({ et: 'Profiiliväljad', en: 'Profile Fields' }),
            customFieldsSection: translation({
                et: 'Kohandatavad väljad',
                en: 'Custom Fields',
            }),
            docTypesSection: translation({ et: 'Dokumenditüübid', en: 'Document Types' }),
            leaveTypesSection: translation({ et: 'Eemaloleku tüübid', en: 'Leave Types' }),
            timeAwaySection: translation({ et: 'Eemalolekud', en: 'Time Away' }),
            timeAwayView: translation({
                et: 'Näeb eemalolekute üksikasju',
                en: 'Can view time-off details',
            }),
            timeAwayManage: translation({
                et: 'Saab hallata eemalolekuid',
                en: 'Can manage time-off requests',
            }),
            addMemberPlaceholder: translation({
                et: 'Otsi ja lisa liikmeid...',
                en: 'Search and add members...',
            }),
            managerAutoBanner: (count: number) =>
                translation({
                    et: `Juhtidele omistatakse automaatselt (${count} liiget). Saab ka käsitsi lisada.`,
                    en: `Auto-assigned to managers (${count} member${count === 1 ? '' : 's'}). Manual additions allowed.`,
                }),
            userAutoBanner: translation({
                et: 'Kasutaja roll on automaatselt omistatud kõikidele aktiivsetele töötajatele.',
                en: 'User role is auto-assigned to all active employees.',
            }),
            // Company scope for a member grant
            companyScopeLabel: translation({ et: 'Ettevõte', en: 'Company' }),
            companyAll: translation({ et: 'Kõik ettevõtted', en: 'All companies' }),
            // Scope options
            scopeOption: (scope: string) => {
                const labels: Record<string, { et: string; en: string }> = {
                    own: { et: 'Ainult enda andmed', en: 'Own data only' },
                    reports: { et: 'Otsealluvad', en: 'Direct reports' },
                    group: { et: 'Grupi liikmed', en: 'Group members' },
                    everyone: { et: 'Kõik töötajad', en: 'Everyone' },
                }
                const l = labels[scope] ?? { et: scope, en: scope }
                return translation(l)
            },
            // Workspace permission labels
            workspacePermLabels: {
                people_directory: translation({ et: 'Töötajate kataloog', en: 'People directory' }),
                org_chart: translation({ et: 'Organisatsiooni struktuur', en: 'Org chart' }),
                everyone_birthday: translation({ et: 'Kõigi sünnipäevad', en: 'All birthdays' }),
                employment_start_date: translation({
                    et: 'Tööle asumise kuupäev',
                    en: 'Employment start date',
                }),
                access_reports: translation({ et: 'Vaata raporteid', en: 'Access reports' }),
                create_reports: translation({ et: 'Loo raporteid', en: 'Create reports' }),
                access_shared_reports: translation({
                    et: 'Jagatud raportid',
                    en: 'Access shared reports',
                }),
                create_shared_reports: translation({
                    et: 'Loo jagatud raporteid',
                    en: 'Create shared reports',
                }),
                view_worktime: translation({
                    et: 'Teiste tööaeg',
                    en: "Others' worktime",
                }),
                view_projects: translation({
                    et: 'Teiste projektid',
                    en: "Others' projects",
                }),
            },
            // Profile permission labels
            profilePermLabels: {
                see_new_hires: translation({ et: 'Näeb uusi töötajaid', en: 'See new hires' }),
                delete_new_hires: translation({
                    et: 'Kustuta uusi töötajaid',
                    en: 'Delete new hires',
                }),
                add_members: translation({ et: 'Lisa liikmeid', en: 'Add members' }),
                send_invites: translation({ et: 'Saada kutseid', en: 'Send invites' }),
                see_offboarded_users: translation({
                    et: 'Näeb lahkunuid',
                    en: 'See offboarded users',
                }),
                offboard_users: translation({
                    et: 'Töölt vabasta',
                    en: 'Offboard users',
                }),
                delete_offboarded_users: translation({
                    et: 'Kustuta lahkunuid',
                    en: 'Delete offboarded users',
                }),
            },
            // Admin permission labels
            adminPermLabels: {
                upload_company_documents: translation({
                    et: 'Laadi ettevõtte dokumente',
                    en: 'Upload company documents',
                }),
                manage_document_types: translation({
                    et: 'Halda dokumenditüüpe',
                    en: 'Manage document types',
                }),
                manage_custom_fields: translation({
                    et: 'Halda kohandatud välju',
                    en: 'Manage custom fields',
                }),
                manage_time_away_settings: translation({
                    et: 'Halda eemalolekute seadeid',
                    en: 'Manage time-away settings',
                }),
                manage_roles: translation({
                    et: 'Halda rolle',
                    en: 'Manage roles',
                }),
                manage_organisation_settings: translation({
                    et: 'Halda organisatsiooni seadeid',
                    en: 'Manage organisation settings',
                }),
                manage_daily_digest: translation({
                    et: 'Halda päevakokkuvõtet',
                    en: 'Manage daily digest',
                }),
                view_audit_log: translation({
                    et: 'Näeb auditilogi',
                    en: 'View audit log',
                }),
                view_error_log: translation({
                    et: 'Näeb vealogi',
                    en: 'View error log',
                }),
                view_as: translation({
                    et: 'Saab vaadata portaali teise kasutajana',
                    en: 'View the portal as another user',
                }),
            },
            // Document type labels
            docTypeLabels: {
                arenguvestlus: translation({ et: 'Arenguvestlus', en: 'Development talk' }),
                bonus: translation({ et: 'Boonus', en: 'Bonus' }),
                commission: translation({ et: 'Komisjonitasu', en: 'Commission' }),
                cv: translation({ et: 'CV', en: 'CV' }),
                employment_agreement: translation({ et: 'Tööleping', en: 'Employment agreement' }),
                exit_interview: translation({ et: 'Lahkumisintervjuu', en: 'Exit interview' }),
                other: translation({ et: 'Muu', en: 'Other' }),
                praktikaleping: translation({ et: 'Praktikaleping', en: 'Internship agreement' }),
                praktika_retro: translation({ et: 'Praktika retro', en: 'Internship retro' }),
                rahulolu_uuring: translation({ et: 'Rahulolu uuring', en: 'Satisfaction survey' }),
                share_options: translation({ et: 'Osalus', en: 'Share options' }),
                tiimijuhi_markmed: translation({ et: 'Tiimijuhi märkmed', en: 'Team lead notes' }),
                tooelulugu: translation({ et: 'Tööelulugu', en: 'Career history' }),
                toolepingu_lisa: translation({ et: 'Töölepingu lisa', en: 'Contract amendment' }),
                volikiri: translation({ et: 'Volikiri', en: 'Power of attorney' }),
            },
        },
    },
}

/**
 * Translators for portal API error codes. Each code maps to a function from its
 * params to a Translation. `satisfies` forces exactly one entry per code with
 * correctly-typed params — adding a code to PortalApiErrorParams without a
 * translation here (or with wrong param names) is a compile error.
 */
type ApiErrorTranslators = {
    [C in PortalApiErrorCode]: (p: PortalApiErrorParams[C]) => Translation
}

const portalApiErrorTranslators = {
    // ── Generic auth / permissions ──
    NOT_AUTHORIZED: () => translation({ et: 'Pole lubatud', en: 'Not authorized' }),
    INSUFFICIENT_PORTAL_PERMISSIONS: () =>
        translation({ et: 'Ebapiisavad portaali õigused', en: 'Insufficient Portal permissions' }),
    UNAUTHORIZED: () => translation({ et: 'Sisselogimine on vajalik', en: 'Please sign in' }),
    SESSION_EXPIRED: () =>
        translation({
            et: 'Seanss on aegunud, logi uuesti sisse',
            en: 'Your session has expired, please sign in again',
        }),
    FORBIDDEN: () =>
        translation({
            et: 'Sul puudub selleks õigus',
            en: 'You do not have permission to do this',
        }),

    // ── Generic HTTP-level failures ──
    INTERNAL_ERROR: () =>
        translation({
            et: 'Serveris tekkis viga, proovi hiljem uuesti',
            en: 'Something went wrong on the server, please try again later',
        }),
    RATE_LIMIT_EXCEEDED: () =>
        translation({
            et: 'Liiga palju päringuid, proovi hetke pärast uuesti',
            en: 'Too many requests, please try again in a moment',
        }),
    INVALID_DATA: (p) =>
        translation({
            et: p.detail ? `Vigased andmed: ${p.detail}` : 'Vigased andmed',
            en: p.detail ? `Invalid data: ${p.detail}` : 'Invalid data',
        }),
    NOT_FOUND: () => translation({ et: 'Kirjet ei leitud', en: 'Not found' }),
    INSUFFICIENT_PERMISSIONS: () =>
        translation({ et: 'Ebapiisavad õigused', en: 'Insufficient permissions' }),
    ADMIN_REQUIRED: () =>
        translation({ et: 'Vajalik on administraatori roll', en: 'Admin role required' }),
    ROLE_NOT_FOUND: () => translation({ et: 'Rolli ei leitud', en: 'Role not found' }),
    ADMIN_ROLE_LOCKED: () =>
        translation({ et: 'Administraatori roll on lukustatud', en: 'Admin role is locked' }),
    CANNOT_MODIFY_SYSTEM_ROLE: () =>
        translation({
            et: 'Süsteemset rolli ei saa ümber nimetada ega selle ulatust muuta',
            en: 'Cannot rename or rescope a system role',
        }),
    CANNOT_DELETE_SYSTEM_ROLE: () =>
        translation({
            et: 'Süsteemset rolli ei saa kustutada',
            en: 'Cannot delete a system role',
        }),
    CANNOT_REMOVE_USER_ROLE_MEMBER: () =>
        translation({
            et: 'Kasutaja süsteemsest rollist ei saa liiget eemaldada',
            en: 'Cannot remove a member from the User system role',
        }),
    LAST_ADMIN_ROLE: () =>
        translation({
            et: 'Ei saa kustutada: süsteemi ei jääks ühtegi administraatorit',
            en: 'Cannot delete: the system would be left without an administrator',
        }),
    LAST_ADMIN_PERMISSION: () =>
        translation({
            et: 'Ei saa eemaldada rollide haldamise õigust: süsteemi ei jääks ühtegi administraatorit',
            en: 'Cannot remove the role-management permission: the system would be left without an administrator',
        }),
    LAST_ADMIN_MEMBER: () =>
        translation({
            et: 'Ei saa eemaldada: süsteemi ei jääks ühtegi administraatorit',
            en: 'Cannot remove: the system would be left without an administrator',
        }),
    LOCATION_IN_USE: () =>
        translation({
            et: 'Asukoht on seotud töösuhte kirjetega ja seda ei saa kustutada',
            en: 'Location is referenced by employment records and cannot be deleted',
        }),
    TEAM_HAS_ACTIVE_MEMBERS: () =>
        translation({
            et: 'Tiimil on aktiivseid liikmeid ja seda ei saa teise ettevõttesse liigutada. Eemalda esmalt kõik liikmed.',
            en: 'Team has active members and cannot be moved to a different company. Remove all members first.',
        }),
    TEAM_HAS_MEMBERS: () =>
        translation({
            et: 'Tiimil on liikmeid (praegu või varem) ja seda ei saa kustutada. Eemalda esmalt kõik liikmed.',
            en: 'Team has members (current or historical) and cannot be deleted. Remove all members first.',
        }),
    LEAVE_TYPE_IN_USE: () =>
        translation({
            et: 'Eemaloleku tüüp on kasutusel ühes või mitmes poliitikas. Kustuta esmalt need.',
            en: 'Leave type is used by one or more policies. Delete those first.',
        }),
    DRIVE_NOT_CONNECTED: () =>
        translation({ et: 'Drive pole ühendatud', en: 'Drive not connected' }),

    // ── Time-off: booking / edit / cancel / approval ──
    INSUFFICIENT_BALANCE: (p) =>
        translation({
            et: `Ebapiisav saldo: saadaval ${p.available} päeva, taotletud ${p.requested}`,
            en: `Insufficient balance: ${p.available} day(s) available, ${p.requested} requested`,
        }),
    START_AFTER_END: () =>
        translation({
            et: 'Alguskuupäev peab olema enne lõppkuupäeva',
            en: 'Start date must be before end date',
        }),
    OVERLAPPING_REQUEST: () =>
        translation({
            et: 'Olemasolev eemaloleku taotlus kattub valitud kuupäevadega',
            en: 'An existing leave request overlaps with the selected dates',
        }),
    MIN_NOTICE_DAYS: (p) =>
        translation({
            et: `See poliitika nõuab vähemalt ${p.days} päeva etteteatamist`,
            en: `This policy requires at least ${p.days} days advance notice`,
        }),
    MIN_DURATION_DAYS: (p) =>
        translation({
            et: `Minimaalne kestus on ${p.days} päeva`,
            en: `Minimum duration is ${p.days} day(s)`,
        }),
    MAX_DURATION_DAYS: (p) =>
        translation({
            et: `Maksimaalne kestus korraga on ${p.days} päeva`,
            en: `Maximum duration is ${p.days} day(s)`,
        }),
    FULL_WEEK_REQUIRED: () =>
        translation({
            et: 'Seda eemaloleku tüüpi tuleb võtta terve nädalana (7 päeva)',
            en: 'This leave type must be taken as a full week (7 days)',
        }),
    NO_LEAVE_DAYS: () =>
        translation({
            et: 'Valitud kuupäevad ei sisalda ühtegi eemaloleku päeva',
            en: 'Selected dates contain no leave days',
        }),
    MISSING_REQUIRED_FIELDS: () =>
        translation({ et: 'Kohustuslikud väljad on täitmata', en: 'Missing required fields' }),
    NOT_ASSIGNED_TO_POLICY: () =>
        translation({
            et: 'Töötaja ei ole sellele poliitikale määratud',
            en: 'Employee is not assigned to this policy',
        }),
    B2B_NOT_ELIGIBLE: () =>
        translation({
            et: 'B2B töötajatel ei ole õigust põhipuhkusele',
            en: 'B2B employees are not eligible for annual leave (põhipuhkus)',
        }),
    INTERNS_NOT_ELIGIBLE: () =>
        translation({
            et: 'Praktikantidel ei ole õigust sellele eemaloleku tüübile',
            en: 'Interns are not eligible for this leave type',
        }),
    NOT_AUTHORIZED_CREATE_REQUEST: () =>
        translation({
            et: 'Pole õigust luua taotlusi selle töötaja eest',
            en: 'Not authorized to create requests for this employee',
        }),
    NOT_AUTHORIZED_CANCEL_REQUEST: () =>
        translation({
            et: 'Pole õigust seda taotlust tühistada',
            en: 'Not authorized to cancel this request',
        }),
    NOT_AUTHORIZED_EDIT_REQUEST: () =>
        translation({
            et: 'Pole õigust seda taotlust muuta',
            en: 'Not authorized to edit this request',
        }),
    NOT_AUTHORIZED_DECIDE_REQUEST: () =>
        translation({
            et: 'Pole õigust seda taotlust kinnitada ega tagasi lükata',
            en: 'Not authorized to approve or reject this request',
        }),
    CANNOT_DECIDE_OWN_REQUEST: () =>
        translation({
            et: 'Sa ei saa oma taotlust kinnitada ega tagasi lükata',
            en: 'You cannot approve or reject your own request',
        }),
    REQUEST_CANNOT_BE_CANCELLED: () =>
        translation({
            et: 'Seda taotlust ei saa enam tühistada',
            en: 'This request can no longer be cancelled',
        }),
    CANCEL_ALREADY_REQUESTED: () =>
        translation({
            et: 'Kustutamissoov on juba juhile kinnitamiseks saadetud',
            en: 'A cancellation request is already awaiting approval',
        }),
    CANCEL_NOT_REQUESTED: () =>
        translation({
            et: 'Sellel eemalolekul pole ootel kustutamissoovi',
            en: 'This request has no pending cancellation request',
        }),
    ONLY_PENDING_UPDATABLE: () =>
        translation({
            et: 'Uuendada saab ainult ootel taotlusi',
            en: 'Only pending requests can be updated',
        }),
    ONLY_PENDING_OR_APPROVED_EDITABLE: () =>
        translation({
            et: 'Muuta saab ainult ootel või kinnitatud taotlusi',
            en: 'Only pending or approved requests can be edited',
        }),
    INVALID_ACTION: () => translation({ et: 'Vigane toiming', en: 'Invalid action' }),
    INVALID_START_DATE_FORMAT: () =>
        translation({ et: 'Vigane alguskuupäeva vorming', en: 'Invalid start_date format' }),
    INVALID_END_DATE_FORMAT: () =>
        translation({ et: 'Vigane lõppkuupäeva vorming', en: 'Invalid end_date format' }),
    EDIT_REQUEST_FIELDS_REQUIRED: () =>
        translation({
            et: 'Vajalik on vähemalt üks väljadest: alguskuupäev, lõppkuupäev või märkus',
            en: 'At least one of start_date, end_date, or note is required',
        }),
    NO_MAIN_VACATION_POLICY: () =>
        translation({ et: 'Peamine puhkusepoliitika puudub', en: 'No main vacation policy' }),
    ADJUSTMENT_FIELDS_REQUIRED: () =>
        translation({
            et: 'Vajalikud on policy_id ja nullist erinev summa',
            en: 'policy_id and non-zero amount are required',
        }),
    ADJUSTMENT_NOTE_REQUIRED: () =>
        translation({
            et: 'Korrigeerimist selgitav märkus on kohustuslik',
            en: 'A note explaining the adjustment is required',
        }),
    ADJUSTMENT_DATE_INVALID: () =>
        translation({
            et: 'Jõustumise kuupäev on kohustuslik (kujul AAAA-KK-PP)',
            en: 'An effective date is required (YYYY-MM-DD)',
        }),
    ADJUSTMENT_DATE_IN_FUTURE: () =>
        translation({
            et: 'Jõustumise kuupäev ei tohi olla tulevikus',
            en: 'The effective date cannot be in the future',
        }),
    ADJUSTMENT_POLICY_NOT_LIMITED: () =>
        translation({
            et: 'Korrigeerida saab ainult limiidiga eemaloleku tüüpe',
            en: 'Only leave types with a limit can be adjusted',
        }),

    // ── Common not-found / id validation ──
    EMPLOYEE_NOT_FOUND: () => translation({ et: 'Töötajat ei leitud', en: 'Employee not found' }),
    POLICY_NOT_FOUND: () => translation({ et: 'Poliitikat ei leitud', en: 'Policy not found' }),
    REQUEST_NOT_FOUND: () => translation({ et: 'Taotlust ei leitud', en: 'Request not found' }),
    USER_NOT_FOUND: () => translation({ et: 'Kasutajat ei leitud', en: 'User not found' }),
    INVALID_ID: () => translation({ et: 'Vigane ID', en: 'Invalid ID' }),
    INVALID_EMPLOYEE_ID: () => translation({ et: 'Vigane töötaja ID', en: 'Invalid employee ID' }),
    INVALID_EMPLOYEE_OR_DOCUMENT_ID: () =>
        translation({
            et: 'Vigane töötaja või dokumendi ID',
            en: 'Invalid employee or document ID',
        }),
    INVALID_EMPLOYEE_OR_CONTACT_ID: () =>
        translation({ et: 'Vigane töötaja või kontakti ID', en: 'Invalid employee or contact ID' }),
    INVALID_EMPLOYEE_OR_ENTRY_ID: () =>
        translation({ et: 'Vigane töötaja või kirje ID', en: 'Invalid employee or entry ID' }),

    // ── Documents ──
    CANNOT_MANAGE_DOCUMENTS: () =>
        translation({
            et: 'Selle töötaja dokumente ei saa hallata',
            en: 'Cannot manage documents for this employee',
        }),
    DOCUMENT_NOT_FOUND: () => translation({ et: 'Dokumenti ei leitud', en: 'Document not found' }),
    DOCUMENT_FILE_NOT_FOUND: () =>
        translation({ et: 'Dokumendi faili ei leitud', en: 'Document file not found' }),

    // ── Custom fields ──
    NOT_AUTHORIZED_EDIT_FIELD: (p) =>
        translation({
            et: `Pole õigust muuta kohandatud välja ${p.fieldId}`,
            en: `Not authorized to edit custom field ${p.fieldId}`,
        }),
    CANNOT_EDIT_KEY: (p) =>
        translation({ et: `Välja ${p.key} ei saa muuta`, en: `Cannot edit ${p.key}` }),
    VALUES_OBJECT_REQUIRED: () =>
        translation({ et: 'Vajalik on values objekt', en: 'values object is required' }),
    NO_VALID_FIELDS: () =>
        translation({ et: 'Uuendamiseks pole sobivaid välju', en: 'No valid fields to update' }),
    INVALID_IBAN: () =>
        translation({
            et: 'IBAN ei ole korrektne (kontrolli riigikoodi, pikkust ja numbreid)',
            en: 'The IBAN is not valid (check the country code, length and digits)',
        }),
    INVALID_SWIFT: () =>
        translation({
            et: 'SWIFT/BIC ei ole korrektne (8 või 11 märki)',
            en: 'The SWIFT/BIC is not valid (8 or 11 characters)',
        }),

    // ── Employee / account / contacts ──
    USER_ID_REQUIRED: () => translation({ et: 'userId on kohustuslik', en: 'userId is required' }),
    EMPLOYEE_ID_REQUIRED: () =>
        translation({ et: 'employee_id on kohustuslik', en: 'employee_id is required' }),
    EMPLOYEE_IDS_MUST_BE_ARRAY: () =>
        translation({ et: 'employee_ids peab olema massiiv', en: 'employee_ids must be an array' }),
    EFFECTIVE_FROM_INVALID: () =>
        translation({
            et: 'Kehtivuse alguskuupäev peab olema kujul AAAA-KK-PP',
            en: 'effective_from must be a YYYY-MM-DD date',
        }),
    WORK_EMAIL_OR_USER_ID_REQUIRED: () =>
        translation({
            et: 'Konto loomiseks on vajalik work_email või user_id',
            en: 'work_email or user_id is required to create the account',
        }),
    FIRST_LAST_COMPANY_REQUIRED: () =>
        translation({
            et: 'Vajalikud on first_name, last_name ja company_id',
            en: 'first_name, last_name, and company_id are required',
        }),
    EMPLOYEE_ALREADY_STATUS: (p) =>
        translation({ et: `Töötaja on juba ${p.status}`, en: `Employee is already ${p.status}` }),

    // ── View as (impersonation) ──
    ALREADY_IMPERSONATING: () =>
        translation({
            et: 'Vaade teise kasutajana on juba aktiivne',
            en: 'Already viewing as another user',
        }),
    CANNOT_IMPERSONATE_SELF: () =>
        translation({
            et: 'Iseennast ei saa vaadata teise kasutajana',
            en: 'Cannot view as yourself',
        }),
    NO_PORTAL_ACCESS: () =>
        translation({
            et: 'Sellel kasutajal puudub ligipääs portaalile',
            en: 'This user has no portal access',
        }),
    NOT_IMPERSONATING: () =>
        translation({
            et: 'Vaade teise kasutajana ei ole aktiivne',
            en: 'Not currently viewing as another user',
        }),

    // ── My assets (asset-management integration) ──
    ASSETS_NOT_CONFIGURED: () =>
        translation({
            et: 'Varade süsteem ei ole seadistatud',
            en: 'Asset system is not configured',
        }),
    ASSET_TAG_REQUIRED: () => translation({ et: 'Sisesta vara kood', en: 'Asset tag is required' }),
    ASSET_ACCOUNT_NOT_FOUND: () =>
        translation({
            et: 'Sinu e-postiga ei leitud varade süsteemist kontot',
            en: 'No asset-system account matches your email',
        }),
    ASSET_NOT_FOUND: () =>
        translation({ et: 'Selle koodiga vara ei leitud', en: 'No asset with this tag' }),
    ASSET_NOT_AVAILABLE: () =>
        translation({
            et: 'See vara ei ole vaba — keegi on selle juba võtnud',
            en: 'This asset is not available — someone already has it',
        }),
    ASSET_CLAIM_FAILED: () =>
        translation({
            et: 'Vara enda nimele võtmine ebaõnnestus',
            en: 'Failed to claim the asset',
        }),
    TITLE_REQUIRED: () => translation({ et: 'Pealkiri on kohustuslik', en: 'title is required' }),
    NAME_PHONE_REQUIRED: () =>
        translation({ et: 'Vajalikud on nimi ja telefon', en: 'name and phone are required' }),
    PHONE_COUNTRY_CODE_REQUIRED: () =>
        translation({
            et: 'Telefoninumber peab algama suunakoodiga, nt +372 5123 4567',
            en: 'Phone number must start with a country code, e.g. +372 5123 4567',
        }),
    CONTACT_NOT_FOUND: () => translation({ et: 'Kontakti ei leitud', en: 'Contact not found' }),
    SELF_MANAGER: () =>
        translation({
            et: 'Töötaja ei saa olla iseenda juht',
            en: 'An employee cannot be their own manager',
        }),
    INVALID_COLLABORATION_TYPE: () =>
        translation({ et: 'Tundmatu koostöövorm', en: 'Unknown collaboration type' }),

    // ── Field-change requests (work location) ──
    UNKNOWN_LOCATION: () => translation({ et: 'Tundmatu asukoht', en: 'Unknown location' }),
    NO_CHANGE: () =>
        translation({
            et: 'See on juba praegune töökoha asukoht',
            en: 'This is already the current work location',
        }),
    ALREADY_DECIDED: () =>
        translation({
            et: 'Taotlus on juba otsustatud',
            en: 'This request has already been decided',
        }),
    CANNOT_DECIDE_OWN: () =>
        translation({
            et: 'Oma taotlust ei saa ise otsustada',
            en: 'You cannot decide your own request',
        }),

    // ── Kogemused (TASK-300) ──
    EXPERIENCE_NOT_FOUND: () => translation({ et: 'Kirjet ei leitud', en: 'Entry not found' }),
    EXPERIENCE_TYPE_INVALID: () =>
        translation({ et: 'Vigane kogemuse tüüp', en: 'Invalid experience type' }),
    EXPERIENCE_NAME_REQUIRED: () =>
        translation({ et: 'Nimi on kohustuslik', en: 'name is required' }),
    EXPERIENCE_PERIOD_INVALID: () =>
        translation({
            et: 'Periood peab olema kujul AAAA-KK',
            en: 'Period must be in YYYY-MM format',
        }),

    // ── Google Drive ──
    DRIVE_URL_REQUIRED: () =>
        translation({ et: 'drive_url on kohustuslik', en: 'drive_url is required' }),
    INVALID_FOLDER_ID: () => translation({ et: 'Vigane kausta ID', en: 'Invalid folder id' }),
    NO_DRIVE_FOLDER_ACCESS: () =>
        translation({
            et: 'Sul puudub ligipääs sellele Google Drive kaustale',
            en: 'You do not have access to this folder in Google Drive',
        }),
    INVALID_KIND: () => translation({ et: 'Vigane tüüp', en: 'Invalid kind' }),

    // ── Contracts / employment / compensation ──
    CONTRACT_FIELDS_REQUIRED: () =>
        translation({
            et: 'Vajalikud on contract_type ja start_date',
            en: 'contract_type and start_date are required',
        }),
    INVALID_CONTRACT_TYPE: (p) =>
        translation({
            et: `Vigane contract_type. Peab olema üks järgnevatest: ${p.validTypes}`,
            en: `Invalid contract_type. Must be one of: ${p.validTypes}`,
        }),
    FIXED_TERM_REQUIRES_END_DATE: () =>
        translation({
            et: 'Tähtajalised lepingud nõuavad lõppkuupäeva',
            en: 'Fixed-term contracts require an end_date',
        }),
    PROBATION_END_DATE_REQUIRED: () =>
        translation({
            et: 'probation_end_date on kohustuslik',
            en: 'probation_end_date is required',
        }),
    CONTRACT_NOT_FOUND: () => translation({ et: 'Lepingut ei leitud', en: 'Contract not found' }),
    NO_ACTIVE_CONTRACT: () =>
        translation({
            et: 'Sellel töötajal pole aktiivset lepingut',
            en: 'No active contract for this employee',
        }),
    EMPLOYEE_NO_ACTIVE_EMPLOYMENT: () =>
        translation({
            et: 'Töötajal pole aktiivset töösuhte kirjet',
            en: 'Employee has no active employment record',
        }),
    FUTURE_EMPLOYMENT_EXISTS: () =>
        translation({
            et: 'Selle kuupäeva kohta on juba tulevane töösuhte kirje',
            en: 'A future employment record already exists for this date',
        }),
    GROSS_AMOUNT_EFFECTIVE_FROM_REQUIRED: () =>
        translation({
            et: 'Vajalikud on gross_amount ja effective_from',
            en: 'gross_amount and effective_from are required',
        }),
    GROSS_AMOUNT_POSITIVE: () =>
        translation({
            et: 'gross_amount peab olema positiivne',
            en: 'gross_amount must be positive',
        }),
    GROSS_AMOUNT_FULL_FTE_POSITIVE: () =>
        translation({
            et: 'gross_amount_full_fte peab olema positiivne',
            en: 'gross_amount_full_fte must be positive',
        }),
    FTE_RANGE: () =>
        translation({ et: 'FTE peab olema vahemikus 0 kuni 1', en: 'FTE must be between 0 and 1' }),
    EFFECTIVE_FROM_REQUIRED: () =>
        translation({ et: 'effective_from on kohustuslik', en: 'effective_from is required' }),
    EFFECTIVE_FROM_FUTURE: () =>
        translation({
            et: 'effective_from peab olema täna või tulevikus',
            en: 'effective_from must be today or in the future',
        }),

    // ── Compensation (base salary, SCD2) ──
    COMPENSATION_PERIOD_OVERLAP: () =>
        translation({
            et: 'Periood kattub töötaja olemasoleva töötasu kirjega',
            en: 'Effective period overlaps an existing compensation record',
        }),

    // ── Compensation line items (bonus / allowance) ──
    LINE_ITEM_FIELDS_REQUIRED: () =>
        translation({
            et: 'Tüüp, summa, periood ja kehtivuse algus on kohustuslikud',
            en: 'Type, amount, period and effective-from are required',
        }),
    LINE_ITEM_AMOUNT_POSITIVE: () =>
        translation({ et: 'Summa peab olema positiivne', en: 'Amount must be positive' }),
    INVALID_COMPENSATION_TYPE: () =>
        translation({ et: 'Tundmatu lisatasu tüüp', en: 'Unknown compensation type' }),
    LINE_ITEM_NOT_FOUND: () =>
        translation({ et: 'Lisatasu kirjet ei leitud', en: 'Line item not found' }),
    LINE_ITEM_DATE_CONFLICT: () =>
        translation({
            et: 'Selle kategooria lisatasu algab juba sellel või hilisemal kuupäeval — muuda olemasolevat kirjet',
            en: 'A compensation entry in this category already starts on or after this date — edit the existing entry instead',
        }),

    // ── Settings: compensation types ──
    COMPENSATION_TYPE_NAME_REQUIRED: () =>
        translation({ et: 'Nimi on kohustuslik', en: 'Name is required' }),
    INVALID_COMPENSATION_BASE_TYPE: (p) =>
        translation({
            et: `Vigane kategooria. Peab olema üks järgnevatest: ${p.validTypes}`,
            en: `Invalid category. Must be one of: ${p.validTypes}`,
        }),
    COMPENSATION_TYPE_NOT_FOUND: () =>
        translation({ et: 'Lisatasu tüüpi ei leitud', en: 'Compensation type not found' }),

    // ── Working patterns / allocations ──
    WORKING_PATTERN_FIELDS_REQUIRED: () =>
        translation({
            et: 'Vajalikud on working_pattern_id ja effective_from',
            en: 'working_pattern_id and effective_from are required',
        }),
    WORKING_PATTERN_LENGTH: () =>
        translation({
            et: 'working_pattern peab olema 7-elemendiline massiiv',
            en: 'working_pattern must be a length-7 array',
        }),
    WORKING_PATTERN_INVALID: () =>
        translation({
            et: 'Nimi on kohustuslik ja working_pattern peab olema 7-elemendiline massiiv numbritest või tõeväärtustest',
            en: 'name is required and working_pattern must be a length-7 array of numbers or booleans',
        }),
    WORKING_PATTERN_NOT_FOUND: () =>
        translation({ et: 'Töömustrit ei leitud', en: 'Working pattern not found' }),
    CANNOT_ASSIGN_ARCHIVED_PATTERN: () =>
        translation({
            et: 'Arhiveeritud töömustrit ei saa määrata',
            en: 'Cannot assign an archived working pattern',
        }),
    ALLOCATION_NOT_FOUND: () =>
        translation({ et: 'Jaotust ei leitud', en: 'Allocation not found' }),
    ONLY_FUTURE_ALLOCATIONS_DELETABLE: () =>
        translation({
            et: 'Kustutada saab ainult tulevasi jaotusi',
            en: 'Only future allocations can be deleted',
        }),

    // ── Settings: leave types / policies / teams / locations / companies / holidays ──
    LEAVE_TYPE_NOT_FOUND: () =>
        translation({ et: 'Eemaloleku tüüpi ei leitud', en: 'Leave type not found' }),
    LEAVE_TYPE_IDS_MUST_BE_ARRAY: () =>
        translation({ et: 'ids peab olema massiiv', en: 'ids must be an array' }),
    INVALID_LEAVE_CATEGORY: () =>
        translation({ et: 'Tundmatu kategooria', en: 'Unknown category' }),
    SLUG_EXISTS: () =>
        translation({ et: 'Selline slug on juba olemas', en: 'Slug already exists' }),
    NAME_LEAVE_TYPE_REQUIRED: () =>
        translation({
            et: 'Vajalikud on nimi ja leave_type_id',
            en: 'name and leave_type_id are required',
        }),
    NAME_COMPANY_REQUIRED: () =>
        translation({
            et: 'Vajalikud on nimi ja company_id',
            en: 'name and company_id are required',
        }),
    NAME_BADGE_TYPE_REQUIRED: () =>
        translation({
            et: 'Vajalikud on nimi ja badge_type',
            en: 'name and badge_type are required',
        }),
    NAME_REQUIRED: () => translation({ et: 'Nimi on kohustuslik', en: 'name is required' }),
    POLICY_HAS_EMPLOYEES: () =>
        translation({
            et: 'Poliitikal on määratud töötajaid ja seda ei saa kustutada',
            en: 'Policy has assigned employees and cannot be deleted',
        }),
    BUNDLE_NAME_POLICIES_REQUIRED: () =>
        translation({
            et: 'Vajalikud on nimi ja vähemalt üks poliitika',
            en: 'A name and at least one policy are required.',
        }),
    BUNDLE_HAS_EMPLOYEES: () =>
        translation({
            et: 'Komplektil on määratud töötajaid ja seda ei saa kustutada',
            en: 'Bundle has assigned employees and cannot be deleted',
        }),
    DATE_NAME_REQUIRED: () =>
        translation({ et: 'Vajalikud on kuupäev ja nimi', en: 'date and name are required' }),
    HOLIDAY_EXISTS: () =>
        translation({
            et: 'Sellel kuupäeval on juba püha',
            en: 'A holiday already exists on this date',
        }),
    COUNTRY_CODE_FORMAT: () =>
        translation({
            et: 'country_code peab olema ISO 3166-1 alpha-2',
            en: 'country_code must be ISO 3166-1 alpha-2',
        }),

    // ── Reports ──
    REPORT_NOT_FOUND: () => translation({ et: 'Raportit ei leitud', en: 'Report not found' }),
    REPORT_NAME_REQUIRED: () =>
        translation({ et: 'Raporti nimi on kohustuslik', en: 'Report name is required' }),
    UNKNOWN_REPORT: (p) =>
        translation({ et: `Tundmatu raport: ${p.slug}`, en: `Unknown report: ${p.slug}` }),
    AT_LEAST_ONE_COLUMN: () =>
        translation({ et: 'Vajalik on vähemalt üks veerg', en: 'At least one column required' }),
    INVALID_EXPORT_CONFIG: () =>
        translation({ et: 'Vigane ekspordi konfiguratsioon', en: 'Invalid export configuration' }),
    UNSUPPORTED_FORMAT: () => translation({ et: 'Toetamata vorming', en: 'Unsupported format' }),
    INVALID_YEAR_MONTH: () =>
        translation({ et: 'Vigane aasta või kuu', en: 'Invalid year or month' }),

    // ── Dashboard layout validation ──
    DASHBOARD_DUPLICATE_CARD: (p) =>
        translation({
            et: `Kaart „${p.key}” on paigutuses topelt`,
            en: `Duplicate card: ${p.key}`,
        }),
    DASHBOARD_UNKNOWN_CARD: (p) =>
        translation({ et: `Tundmatu kaart: ${p.key}`, en: `Unknown card: ${p.key}` }),
    DASHBOARD_CARD_NOT_ALLOWED: (p) =>
        translation({
            et: `Kaart „${p.key}” pole sinu rollile lubatud`,
            en: `Card "${p.key}" is not available for your role`,
        }),
    DASHBOARD_NON_MANDATORY_IN_MANDATORY: (p) =>
        translation({
            et: `Kaart „${p.key}” ei kuulu kohustuslike kaartide hulka`,
            en: `Card "${p.key}" does not belong in the mandatory section`,
        }),
    DASHBOARD_MANDATORY_IN_REGULAR: (p) =>
        translation({
            et: `Kohustuslik kaart „${p.key}” ei saa olla tavakaartide hulgas`,
            en: `Mandatory card "${p.key}" cannot be placed in the regular section`,
        }),
    DASHBOARD_MISSING_MANDATORY: (p) =>
        translation({
            et: `Kohustuslik kaart „${p.key}” on paigutusest puudu`,
            en: `Mandatory card "${p.key}" is missing from the layout`,
        }),

    // ── Feedback widget screenshot upload ──
    FEEDBACK_NO_IMAGE: () => translation({ et: 'Pildifail puudub', en: 'No image file provided' }),
    FEEDBACK_IMAGE_TYPE: () =>
        translation({
            et: 'Fail peab olema JPG, PNG, WEBP või GIF pilt',
            en: 'File must be a JPG, PNG, WEBP, or GIF image',
        }),
    FEEDBACK_IMAGE_TOO_LARGE: () =>
        translation({ et: 'Fail on suurem kui 5 MB', en: 'File size exceeds the 5 MB limit' }),
    FEEDBACK_UPLOAD_FAILED: () =>
        translation({ et: 'Pildi üleslaadimine ebaõnnestus', en: 'Failed to upload image' }),

    // ── Projects / events ──
    UNKNOWN_PROJECT: () => translation({ et: 'Tundmatu projekt', en: 'Unknown project' }),
    UNKNOWN_EVENT: () => translation({ et: 'Tundmatu sündmus', en: 'Unknown event' }),

    // ── Generic status validation ──
    STATUS_MUST_BE_ONE_OF: (p) =>
        translation({
            et: `staatus peab olema üks järgnevatest: ${p.statuses}`,
            en: `status must be one of: ${p.statuses}`,
        }),

    // ── Offboarding (TASK-403) ──
    INVALID_END_DATE: () =>
        translation({ et: 'Vigane töösuhte lõpu kuupäev', en: 'Invalid employment end date' }),
    VALUE_MUST_BE_ONE_OF: (p) =>
        translation({
            et: `${p.field} peab olema üks järgnevatest: ${p.values}`,
            en: `${p.field} must be one of: ${p.values}`,
        }),

    // ── Network / client-side fallback ──
    NETWORK_ERROR: () => translation({ et: 'Võrgu viga', en: 'Network error' }),
} satisfies ApiErrorTranslators

/** Whether the client knows a translator for `code` (a newer server may send codes this bundle lacks). */
export const isKnownApiErrorCode = (code: string): code is PortalApiErrorCode =>
    Object.prototype.hasOwnProperty.call(portalApiErrorTranslators, code)

/**
 * Translate a portal API error code into the active language. Used by the portal
 * API client to render server errors in the user's language. The English
 * `message` on the wire remains the fallback for un-migrated error responses.
 */
export const translateApiError = <C extends PortalApiErrorCode>(
    code: C,
    params: PortalApiErrorParams[C],
    lang: Language,
): string => {
    // Indexing with a generic key widens the value to a union of all translators,
    // which TS can't prove is callable with this code's params — but the
    // `satisfies` guarantee above makes it sound, so cast at this single boundary.
    const translator = portalApiErrorTranslators[code] as (
        p: PortalApiErrorParams[C],
    ) => Translation
    return translator(params)(lang)
}
